<template>
    <div>
        <v-navigation-drawer
            fixed
            v-show="get_user.id"
            :mini-variant="sidebarCollapsed"
            mini-variant-width="72"
            width="240"
            :permanent="$vuetify.breakpoint.mdAndUp"
            :temporary="$vuetify.breakpoint.smAndDown"
            v-model="sidebarVisible"
            class="main-sidebar"
        >
            <!-- Brand — in the `prepend` slot so it renders outside
                 v-navigation-drawer's scrollable content area and stays
                 pinned to the top of the sidebar regardless of nav list length. -->
            <template v-slot:prepend>
                <div class="sidebar-brand">
                    <div class="sidebar-brand-icon">
                        <v-icon color="white" size="16">mdi-checkbox-marked</v-icon>
                    </div>
                    <span class="sidebar-brand-text" v-show="!sidebarCollapsed">DGTasks</span>
                </div>
            </template>

            <!-- Impersonation Banner -->
            <div v-if="is_impersonating && get_user.id" class="impersonation-banner" v-show="!sidebarCollapsed">
                <v-icon small color="white" class="mr-2">mdi-account-switch</v-icon>
                <span class="impersonation-text">
                    You are impersonating
                    <strong>{{ get_user.name }}</strong>
                    <span v-if="get_impersonator && get_impersonator.user">
                        &middot; signed in as
                        <strong>{{ get_impersonator.user.name }}</strong>
                    </span>
                </span>
                <v-btn small dark outlined :loading="exitingImpersonation" @click="exitImpersonation"
                    class="impersonation-exit-btn text-none mt-2">
                    <v-icon left small>mdi-logout-variant</v-icon>
                    Exit Impersonation
                </v-btn>
            </div>

            <MainSidebar
                :tabs="navigationTabs"
                :has-access="hasAccess"
                :is-active-tab="isActiveTab"
                :get-tab-badge-count="getTabBadgeCount"
                :get-tour-attribute="getTourAttribute"
                @navigate="navigateToPage"
            />

            <template v-slot:append>
                <v-divider></v-divider>
                <v-list dense nav>
                    <v-list-item link @click="sidebarCollapsed = !sidebarCollapsed" class="d-none d-md-flex">
                        <v-list-item-icon>
                            <v-icon>{{ sidebarCollapsed ? 'mdi-chevron-double-right' : 'mdi-chevron-double-left' }}</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                            <v-list-item-title>Collapse</v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>
                </v-list>
                <v-list dense nav>
                    <v-list-item @click="logout()" link>
                        <v-list-item-icon>
                            <v-icon>mdi-logout</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                            <v-list-item-title>Logout</v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>
                </v-list>
            </template>
        </v-navigation-drawer>

        <!-- Slim top strip: mobile menu toggle + notifications + tour help -->
        <div class="top-navigation sidebar-offset" :class="{ 'sidebar-offset--collapsed': sidebarCollapsed }"
            v-if="get_user.id">
            <div class="nav-container">
                <div class="nav-left">
                    <!-- Mobile hamburger (opens the sidebar as a temporary drawer) -->
                    <button class="nav-hamburger" @click="mobileNavOpen = true" aria-label="Open menu">
                        <v-icon>mdi-menu</v-icon>
                    </button>

                    <!-- Profile section -->
                    <div class="nav-profile">
                        <v-avatar color="primary" size="32">
                            <v-img v-if="get_user?.profile_picture"
                                :src="getProfilePictureUrl(get_user.profile_picture)"></v-img>
                            <span v-else class="white--text text-caption">
                                {{ get_user?.name?.[0] ?? "NN" }}
                            </span>
                        </v-avatar>
                        <div class="nav-profile-identity d-none d-sm-flex">
                            <span class="nav-profile-name">{{ get_user?.name || "Guest" }}</span>
                            <span class="nav-profile-role">{{ get_user?.role?.name || "No Role" }}</span>
                        </div>
                        <v-divider vertical class="mx-2 d-none d-md-flex"></v-divider>
                        <div class="nav-profile-actions d-none d-md-flex">
                            <v-btn outlined rounded small class="text-none" @click="viewProfile">
                                <v-icon small left>mdi-account</v-icon>
                                Profile
                            </v-btn>
                        </div>
                    </div>

                    <v-divider vertical class="mx-3 d-none d-md-flex"></v-divider>

                    <!-- Current time in the user's display timezone -->
                    <TimeBanner class="d-none d-md-flex" />
                </div>

                <!-- Settings Menu -->
                <div class="nav-settings">
                    <!-- SOD/EOD header actions: SOD slot always visible, EOD slot only
                         once eligible (sod_submitted || local_hour >= eod_prompt_hour).
                         Both open the exact same dialogs used by the automatic popup. -->
                    <div class="nav-sod-eod d-none d-sm-flex" v-if="get_user.id">
                        <v-btn outlined rounded small class="text-none" @click="openSodDialog">
                            <v-icon small left>mdi-white-balance-sunny</v-icon>
                            {{ sodButtonLabel }}
                        </v-btn>
                        <v-btn v-if="showEodButton" outlined rounded small class="text-none" @click="openEodDialog">
                            <v-icon small left>mdi-weather-night</v-icon>
                            {{ eodButtonLabel }}
                        </v-btn>
                    </div>

                    <!-- Notifications Bell -->
                    <v-menu ref="notificationMenu" offset-y left bottom min-width="420" max-width="500"
                        :close-on-content-click="false" v-model="notificationMenuOpen"
                        content-class="notification-menu">
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn icon v-bind="attrs" v-on="on" class="settings-btn notification-btn"
                                data-tour="notifications-badge" @click="openNotifications" style="position: relative;">
                                <v-icon>mdi-bell-outline</v-icon>
                                <div v-if="notificationCount > 0" class="notification-badge">
                                    {{ notificationCount }}
                                </div>
                            </v-btn>
                        </template>
                        <v-card class="notification-card" elevation="8" min-width="420">
                            <div class="notification-header">
                                <div class="notification-title">
                                    <v-icon left color="primary">mdi-bell</v-icon>
                                    <span class="font-weight-bold">Notifications</span>
                                    <v-chip v-if="notificationCount > 0" x-small color="primary" class="ml-2">
                                        {{ notificationCount }}
                                    </v-chip>
                                </div>
                                <div class="notification-header-actions">
                                    <v-btn x-small icon color="primary" @click.stop="refreshNotifications"
                                        title="Refresh notifications from server" class="refresh-btn">
                                        <v-icon small>mdi-refresh</v-icon>
                                    </v-btn>
                                    <v-btn x-small text color="primary" @click.stop="markAllNotificationsRead"
                                        v-if="unreadNotificationCount > 0" class="mark-all-btn">
                                        <v-icon small left>mdi-check-all</v-icon>
                                        Mark all read
                                    </v-btn>
                                </div>
                            </div>
                            <v-divider></v-divider>
                            <div class="notification-list" v-if="notificationsForDisplay.length > 0">
                                <div v-for="item in notificationsForDisplay" :key="item.id"
                                    :class="['notification-item', { 'notification-unread': item.type === 'notification' && item.is_read === 0 }]"
                                    @click="handleNotificationClick(item)">
                                    <div class="notification-icon">
                                        <v-avatar :color="getNotificationColor(item)" size="40">
                                            <v-icon dark small>{{ getNotificationIcon(item) }}</v-icon>
                                        </v-avatar>
                                    </div>
                                    <div class="notification-content">
                                        <div class="notification-text" v-html="item.content"></div>
                                        <div class="notification-time">
                                            <v-icon x-small>mdi-clock-outline</v-icon>
                                            {{ formatNotificationDate(item.created_at) }}
                                        </div>
                                    </div>
                                    <div class="notification-actions">
                                        <v-btn v-if="item.type === 'notification' && item.is_read === 0" icon x-small
                                            @click.stop="markNotificationAsRead(item.id)" class="mark-read-btn">
                                            <v-icon x-small color="primary">mdi-check</v-icon>
                                        </v-btn>
                                        <v-icon v-if="item.type === 'notification' && item.is_read === 0" small
                                            color="primary" class="unread-dot">
                                            mdi-circle
                                        </v-icon>
                                    </div>
                                </div>
                            </div>
                            <div v-else class="empty-notifications">
                                <v-icon size="64" color="grey lighten-1">mdi-bell-off-outline</v-icon>
                                <div class="empty-text">No notifications</div>
                                <div class="empty-subtext">You're all caught up!</div>
                            </div>
                            <v-divider></v-divider>
                            <div class="notification-footer">
                                <v-btn text small color="primary" @click="viewAllNotifications" class="view-all-btn">
                                    <v-icon small left>mdi-arrow-right</v-icon>
                                    View All Notifications
                                </v-btn>
                            </div>
                        </v-card>
                    </v-menu>

                    <!-- Tour Help Button -->
                    <TourTrigger />

                    <!-- Settings Menu -->
                    <v-menu offset-y bottom left :nudge-bottom="20" min-width="280" max-height="85vh"
                        content-class="settings-menu">
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn icon v-bind="attrs" v-on="on" class="settings-btn">
                                <v-badge color="error" dot overlap
                                    :value="approveLeaveRequest.length > 0 || approveOT.length > 0">
                                    <v-icon color="#666">bx bxs-cog</v-icon>
                                </v-badge>
                            </v-btn>
                        </template>
                        <v-list>
                            <!-- <v-list-item @click.stop="openInbox" style="cursor: pointer">
                                <v-list-item-icon>
                                    <v-icon>mdi-bell-outline</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title>
                                    <span style="font-size: 0.9rem; font-weight: 600">
                                        Notifications
                                    </span>
                                </v-list-item-title>
                            </v-list-item> -->
                            <v-list-item @click.stop="leaveRequest" style="cursor: pointer">
                                <v-list-item-icon>
                                    <v-icon>mdi-calendar-clock</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title>
                                    <span style="font-size: 0.9rem; font-weight: 600">
                                        Request a Leave / Overtime / WFH
                                    </span>
                                </v-list-item-title>
                            </v-list-item>
                            <v-list-item @click.stop="approveRequest()" style="cursor: pointer; color: '';"
                                v-if="canAccessApprovals">
                                <v-list-item-icon>
                                    <v-badge color="error" dot overlap
                                        :value="approveLeaveRequest.length > 0 || approveOT.length > 0">
                                        <v-icon>mdi-calendar-check</v-icon>
                                    </v-badge>
                                </v-list-item-icon>
                                <v-list-item-title>
                                    <span style="font-size: 0.9rem; font-weight: 600">
                                        Approve Leave / Overtime / WFH Requests
                                    </span>
                                </v-list-item-title>
                            </v-list-item>
                            <v-list-item @click.stop="openPayroll" style="cursor: pointer">
                                <v-list-item-icon>
                                    <v-icon>mdi-cash-multiple</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title>
                                    <span style="font-size: 0.9rem; font-weight: 600">
                                        Payroll
                                    </span>
                                </v-list-item-title>
                            </v-list-item>
                            <v-list-item @click.stop="openPTPayroll" style="cursor: pointer">
                                <v-list-item-icon>
                                    <v-icon>mdi-cash</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title>
                                    <span style="font-size: 0.9rem; font-weight: 600">
                                        OSF/PT Payroll
                                    </span>
                                </v-list-item-title>
                            </v-list-item>
                            <v-list-item @click.stop="openRequestNewHire" style="cursor: pointer">
                                <v-list-item-icon>
                                    <v-icon>mdi-account-plus</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title>
                                    <span style="font-size: 0.9rem; font-weight: 600">
                                        Request New Hire
                                    </span>
                                </v-list-item-title>
                            </v-list-item>
                             <v-list-item @click.stop="openPersonalResources" style="cursor: pointer">
                                <v-list-item-icon>
                                    <v-icon>mdi-folder-account</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title>
                                    <span style="font-size: 0.9rem; font-weight: 600">
                                        Personal Resources
                                    </span>
                                </v-list-item-title>
                            </v-list-item>
                            <v-list-item @click.stop="$router.push({ path: '/sod-eod/history' })" style="cursor: pointer">
                                <v-list-item-icon>
                                    <v-icon>mdi-calendar-star</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title>
                                    <span style="font-size: 0.9rem; font-weight: 600">
                                        My SOD/EOD History
                                    </span>
                                </v-list-item-title>
                            </v-list-item>
                            <template v-if="[1, 2, 11].includes(get_user.role_id) || canManageAgentSettings || (get_user.managed_users && get_user.managed_users.length > 0) || !!get_user.time_tracking_manager">
                                <v-divider></v-divider>
                                <v-list-item dense>
                                    <v-list-item-title>
                                        <span style="font-size: 0.75rem; font-weight: 700; letter-spacing: 0.05em; color: #888;">
                                            ADMIN ACTIONS
                                        </span>
                                    </v-list-item-title>
                                </v-list-item>
                            </template>
                            <v-list-item @click.stop="$router.push({ path: '/sod-eod/team' })"
                                style="cursor: pointer"
                                v-if="[1, 2, 11].includes(get_user.role_id) || (get_user.managed_users && get_user.managed_users.length > 0) || !!get_user.time_tracking_manager">
                                <v-list-item-icon>
                                    <v-icon>mdi-calendar-multiselect</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title>
                                    <span style="font-size: 0.9rem; font-weight: 600">
                                        Team SOD/EOD Review
                                    </span>
                                </v-list-item-title>
                            </v-list-item>
                            <v-list-item @click.stop="$router.push({ path: '/leave-management' })"
                                style="cursor: pointer" v-if="canAccessLeaveManagement">
                                <v-list-item-icon>
                                    <v-icon>mdi-calendar-remove</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title>
                                    <span style="font-size: 0.9rem; font-weight: 600">
                                        Leave Management
                                    </span>
                                </v-list-item-title>
                            </v-list-item>
                            <v-list-item @click.stop="$router.push({ path: '/overtime-management' })"
                                style="cursor: pointer" v-if="get_user.role_id === 1">
                                <v-list-item-icon>
                                    <v-icon>mdi-clock-plus-outline</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title>
                                    <span style="font-size: 0.9rem; font-weight: 600">
                                        Overtime Management
                                    </span>
                                </v-list-item-title>
                            </v-list-item>
                            <v-list-item @click.stop="$router.push({ path: '/resource-planner' })" style="cursor: pointer" v-if="get_user.role_id === 1">
                                <v-list-item-icon>
                                    <v-icon>mdi-account-multiple-outline</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title>
                                    <span style="font-size: 0.9rem; font-weight: 600">
                                        Resource Planner
                                    </span>
                                </v-list-item-title>
                            </v-list-item>
                            <v-list-item v-if="get_user.role_id === 1" style="cursor: pointer"
                                @click.stop="$router.push({ path: '/email-warmup' })">
                                <v-list-item-icon>
                                    <v-icon>mdi-email-fast-outline</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title>
                                    <span style="font-size: 0.9rem; font-weight: 600">
                                        Email Warmup
                                    </span>
                                </v-list-item-title>
                            </v-list-item>
                            <v-list-item v-if="canManageAgentSettings" style="cursor: pointer"
                                @click.stop="$router.push({ path: '/agent-settings' })">
                                <v-list-item-icon>
                                    <v-icon>mdi-tune-variant</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title>
                                    <span style="font-size: 0.9rem; font-weight: 600">
                                        Agent Settings
                                    </span>
                                </v-list-item-title>
                            </v-list-item>
                            <v-list-item v-if="canManageAgentSettings" style="cursor: pointer"
                                @click.stop="$router.push({ path: '/ai-usage' })">
                                <v-list-item-icon>
                                    <v-icon>mdi-robot-outline</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title>
                                    <span style="font-size: 0.9rem; font-weight: 600">
                                        AI Usage
                                    </span>
                                </v-list-item-title>
                            </v-list-item>
                            <v-list-item v-if="[1, 2, 11].includes(get_user.role_id)" style="cursor: pointer"
                                @click.stop="$router.push({ path: '/feedback' })">
                                <v-list-item-icon>
                                    <v-icon>mdi-message-alert-outline</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title>
                                    <span style="font-size: 0.9rem; font-weight: 600">
                                        Feedbacks
                                    </span>
                                </v-list-item-title>
                            </v-list-item>
                            <v-divider></v-divider>
                            <v-list-item @click.stop="$router.push({ path: '/settings' })"
                                style="cursor: pointer">
                                <v-list-item-icon>
                                    <v-icon>mdi-cog</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title>
                                    <span style="font-size: 0.9rem; font-weight: 600">
                                        Settings
                                    </span>
                                </v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                </div>
            </div>
        </div>

        <!-- Main Content Area -->
        <div class="main-content"
            :class="{ 'sidebar-offset': get_user.id, 'sidebar-offset--collapsed': get_user.id && sidebarCollapsed }">
            <slot></slot>
        </div>

        <v-dialog v-model="newRequestDialog" max-width="700" scrollable>
            <v-card>
                <v-card-title class="primary white--text">
                    <v-icon left color="white">mdi-plus-circle</v-icon>
                    New Hire Request
                    <v-spacer />
                    <v-btn icon dark @click="newRequestDialog = false"><v-icon>mdi-close</v-icon></v-btn>
                </v-card-title>
                <v-card-text class="pa-4">
                    <div class="d-flex justify-center mb-4" style="gap: 8px;">
                        <v-chip v-for="(step, idx) in formSteps" :key="idx" :color="newRequestStep === idx ? 'primary' : 'default'"
                            :dark="newRequestStep === idx" :outlined="newRequestStep !== idx" small @click="newRequestStep = idx">
                            {{ step }}
                        </v-chip>
                    </div>

                    <div v-show="newRequestStep === 0">
                        <v-text-field v-model="newRequest.job_title" label="Job Title *" placeholder="e.g. Senior Developer" outlined dense />
                        <v-row>
                            <v-col cols="6"><v-text-field v-model="newRequest.department" label="Department" placeholder="e.g. Engineering" outlined dense /></v-col>
                            <v-col cols="6"><v-text-field v-model="newRequest.portfolio" label="Company / Portfolio" placeholder="e.g. PT, STAGIT" outlined dense /></v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="6">
                                <v-select v-model="newRequest.priority" :items="['Low', 'Normal', 'High', 'Urgent']" label="Priority" outlined dense />
                            </v-col>
                            <v-col cols="6">
                                <v-select v-model="newRequest.employment_type" :items="employmentTypes" label="Employment Type" outlined dense />
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="6"><v-text-field v-model="newRequest.hiring_manager_name" label="Hiring Manager Name" outlined dense /></v-col>
                            <v-col cols="6"><v-text-field v-model="newRequest.requester_email" label="Requester Email" outlined dense /></v-col>
                        </v-row>
                        <v-textarea v-model="newRequest.reason_for_hire" label="Reason for Hire" placeholder="Primary reason for this hire" outlined dense rows="3" />
                    </div>

                    <div v-show="newRequestStep === 1">
                        <v-textarea v-model="newRequest.job_description" label="Job Description" placeholder="Core responsibilities, expectations..." outlined dense rows="6" />
                        <v-text-field v-model="newRequest.experience" label="Experience Level" placeholder="e.g. 3-5 years, Senior, Entry-Level" outlined dense />
                        <div class="text-subtitle-2 mb-1">Salary Range</div>
                        <v-row>
                            <v-col cols="4">
                                <v-select v-model="newRequest.salary_currency" :items="['$ USD', '€ EUR', '£ GBP', 'AED', 'PHP']" outlined dense />
                            </v-col>
                            <v-col cols="8">
                                <v-text-field v-model="newRequest.salary_range" placeholder="e.g. 80,000 - 100,000" outlined dense />
                            </v-col>
                        </v-row>
                        <v-textarea v-model="newRequest.success_criteria" label="Success in First 90 Days" placeholder="What does success look like?" outlined dense rows="3" />
                    </div>

                    <div v-show="newRequestStep === 2">
                        <v-textarea v-model="newRequest.mandatory_skills" label="Mandatory Skills (Must-haves)" placeholder="Required skills and technologies" outlined dense rows="4" />
                        <v-text-field v-model="newRequest.other_mandatory_skills" label="Other Mandatory Skills" outlined dense />
                        <v-textarea v-model="newRequest.preferred_qualifications" label="Preferred Qualifications (Nice-to-haves)" outlined dense rows="4" />
                        <v-text-field v-model="newRequest.nationality_preference" label="Preferred Nationality" outlined dense />

                        <v-card outlined class="pa-4 mt-2">
                            <div class="text-subtitle-2 font-weight-bold mb-3">Candidate Attribute Priorities (1-5)</div>
                            <div v-for="attr in attributeSliders" :key="attr.key" class="d-flex align-center mb-1" style="gap: 12px;">
                                <span class="text-caption" style="min-width: 130px;">{{ attr.label }}</span>
                                <v-slider v-model="newRequest[attr.key]" :min="1" :max="5" :step="1" hide-details dense thumb-label style="flex: 1;" />
                                <span class="text-body-2 font-weight-medium" style="min-width: 20px;">{{ newRequest[attr.key] }}</span>
                            </div>
                        </v-card>
                    </div>

                    <div v-show="newRequestStep === 3">
                        <v-textarea v-model="newRequest.interview_panel" label="Interview Panel" placeholder="Team members involved in interviews" outlined dense rows="4" />
                        <v-text-field v-model="newRequest.contact_person" label="Primary Contact for Recruitment" outlined dense />
                        <v-textarea v-model="newRequest.interview_notes" label="Other Information" placeholder="Anything else to help find the right candidate" outlined dense rows="4" />
                        <v-card outlined class="pa-3 mt-1">
                            <div class="d-flex align-center justify-space-between">
                                <span class="text-body-2">Requires Dean Approval</span>
                                <v-switch v-model="newRequest.requires_dean_approval" hide-details dense class="mt-0 pt-0" />
                            </div>
                        </v-card>
                    </div>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn v-if="newRequestStep > 0" text @click="newRequestStep--">
                        <v-icon left>mdi-chevron-left</v-icon> Back
                    </v-btn>
                    <v-btn v-if="newRequestStep < formSteps.length - 1" color="primary" @click="newRequestStep++">
                        Next <v-icon right>mdi-chevron-right</v-icon>
                    </v-btn>
                    <v-btn v-else color="primary" :loading="savingRequest" :disabled="!newRequest.job_title" @click="createJobRequest">
                        <v-icon left>mdi-check</v-icon> Submit Request
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <TaskNavigationComponent :task_details_dialog="task_details_dialog"
            :can_close_task_details_dialog="can_close_task_details_dialog" :users="users" @updateTask="updateTask"
            @closeTaskDetailsDialog="closeTaskDetailsDialog" />

        <!-- SOD/EOD dialogs, mounted globally so the automatic popup and the header
             actions always open the exact same components (never separate forms). -->
        <SodDialog v-model="sodDialogOpen" @dismissed="handleSodDismissed" @submitted="handleSodSubmitted" />
        <EodDialog v-model="eodDialogOpen" @dismissed="handleEodDismissed" @submitted="handleEodSubmitted" />
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import TaskNavigationComponent from '../components/modules/TaskNavigationComponent.vue'
import TourTrigger from '../components/modules/TourTrigger.vue'
import TimeBanner from '../components/settings/TimeBanner.vue'
import MainSidebar from '../components/layout/MainSidebar.vue'
import { canAccessLeaveApproval } from '../components/leave-management/leaveUtils'
import { manageableCompanyIds } from '../components/companies/companyAccess'
import SodDialog from '../components/modules/SodDialog.vue'
import EodDialog from '../components/modules/EodDialog.vue'
import { canAccessLeaveManagement as resolveLeaveAccess } from '../components/leave-management/leaveUtils'
import { canManageAgentSettings } from '../components/agent-time-tracking/agentSettingsAccess'
import { hasAssessmentAccess } from '../components/sales-assessment/admin/assessmentUi'

export default {
    components: {
        TaskNavigationComponent,
        TourTrigger,
        TimeBanner,
        MainSidebar,
        SodDialog,
        EodDialog,
    },
    provide() {
        return {
            users: () => this.users,
            updateUser: this.updateUser,
            addUser: this.addUser,
            removeUser: this.removeUser,
            refreshUsers: this.getUsers,
            personalTasks: () => this.all_personal_tasks,
            assignedTasks: () => this.all_assigned_tasks,
            unapprovedTasks: () => this.all_unapproved_tasks,
            rejectedTasks: () => this.all_rejected_tasks,
            isManagerForApproval: () => this.isManagerForApproval,
            refreshTasks: this.getTasks,
            refreshUnapprovedTasks: this.getUnapprovedTasks,
            refreshTasksForUser: this.getTasksForUser,
            refreshCompletedTasks: this.loadCompletedTasks,
            // Completed tasks (latest COMPLETED_TASKS_PAGE_SIZE, paginated independently
            // from active tasks, kept out of personalTasks()/assignedTasks())
            completedPersonalTasks: () => this.all_completed_personal_tasks,
            completedAssignedTasks: () => this.all_completed_assigned_tasks,
            loadMoreCompletedPersonalTasks: this.loadMoreCompletedPersonalTasks,
            loadMoreCompletedAssignedTasks: this.loadMoreCompletedAssignedTasks,
            isLoadingCompletedPersonalTasks: () => this.isLoadingCompletedPersonalTasks,
            isLoadingCompletedAssignedTasks: () => this.isLoadingCompletedAssignedTasks,
            hasMoreCompletedPersonalTasks: () => this.completedPersonalHasMore,
            hasMoreCompletedAssignedTasks: () => this.completedAssignedHasMore,
            completedPersonalTasksTotal: () => this.completedPersonalTotal,
            completedAssignedTasksTotal: () => this.completedAssignedTotal,
            // Server-computed "My Statistics" counts (assignee-only, single aggregate query)
            myTaskStats: () => this.myTaskStats,
            refreshMyTaskStats: this.getMyTaskStats,
            isLoadingMyTaskStats: () => this.isLoadingMyTaskStats,
            addPersonalTask: this.addPersonalTask,
            addAssignedTask: this.addAssignedTask,
            updateTaskInArrays: this.updateTaskInArrays,
            removeTaskFromArrays: this.removeTaskFromArrays,
            restoreTaskToArrays: this.restoreTaskToArrays,
            optimisticUpdateTask: this.optimisticUpdateTask,
            optimisticDeleteTask: this.optimisticDeleteTask,
            optimisticCreateTask: this.optimisticCreateTask,
            optimisticBulkUpdateTasks: this.optimisticBulkUpdateTasks,
            optimisticBulkDeleteTasks: this.optimisticBulkDeleteTasks,
            findTaskInArrays: this.findTaskInArrays,
            // Meetings providers
            meetings: () => this.meetings,
            refreshMeetings: this.getMeetings,
            // Companies providers
            companies: () => this.companies,
            refreshCompanies: this.getCompanies,
            addCompany: this.addCompany,
            updateCompany: this.updateCompany,
            removeCompany: this.removeCompany,
            optimisticUpdateCompany: this.optimisticUpdateCompany,
            // Meeting tasks providers
            meetingTasks: () => this.meetingTasks,
            refreshMeetingTasks: this.getMeetingTasks,
            // Resources providers
            resources: () => this.resources,
            companyResources: () => this.companyResources,
            refreshResources: this.getResources,
            refreshCompanyResources: this.getCompanyResources,
            addResource: this.addResource,
            updateResource: this.updateResource,
            removeResource: this.removeResource,
            findResourceById: this.findResourceById,
            // Personal Resources providers
            personalResources: () => this.personalResources,
            refreshPersonalResources: this.getPersonalResources,
            addPersonalResource: this.addPersonalResource,
            updatePersonalResource: this.updatePersonalResource,
            removePersonalResource: this.removePersonalResource,
            findPersonalResourceById: this.findPersonalResourceById,
            // Floor Plan providers
            floorPlanSeats: () => this.floorPlanSeats,
            isLoadingFloorPlanSeats: () => this.isLoadingFloorPlanSeats,
            refreshFloorPlanSeats: this.getFloorPlanSeats,
            assignFloorPlanSeat: this.assignFloorPlanSeat,
            unassignFloorPlanSeat: this.unassignFloorPlanSeat,
            moveFloorPlanSeat: this.moveFloorPlanSeat,
            floorPlanUserDetails: () => this.floorPlanUserDetails,
            refreshFloorPlanUserDetails: this.getFloorPlanUserDetails,
            saveFloorPlanUserDetails: this.saveFloorPlanUserDetails,
            floorPlanRemoteUsers: () => this.floorPlanRemoteUsers,
            refreshFloorPlanRemoteUsers: this.getFloorPlanRemoteUsers,
            addFloorPlanRemoteUser: this.addFloorPlanRemoteUser,
            removeFloorPlanRemoteUser: this.removeFloorPlanRemoteUser,
            // Goals providers
            goals: () => this.goals,
            refreshGoals: this.getGoals,
            addGoal: this.addGoal,
            updateGoal: this.updateGoal,
            removeGoal: this.removeGoal,
            optimisticAddGoal: this.optimisticAddGoal,
            optimisticUpdateGoal: this.optimisticUpdateGoal,
            optimisticDeleteGoal: this.optimisticDeleteGoal,
            // Bottlenecks providers
            bottlenecks: () => this.bottlenecks,
            refreshBottlenecks: this.getBottlenecks,
            // Developments providers
            developments: () => this.developments,
            refreshDevelopments: this.getDevelopments,
            createDevelopment: this.createDevelopment,
            updateDevelopment: this.updateDevelopment,
            deleteDevelopment: this.deleteDevelopment,
            getCompanyDevelopment: this.getCompanyDevelopment,
            // Milestones providers
            milestones: () => this.milestones,
            refreshMilestones: this.getMilestones,
            // Overview providers (for admin/manager)
            overviewTasks: () => this.all_overview_tasks,
            overviewStats: () => this.overview_stats,
            refreshOverviewTasks: this.loadOverviewTasks,
            updateOverviewStats: this.updateOverviewStatsOptimistically,
            // Reference data providers
            priorities: () => this.priorities,
            statuses: () => this.statuses,
            chatTemplates: () => this.chatTemplates,
            refreshChatTemplates: this.getChatTemplates,
            tags: () => this.tags,
            meetingStatuses: () => this.meeting_statuses,
            roles: () => this.roles,
            refreshPriorities: this.getPriorities,
            refreshStatuses: this.getStatus,
            refreshTags: this.getTags,
            refreshMeetingStatuses: this.getMeetingStatus,
            refreshRoles: this.getUserRoles,
            // Members providers (role members)
            allManagers: () => this.allManagers,
            roleManagers: () => this.roleManagers,
            refreshManagers: this.getAllManagers,
            // Role-based tasks provider (admin only)
            roleTasksMap: () => this.roleTasksMap,
            refreshRoleTasksMap: this.loadAllRoleTasksForAdmin,
            // Leave management providers
            leaveBalances: () => this.leaveBalances,
            refreshLeaveBalances: this.getLeaveBalances,
            isLoadingLeaveBalances: () => this.isLoadingLeaveBalances,
            // SOD/EOD (Start of Day / End of Day) providers
            sodEodStatus: () => this.sodEodStatus,
            refreshSodEodStatus: this.getSodEodStatus,
            todaySod: () => this.todaySod,
            todayEodPrefill: () => this.todayEodPrefill,
            refreshTodaySod: this.getTodaySod,
            refreshTodayEodPrefill: this.getTodayEodPrefill,
            submitSod: this.submitSod,
            submitEod: this.submitEod,
            clearSod: this.clearSod,
            clearEod: this.clearEod,
            mySodEodHistory: () => this.mySodEodHistory,
            refreshMySodEodHistory: this.getMySodEodHistory,
            isLoadingMySodEodHistory: () => this.isLoadingMySodEodHistory,
            managedSodEodHistory: () => this.managedSodEodHistory,
            refreshManagedSodEodHistory: this.getManagedSodEodHistory,
            // SOD/EOD reporting (summary/trends/workload/incomplete-reasons/
            // consistency/tracked-time for a date range) — self, a managed
            // user, and manager/admin team-wide, sharing one backend
            // aggregation per the reporting investigation.
            sodEodReport: () => this.sodEodReport,
            getSodEodReport: this.getSodEodReport,
            isLoadingSodEodReport: () => this.isLoadingSodEodReport,
            managedSodEodReport: () => this.managedSodEodReport,
            getManagedSodEodReport: this.getManagedSodEodReport,
            teamSodEodReport: () => this.teamSodEodReport,
            getTeamSodEodReport: this.getTeamSodEodReport,
            isLoadingTeamSodEodReport: () => this.isLoadingTeamSodEodReport,
            // Loading state providers
            isLoadingUsers: () => this.isLoadingUsers,
            isLoadingTasks: () => this.isLoadingTasks,
            tasksLoaded: () => this.tasksLoaded,
            // Inbox/messaging providers
            conversations: () => this.conversations,
            messages: (conversationId) => this.getMessagesByConversation(conversationId),
            refreshConversations: this.getConversations,
            refreshConversationsWithPolling: this.refreshConversationsWithPolling,
            getConversationMessages: this.getConversationMessages,
            optimisticSendMessage: this.optimisticSendMessage,
            markConversationAsRead: this.markConversationAsRead,
            unreadMessagesCount: () => this.unreadMessagesCount,
            setActiveConversation: this.setActiveConversation,
            // Chat: attachments, reactions, history paging
            sendMessageWithAttachments: this.sendMessageWithAttachments,
            retryTranscription: this.retryTranscription,
            optimisticToggleReaction: this.optimisticToggleReaction,
            pinMessage: this.pinMessage,
            unpinMessage: this.unpinMessage,
            loadBoard: this.loadBoard,
            loadSharedItems: this.loadSharedItems,
            loadOlderMessages: this.loadOlderMessages,
            loadMessagesAround: this.loadMessagesAround,
            loadNewerMessages: this.loadNewerMessages,
            jumpToLatest: this.jumpToLatest,
            hasNewerMessages: this.hasNewerMessages,
            isConversationDetached: this.isConversationDetached,
            hasMoreMessages: (conversationId) => this.messagesHasMore[conversationId] !== false,
            isLoadingOlderMessages: (conversationId) => !!this.loadingOlderMessages[conversationId],
            // Chat: AI
            summarizeConversation: this.summarizeConversation,
            composeDraft: this.composeDraft,
            extractActionItems: this.extractActionItems,
            extractTasksFromMessage: this.extractTasksFromMessage,
            triageInbox: this.triageInbox,
            loadThread: this.loadThread,
            summarizeThread: this.summarizeThread,
            suggestReplies: this.suggestReplies,
            askChat: this.askChat,
            loadConversationContext: this.loadConversationContext,
            saveConversationContext: this.saveConversationContext,
            assistantUser: () => this.assistantUser,
            assistantReply: this.assistantReply,
            threadReplies: (rootId) => this.threadMessages[rootId] || [],
            closeThread: this.closeThread,
            appendThreadReply: this.appendThreadReply,
            pmtInjectThreadReply: this.pmtInjectThreadReply,
            deleteMessage: this.deleteMessage,
            // Chat: group conversations
            createGroupConversation: this.createGroupConversation,
            addGroupParticipants: this.addGroupParticipants,
            removeGroupParticipant: this.removeGroupParticipant,
            setGroupParticipantRole: this.setGroupParticipantRole,
            renameGroupConversation: this.renameGroupConversation,
            leaveGroupConversation: this.leaveGroupConversation,
            // Chat: linked tasks
            linkedTask: (taskId) => this.linkedTasks[taskId] || null,
            searchTasks: this.searchTasks,
            linkTasksToMessage: this.linkTasksToMessage,
            openTaskDetails: this.openTaskDetails,
            updateLinkedTaskStatus: this.updateLinkedTaskStatus,
            updateLinkedTaskAssignees: this.updateLinkedTaskAssignees,
            // Chat: presence and typing
            onlineUserIds: () => this.onlineUserIds,
            typingUsersIn: (conversationId) => this.typingUsers[conversationId] || [],
            emitTyping: this.emitTyping,
            watchConversationPresence: this.watchConversationPresence,
            unwatchConversationPresence: this.unwatchConversationPresence,
            conversationReadReceipts: () => this.conversationReadReceipts,
            // Notifications providers
            notifications: () => this.notifications,
            refreshNotifications: this.refreshNotifications,
            markAllNotificationsRead: this.markAllNotificationsRead,
            markNotificationAsRead: this.markNotificationAsRead,
            handleNotificationClick: this.handleNotificationClick,
            getNotificationIcon: this.getNotificationIcon,
            getNotificationColor: this.getNotificationColor,
            formatNotificationDate: this.formatNotificationDate,
            unreadNotificationCount: () => this.unreadNotificationCount,
            // Leave/Overtime/WFH "to approve" badge counts (settings-menu gear icon) —
            // fetched once at app mount, so any screen that approves/declines a
            // request (or just re-lands on the approvals hub) should call this to
            // keep the badge from going stale for the rest of the session.
            refreshApprovalCounts: this.refreshApprovalCounts,
            // Task details dialog providers
            openTaskDetailsDialog: this.openTaskDetailsDialog,
            closeTaskDetailsDialog: this.closeTaskDetailsDialog,
            // AI Chat providers
            aiChats: () => this.aiChats,
            refreshAiChats: this.getAiChats,
            optimisticCreateAiChat: this.optimisticCreateAiChat,
            optimisticUpdateAiChat: this.optimisticUpdateAiChat,
            optimisticDeleteAiChat: this.optimisticDeleteAiChat,
            optimisticArchiveAiChat: this.optimisticArchiveAiChat,
            optimisticUnarchiveAiChat: this.optimisticUnarchiveAiChat,
        }
    },
    data: () => ({
        // Mobile: the sidebar collapses to a hamburger-opened temporary drawer
        mobileNavOpen: false,
        // Desktop: the sidebar can be collapsed to icon-only (mini variant)
        sidebarCollapsed: false,
        // Loading state tracking
        isLoadingUsers: false,
        isLoadingTasks: false,
        tasksLoaded: false, // Track if tasks have been loaded at least once
        users: [], // Centralized users data
        // Dashboard "My Statistics" card — server-computed, assignee-only counts
        myTaskStats: { todo_tasks: 0, in_progress_tasks: 0, completed_tasks: 0, overdue_tasks: 0 },
        isLoadingMyTaskStats: false,
        // Task data centralized here so multiple routes/components can access without extra API calls
        all_personal_tasks: [],
        all_assigned_tasks: [],
        // Completed tasks (status_id = 4) are kept separate from the active
        // arrays above and loaded in pages of COMPLETED_TASKS_PAGE_SIZE, ordered
        // by most recently completed, via loadCompletedTasks()/loadMoreCompleted*Tasks().
        COMPLETED_TASKS_PAGE_SIZE: 50,
        all_completed_personal_tasks: [],
        all_completed_assigned_tasks: [],
        completedPersonalOffset: 0,
        completedAssignedOffset: 0,
        completedPersonalTotal: 0,
        completedAssignedTotal: 0,
        completedPersonalHasMore: true,
        completedAssignedHasMore: true,
        isLoadingCompletedPersonalTasks: false,
        isLoadingCompletedAssignedTasks: false,
        all_unapproved_tasks: [], // Tasks with status_id = 8 (pending approval)
        all_rejected_tasks: [], // Tasks with status_id = 9 that were rejected from approval
        isManagerForApproval: null, // null = not yet loaded, true/false set from API response
        // Meetings data centralized here
        meetings: [],
        // Companies data centralized here  
        companies: [],
        // Meeting tasks data
        meetingTasks: [],
        // Resources data centralized here
        resources: [],
        // Company Resources data centralized here (filtered by is_internal_resource)
        companyResources: [],
        // Personal Resources data centralized here
        personalResources: [],
        // Floor Plan seat assignments centralized here
        floorPlanSeats: [],
        isLoadingFloorPlanSeats: false,
        // Floor Plan "get to know me" details, keyed by user_id
        floorPlanUserDetails: {},
        // Floor Plan remote (non-seated) users, per unit
        floorPlanRemoteUsers: [],
        // Goals data centralized here
        goals: [],
        // Bottlenecks data centralized here
        bottlenecks: [],
        // Developments data centralized here
        developments: [],
        // Milestones data centralized here
        milestones: [],
        // Centralized reference data
        priorities: [],
        statuses: [],
        tags: [],
        chatTemplates: [],
        meeting_statuses: [],
        roles: [], // User roles from get_user_roles API
        // Managers data centralized here
        allManagers: [], // All role-member assignments (user_role_members table)
        roleManagers: [], // Supervisors who approve tasks (user_role_managers table)
        // Overview data centralized here (for role_id 1 and 11 only)
        all_overview_tasks: [],
        overview_stats: {
            total_tasks: 0,
            in_progress_tasks: 0,
            completed_tasks: 0,
            overdue_tasks: 0,
            by_role: [],
        },
        // Role-based tasks for Overview component (admin only)
        roleTasksMap: {}, // Tasks grouped by role_id from get_all_tasks_by_roles endpoint
        // Leave management data centralized here
        leaveBalances: [], // All employee leave balances
        isLoadingLeaveBalances: false,
        // SOD/EOD (Start of Day / End of Day) data centralized here
        // { work_date, timezone, sod_required, eod_required, eod_prompt_hour,
        //   sod_submitted, sod_submitted_at, eod_submitted, eod_submitted_at }
        sodEodStatus: null,
        todaySod: null, // Today's SOD submission (content), null if not submitted yet
        todayEodPrefill: null, // Today's EOD prefill (planned tasks/progress + existing answers, if any)
        mySodEodHistory: [], // Current user's own past SOD/EOD submissions
        isLoadingMySodEodHistory: false,
        managedSodEodHistory: [], // A managed user's past SOD/EOD submissions (manager review)
        // SOD/EOD reporting aggregates (date-ranged summary/trend/workload/
        // incomplete-reason/consistency/tracked-time), separate from the
        // unbounded history above which still feeds the calendar/history list.
        sodEodReport: null, // Current user's own report for the selected range
        managedSodEodReport: null, // A managed user's report (manager's drill-down)
        teamSodEodReport: null, // Manager/admin team-wide report for the selected range
        isLoadingSodEodReport: false,
        isLoadingTeamSodEodReport: false,
        // Auto-popup + header-action dialog visibility (dialogs are mounted globally, see template)
        sodDialogOpen: false,
        eodDialogOpen: false,
        sodEodPollInterval: null,
        // Inbox/messaging data
        conversations: [],
        // AI Chat data
        aiChats: [],
        conversationMessages: {}, // Object keyed by conversation_id containing message arrays
        threadMessages: {}, // Replies keyed by their thread's root message id
        assistantUser: null, // The @mentionable AI bot, when the feature is on
        unreadMessagesCount: 0,
        activeConversationId: null, // Track currently viewed conversation
        // Whether the logged-in user has ever synced any agent_sessions rows —
        // controls visibility of the "Time Dashboard" nav tab (see mounted()).
        hasAgentTrackedTime: false,
        // Grouped + ordered for the sidebar; MainSidebar renders a divider
        // whenever `group` changes between two consecutively *visible* tabs.
        navigationTabs: [
            /*{
                name: 'Home',
                icon: 'mdi-home-variant-outline',
                route: '/home',
                component: 'HomeComponent',
                disabled: false,
                requiresRole: null,
                group: 'work'
            },*/

            // Work
            {
                name: 'Dashboard',
                icon: 'mdi-format-list-checks',
                route: '/dashboard',
                component: 'MyWorkComponent',
                disabled: false,
                requiresRole: null,
                group: 'work'
            },
            // {
            //     name: 'Calendar',
            //     icon: 'mdi-calendar-clock',
            //     route: '/calendar',
            //     component: 'CalendarView',
            //     disabled: false,
            //     requiresRole: null,
            //     group: 'work'
            // },
            {
                name: 'Task Approvals',
                icon: 'mdi-clipboard-check-outline',
                route: '/task_approval',
                component: 'TaskApprovalComponent',
                disabled: false,
                requiresRole: null,
                group: 'work'
            },

            // Collaborate
            {
                name: 'Messages',
                icon: 'mdi-message-text-outline',
                route: '/chats',
                component: 'InboxComponent',
                disabled: false,
                requiresRole: null,
                badge: 'unreadMessagesCount',
                group: 'collaborate'
            },
            {
                name: 'Meetings',
                icon: 'mdi-calendar-multiple',
                route: '/meetings',
                component: 'MeetingsComponent',
                disabled: false,
                requiresRole: null,
                group: 'collaborate'
            },
            {
                name: 'AI Chat',
                icon: 'mdi-robot-outline',
                route: '/ai-chat',
                component: 'AiChatComponent',
                disabled: false,
                requiresRole: null, // Allow all users
                group: 'collaborate'
            },
            {
                name: 'AI Cost and Usage',
                icon: 'mdi-cash-multiple',
                route: '/ai-cost',
                component: 'AiCostCenterComponent',
                disabled: false,
                requiresRole: [1, 11], // Admins and Super Admins
                group: 'collaborate'
            },

            // Organize
            {
                name: 'Projects',
                icon: 'mdi-clipboard-text-multiple',
                route: '/projects',
                component: 'ProjectsComponent',
                disabled: false,
                requiresRole: null,
                group: 'organize'
            },
            {
                name: 'Companies',
                icon: 'mdi-domain',
                route: '/companies',
                component: 'CompaniesComponent',
                disabled: false,
                requiresRole: [1],
                allowCompanyManagers: true,
                group: 'organize'
            },
            {
                name: 'Teams',
                icon: 'mdi-account-multiple-outline',
                route: '/teams',
                component: 'TeamsComponent',
                disabled: false,
                requiresRole: null,
                group: 'organize'
            },
            {
                name: 'Employee Growth',
                icon: 'mdi-trending-up',
                route: '/employee-growth',
                component: 'EmployeeGrowthComponent',
                disabled: false,
                requiresRole: null,
                group: 'organize'
            },
            {
                name: 'Growth Admin',
                icon: 'mdi-account-star-outline',
                route: '/employee-growth-admin',
                component: 'AdminGrowthComponent',
                disabled: false,
                requiresRole: [1],
                group: 'organize'
            },
            {
                name: 'Resources',
                icon: 'mdi-folder-multiple',
                route: '/resources',
                component: 'ResourcesComponent',
                disabled: false, // Placeholder for future implementation
                requiresRole: null,
                group: 'organize'
            },

            // Time
            {
                name: 'Time Dashboard',
                icon: 'mdi-clock-check-outline',
                route: '/time-dashboard',
                component: 'PersonalTimeTrackingComponent',
                disabled: false,
                requiresRole: null,
                requiresTrackedTime: true,
                group: 'time'
            },
            {
                name: 'Download App',
                icon: 'mdi-download-circle-outline',
                route: '/download-app',
                component: 'DownloadAppComponent',
                disabled: false,
                requiresRole: null,
                group: 'time'
            },
            {
                name: 'Time Tracking',
                icon: 'mdi-clock-time-four-outline',
                route: '/time-tracking',
                component: 'AgentTimeTrackingComponent',
                disabled: false,
                requiresRole: [1],
                allowedUserIds: [47],
                requiresManagesUsers: true,
                group: 'time'
            },
            {
                name: 'Calendar',
                icon: 'mdi-calendar-account',
                route: '/my-calendar',
                component: 'MyCalendarComponent',
                disabled: false,
                requiresRole: null,
                group: 'time'
            },

            // Insights
            /*{
                name: 'Reports',
                icon: 'mdi-chart-box-outline',
                route: '/reports',
                component: 'ReportsComponent',
                disabled: false,
                requiresRole: null,
                group: 'insights'
            },
            {
                name: 'KPIs',
                icon: 'mdi-target',
                // Dynamic per-user route — resolved in navigateToPage()/isActiveTab().
                route: null,
                dynamicRoute: 'my-kpis',
                component: 'UserKpisComponent',
                disabled: false,
                requiresRole: null,
                group: 'insights'
            },*/

            // Admin
            {
                name: 'User Directory',
                icon: 'mdi-account-group',
                route: '/directory',
                component: 'UserDirectoryComponent',
                disabled: false,
                requiresRole: null, // Open to all; page itself restricts full data to Super Admin
                group: 'admin'
            },
            {
                name: "Manager's Hub",
                icon: 'mdi-account-tie',
                route: '/managers-hub',
                component: 'OverviewComponent',
                disabled: false,
                requiresRole: [1],
                group: 'admin'
            },
            {
                name: 'Floor Plan',
                icon: 'mdi-office-building-marker-outline',
                route: '/floor-plan',
                component: 'FloorPlanComponent',
                disabled: false,
                requiresRole: null, // Open to all
                group: 'admin'
            },
            {
                name: 'Recruitment',
                icon: 'mdi-account-search',
                route: '/recruitment',
                component: 'RecruitmentComponent',
                disabled: false,
                requiresRecruitmentAccess: true,
                group: 'admin'
            },
            {
                name: 'Sales Assessments',
                icon: 'mdi-clipboard-text-search-outline',
                route: '/sales-assessments',
                component: 'SalesAssessmentsAdmin',
                disabled: false,
                requiresAssessmentAccess: true,
                group: 'admin'
            },
            /*{
                name: 'Payroll',
                icon: 'mdi-cash-multiple',
                route: '/payroll',
                component: 'PayrollComponent',
                disabled: false,
                requiresRole: [1],
                group: 'admin'
            },*/
        ],
        notifications: [],
        task_details_dialog: false,
        selected_task: null,
        can_close_task_details_dialog: false,
        original_selected_task: {},
        newRequestDialog: false,
        savingRequest: false,
        newRequestStep: 0,
        formSteps: ['Basic Info', 'Job Details', 'Requirements', 'Interview Setup'],
        attributeSliders: [
            { key: 'attribute_technical_skills', label: 'Technical Skills' },
            { key: 'attribute_cultural_fit', label: 'Cultural Fit' },
            { key: 'attribute_problem_solving', label: 'Problem Solving' },
            { key: 'attribute_communication', label: 'Communication' },
            { key: 'attribute_industry_experience', label: 'Industry Experience' },
        ],
        newRequest: {
            job_title: '',
            department: '',
            portfolio: '',
            priority: 'Normal',
            employment_type: 'Full-Time',
            hiring_manager_name: '',
            requester_email: '',
            reason_for_hire: '',
            job_description: '',
            experience: '',
            salary_currency: '$ USD',
            salary_range: '',
            success_criteria: '',
            mandatory_skills: '',
            other_mandatory_skills: '',
            preferred_qualifications: '',
            nationality_preference: '',
            attribute_technical_skills: 3,
            attribute_cultural_fit: 3,
            attribute_problem_solving: 3,
            attribute_communication: 3,
            attribute_industry_experience: 3,
            interview_panel: '',
            contact_person: '',
            interview_notes: '',
            requires_dean_approval: false,
        },
        employmentTypes: ['Full-Time', 'Part-Time', 'Contract', 'Internship'],
        unreadNotificationCount: 0,
        // Notification polling workaround for Pusher issues
        notificationPollingInterval: null,
        readNotificationsLoaded: false,
        // Track previous unread count to avoid unnecessary task reloads
        previousUnreadNotificationCount: 0,
        // Track if notification polling is currently running to prevent overlaps
        isNotificationPollingRunning: false,
        // Notification menu state
        notificationMenuOpen: false,
        // Impersonation
        exitingImpersonation: false,
        // Chat history paging, keyed by conversation id
        messagesHasMore: {},
        messagesHasNewer: {},
        conversationDetached: {},
        loadingOlderMessages: {},
        // Tasks referenced by chat messages, keyed by task id. Messages carry only
        // task ids; cards read the summary from here so a single status change
        // refreshes every card showing that task.
        linkedTasks: {},
        // Presence: ids of users currently connected, and who is typing where
        onlineUserIds: [],
        typingUsers: {},
        typingTimers: {},
        // Last read timestamp per conversation, keyed by conversation id then user id
        conversationReadReceipts: {},
        // Pusher presence channel for the conversation currently open
        activePresenceChannel: null,
        lastTypingEmittedAt: 0,
    }),
    watch: {
        // Close the mobile nav drawer on any navigation.
        $route() {
            this.mobileNavOpen = false;
        },
    },
    computed: {
        // v-navigation-drawer's v-model: always "open" once permanent
        // (desktop), mirrors the hamburger-driven flag once temporary
        // (mobile).
        sidebarVisible: {
            get() {
                return this.$vuetify.breakpoint.mdAndUp ? true : this.mobileNavOpen;
            },
            set(value) {
                this.mobileNavOpen = value;
            },
        },
        ...mapGetters({
            get_user: 'auth/get_user',
            get_selected_company: 'companies/get_selected_company',
            get_unread_count: 'messages/get_unread_count',
            get_selected_task: 'tasks/get_selected_task',
            get_unread_tasks: 'tasks/get_unread_tasks',
            approveLeaveRequest: 'leave/getToApproveLeaveRequest',
            approveOT: 'leave/getToApproveOTData',
            get_preferences: 'auth/get_preferences',
            is_impersonating: 'auth/is_impersonating',
            get_impersonator: 'auth/get_impersonator',
        }),
        apiUrl() {
            return process.env.VUE_APP_API_URL || ''
        },
        // Notifications only for display (limited to 5 most recent)
        notificationsForDisplay() {
            return this.notifications
                .map(notification => ({
                    ...notification,
                    type: 'notification',
                    display_type: 'notification'
                }))
                .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                .slice(0, 5)
        },
        // Notification count for badge (only unread notifications)
        notificationCount() {
            return this.notifications.filter(n => n.is_read === 0).length
        },
        // Approve Leave / Overtime / WFH page: Super Admin, company managers, or
        // anyone who still has requests waiting on them.
        canAccessApprovals() {
            return canAccessLeaveApproval(
                this.get_user,
                this.users,
                this.approveLeaveRequest.length > 0 || this.approveOT.length > 0,
                this.roleManagers
            );
        },
        // True if the current user is a manager of any role (in user_role_members table)
        isRoleManager() {
            if (!this.get_user?.id) return false;
            return this.allManagers.some(member => member.user_id === this.get_user.id);
        },
        // Leave Management: Super Admin + Manager roles + anyone set up as a team
        // manager / with direct reports. The page and API scope the data to the
        // manager's own team + direct reports.
        canManageAgentSettings() {
            return canManageAgentSettings(this.get_user);
        },
        canAccessLeaveManagement() {
            return resolveLeaveAccess(this.get_user, this.roleManagers);
        },
        // True if the current user directly manages at least one other user
        // (user_managed_users table) — used to grant Time Tracking access to
        // managers who aren't Admins.
        userManagesAtLeastOneUser() {
            if (!this.get_user?.id) return false;
            // Admin-picked Time Tracking managers (see all users' tracking)
            if (this.get_user.time_tracking_manager) return true;
            if (this.get_user.managed_users && this.get_user.managed_users.length > 0) {
                return true;
            }
            const currentUser = this.users.find(u => u.id === this.get_user.id);
            return !!(currentUser && currentUser.managed_users && currentUser.managed_users.length > 0);
        },
        // SOD/EOD header action labels + EOD slot eligibility — same labeled
        // progression as the Tracking App, derived identically from sodEodStatus().
        sodButtonLabel() {
            return this.sodEodStatus?.sod_submitted ? 'View Start of Day' : 'Start My Day';
        },
        showEodButton() {
            if (!this.sodEodStatus) return false;
            const localHour = new Date().getHours();
            const promptHour = this.sodEodStatus.eod_prompt_hour ?? 17;
            return !!(this.sodEodStatus.sod_submitted || localHour >= promptHour);
        },
        eodButtonLabel() {
            return this.sodEodStatus?.eod_submitted ? 'View End of Day' : 'End My Day';
        },
    },
    async mounted() {
        // Pick up display timezone / clock format stored on the account, so an
        // existing session on a new machine gets them without re-logging in.
        this.$store.dispatch('settings/hydrateFromUser', this.get_user);

        // ⚡ Set up chat/message broadcasting FIRST before loading any data
        this.setupChatListener();

        // Ask once for permission to show desktop notifications, so new
        // messages reach the user even when the tab is in the background.
        this.requestNotificationPermission();

        // Best-effort: the chat works unchanged if the assistant is disabled.
        this.loadAssistant();

        try {
            this.getCompanies(),
                this.isLoadingUsers = true;
            await this.getUsers();
            this.isLoadingUsers = false;

            this.isLoadingTasks = true;
            await this.getTasks();
            this.isLoadingTasks = false;
            this.tasksLoaded = true;

            // Load reference data that all components need
            await Promise.all([
                this.getPriorities(),
                this.getStatus(),
                this.getChatTemplates(),
                // this.getTags(),
                this.getMeetingStatus(),
                this.getUserRoles(),
                this.getUserTasksOverview(),
                this.getMyTaskStats(),
                this.loadUnreadNotifications() // Load unread notifications initially
            ]);

            // Check (non-blocking) whether the Time Dashboard nav tab should show
            this.$axios.get('api/agent/has-tracked-time').then(res => {
                if (res.data?.response) {
                    this.hasAgentTrackedTime = !!res.data.has_tracked_time;
                }
            }).catch(() => {});

            // SOD/EOD: fetch status (non-blocking) and, once known, auto-open the
            // relevant popup if required/not-yet-submitted and not snoozed for today.
            // Re-checked on an interval so the EOD popup can still trigger later in
            // the same session without a page reload.
            this.cleanupLegacySodEodSnoozeKeys();
            this.getSodEodStatus().then(() => this.maybeAutoOpenSodEod());
            this.startSodEodPolling();

            // Start notification polling (every 10 seconds for unread notifications)
            this.startNotificationPolling();

            // Load read notifications once in background
            setTimeout(() => {
                this.loadReadNotifications();
            }, 2000); // Delay by 2 seconds to not interfere with initial loading

            // Now load everything else in parallel
            await Promise.all([
                this.getToApproveOvertime(),
                this.getToApproveLeaveRequest(),
                this.getMeetings(),
                this.getResources(),
                this.getCompanyResources(),
                this.getGoals(),
                this.getMilestones(),
                this.getBottlenecks(),
                this.getConversations(), // Load inbox conversations
                this.getUnapprovedTasks(), // Load unapproved tasks for approval workflow
                this.getAllManagers(), // Load all role-manager assignments
                // this.getUnreadMessages(),
                this.getAiChats(), // Load AI chats
            ]);

            // Load the first page (latest 50) of completed tasks in the background,
            // separately from active tasks, so the initial load stays fast.
            this.loadCompletedTasks().catch((e) =>
                console.warn('Failed to load completed tasks in background:', e)
            );

            // Load overview tasks in background if user is admin or manager (role_id 1 or 11)
            if (this.get_user && (this.get_user.role_id === 1 || this.get_user.role_id === 11)) {
                this.loadOverviewTasks().catch((e) =>
                    console.warn('Failed to load overview tasks in background:', e)
                );
                // Load all role tasks for admin users
                this.loadAllRoleTasksForAdmin().catch((e) =>
                    console.warn('Failed to load role tasks in background:', e)
                );
            }
        } catch (error) {
            console.error("Error fetching data:", error)
        }
    },
    beforeDestroy() {
        // Clean up notification polling interval
        this.stopNotificationPolling();

        // Clean up SOD/EOD status polling interval
        this.stopSodEodPolling();

        // Clear any pending typing-expiry timers
        Object.values(this.typingTimers).forEach(timer => clearTimeout(timer));

        // Leave broadcasting channels
        this.unwatchConversationPresence();
        if (this.$echo && this.get_user?.id) {
            this.$echo.leave(`chat.${this.get_user.id}`);
            this.$echo.leave('online');
        }
    },
    methods: {
        getTourAttribute(tabName) {
            const tourMap = {
                'Dashboard': null, // Dashboard has its own tour system
                'Task Management': 'task-management-nav',
                'Inbox': 'notifications-nav',
                "Manager's Hub": 'overview-nav',
                'User Directory': 'users-nav',
                'Projects': 'companies-nav',
                'Goals': 'goals-nav',
                'Meetings': 'meetings-nav',
                'Resources': 'resources-nav'
            };
            return tourMap[tabName] || null;
        },

        viewProfile() {
            if (this.$route.path !== "/profile") {
                this.$router.push("/profile");
            }
        },
        openPersonalResources() {
            if (this.$route.path !== "/personal_resources") {
                this.$router.push("/personal_resources");
            }
        },
        openInbox() {
            if (this.$route.path !== "/inbox") {
                this.$router.push("/inbox");
            }
        },
        openPayroll() {
            if (this.$route.path !== "/payroll") {
                this.$router.push("/payroll");
            }
        },
        openPTPayroll() {
            if (this.$route.path !== "/payroll/pt") {
                this.$router.push("/payroll/pt");
            }
        },
        openRequestNewHire() {
            this.newRequestDialog = true;
        },
        getEmptyRequest() {
            return {
                job_title: '',
                department: '',
                portfolio: '',
                priority: 'Normal',
                employment_type: 'Full-Time',
                hiring_manager_name: '',
                requester_email: '',
                reason_for_hire: '',
                job_description: '',
                experience: '',
                salary_currency: '$ USD',
                salary_range: '',
                success_criteria: '',
                mandatory_skills: '',
                other_mandatory_skills: '',
                preferred_qualifications: '',
                nationality_preference: '',
                attribute_technical_skills: 3,
                attribute_cultural_fit: 3,
                attribute_problem_solving: 3,
                attribute_communication: 3,
                attribute_industry_experience: 3,
                interview_panel: '',
                contact_person: '',
                interview_notes: '',
                requires_dean_approval: false,
            }
        },
        async createJobRequest() {
            if (!this.newRequest.job_title) return;
            this.savingRequest = true;
            try {
                const response = await this.$axios.post('api/recruitment/job-requests', this.newRequest);
                if (response.data.response) {
                    this.$toast.success('Job request created');
                    this.newRequestDialog = false;
                    this.newRequestStep = 0;
                    this.newRequest = this.getEmptyRequest();
                    if (typeof window !== 'undefined') {
                        window.dispatchEvent(new CustomEvent('recruitment:job-request-created', {
                            detail: response.data.data || null,
                        }));
                    }
                } else {
                    this.$toast.error(response.data.message || 'Failed to create request');
                }
            } catch (error) {
                console.error('Error creating job request:', error);
                this.$toast.error('Failed to create job request');
            } finally {
                this.savingRequest = false;
            }
        },
        leaveRequest() {
            if (this.$route.path !== "/leaveRequest") {
                this.$router.push("/leaveRequest");
            }
        },
        approveRequest() {
            if (this.$route.path !== "/leaveApproval") {
                this.$router.push("/leaveApproval");
            }
        },
        showSnackBar(message) {
            this.$store.commit(
                "auth/setMessage",
                { show: true, message: message },
                { root: 1 }
            );
        },
        getProfilePictureUrl(profilePicture) {
            if (!profilePicture) return null;

            // If it's already a full URL, return as is
            if (profilePicture.startsWith('http://') || profilePicture.startsWith('https://')) {
                return profilePicture;
            }

            // Otherwise, construct the full URL with the API base URL
            const apiUrl = this.apiUrl.endsWith('/') ? this.apiUrl.slice(0, -1) : this.apiUrl;
            const picturePath = profilePicture.startsWith('/') ? profilePicture : `/${profilePicture}`;
            return `${apiUrl}${picturePath}`;
        },
        async getCompanies() {
            try {
                let response = await this.$axios.get("api/companies/get_companies");

                if (response.data.response) {
                    this.companies = response.data.data || [];
                }
            } catch (error) {
                console.error('Error fetching companies:', error);
            }
        },
        async getMeetings() {
            try {
                const response = await this.$axios.get("api/meetings/get_meetings");
                if (response.data.response) {
                    this.meetings = response.data.data || [];
                }
            } catch (error) {
                console.error('Error fetching meetings:', error);
            }
        },
        async getMeetingTasks(meetingId) {
            try {
                const response = await this.$axios.get(`api/tasks/get_meeting_task/${meetingId}`);
                if (response.data.response) {
                    this.meetingTasks = response.data.data || [];
                    return response.data.data;
                }
                return [];
            } catch (error) {
                console.error('Error fetching meeting tasks:', error);
                return [];
            }
        },
        async getResources() {
            try {
                const response = await this.$axios.get('/api/resources');
                if (response.data.success) {
                    this.resources = response.data.data || [];
                }
            } catch (error) {
                console.error('Error fetching resources:', error);
            }
        },
        async getCompanyResources() {
            try {
                const response = await this.$axios.get('/api/resources?is_internal_resource=1');
                if (response.data.success) {
                    this.companyResources = response.data.data || [];
                }
            } catch (error) {
                console.error('Error fetching company resources:', error);
            }
        },
        addResource(resource) {
            this.resources.push(resource);
        },
        updateResource({ resourceId, updatedData }) {
            const index = this.resources.findIndex(r => r.id === resourceId);
            if (index !== -1) {
                Object.assign(this.resources[index], updatedData);
            }
        },
        removeResource(resourceId) {
            const index = this.resources.findIndex(r => r.id === resourceId);
            if (index !== -1) {
                this.resources.splice(index, 1);
            }
        },
        findResourceById(resourceId) {
            return this.resources.find(r => r.id === resourceId);
        },
        //Personal Resources
        async getPersonalResources() {
            try {
                const response = await this.$axios.get('/api/personal_resources');
                if (response.data.success) {
                    this.personalResources = response.data.data || [];
                }
            } catch (error) {
                console.error('Error fetching personal resources:', error);
            }
        },
        addPersonalResource(resource) {
            this.personalResources.push(resource);
        },
        updatePersonalResource({ resourceId, updatedData }) {
            const index = this.personalResources.findIndex(r => r.id === resourceId);
            if (index !== -1) {
                Object.assign(this.personalResources[index], updatedData);
            }
        },
        removePersonalResource(resourceId) {
            const index = this.personalResources.findIndex(r => r.id === resourceId);
            if (index !== -1) {
                this.personalResources.splice(index, 1);
            }
        },
        findPersonalResourceById(resourceId) {
            return this.personalResources.find(r => r.id === resourceId);
        },

        // Floor Plan
        async getFloorPlanSeats() {
            this.isLoadingFloorPlanSeats = true;
            try {
                const response = await this.$axios.get('/api/floor_plan/seats');
                if (response.data.response) {
                    this.floorPlanSeats = response.data.data || [];
                }
            } catch (error) {
                console.error('Error fetching floor plan seats:', error);
            } finally {
                this.isLoadingFloorPlanSeats = false;
            }
        },
        async assignFloorPlanSeat(seatKey, userId) {
            const response = await this.$axios.post(`/api/floor_plan/seats/${seatKey}`, { user_id: userId });
            if (response.data.response) {
                const seatData = response.data.data;
                const index = this.floorPlanSeats.findIndex(s => s.seat_key === seatKey);
                if (index !== -1) {
                    this.floorPlanSeats.splice(index, 1, seatData);
                } else {
                    this.floorPlanSeats.push(seatData);
                }
                return seatData;
            }
            throw new Error(response.data.message || 'Failed to assign seat');
        },
        async unassignFloorPlanSeat(seatKey) {
            const response = await this.$axios.delete(`/api/floor_plan/seats/${seatKey}`);
            if (response.data.response) {
                const index = this.floorPlanSeats.findIndex(s => s.seat_key === seatKey);
                if (index !== -1) {
                    this.floorPlanSeats.splice(index, 1);
                }
                return true;
            }
            throw new Error(response.data.message || 'Failed to unassign seat');
        },
        async moveFloorPlanSeat(fromSeatKey, toSeatKey) {
            const fromIndex = this.floorPlanSeats.findIndex(s => s.seat_key === fromSeatKey);
            if (fromIndex === -1) return null;
            const originalSeats = this.floorPlanSeats.map(s => ({ ...s }));
            const fromSeat = this.floorPlanSeats[fromIndex];
            const userId = fromSeat.user && fromSeat.user.id;
            if (!userId) return null;

            // Optimistic update: move the assignment locally before the API confirms it.
            this.floorPlanSeats.splice(fromIndex, 1);
            const optimisticSeat = { ...fromSeat, seat_key: toSeatKey };
            const toIndex = this.floorPlanSeats.findIndex(s => s.seat_key === toSeatKey);
            if (toIndex !== -1) {
                this.floorPlanSeats.splice(toIndex, 1, optimisticSeat);
            } else {
                this.floorPlanSeats.push(optimisticSeat);
            }

            try {
                const assignResponse = await this.$axios.post(`/api/floor_plan/seats/${toSeatKey}`, { user_id: userId });
                if (!assignResponse.data.response) {
                    throw new Error(assignResponse.data.message || 'Failed to assign seat');
                }
                const seatData = assignResponse.data.data;
                const confirmedIndex = this.floorPlanSeats.findIndex(s => s.seat_key === toSeatKey);
                if (confirmedIndex !== -1) {
                    this.floorPlanSeats.splice(confirmedIndex, 1, seatData);
                }

                await this.$axios.delete(`/api/floor_plan/seats/${fromSeatKey}`);
                return seatData;
            } catch (error) {
                this.floorPlanSeats = originalSeats;
                throw error;
            }
        },
        async getFloorPlanUserDetails(userId) {
            const response = await this.$axios.get(`/api/floor_plan/user_details/${userId}`);
            if (response.data.response) {
                this.$set(this.floorPlanUserDetails, userId, response.data.data);
                return response.data.data;
            }
            throw new Error(response.data.message || 'Failed to fetch user details');
        },
        async saveFloorPlanUserDetails(userId, details) {
            const response = await this.$axios.post(`/api/floor_plan/user_details/${userId}`, details);
            if (response.data.response) {
                this.$set(this.floorPlanUserDetails, userId, response.data.data);
                return response.data.data;
            }
            throw new Error(response.data.message || 'Failed to save user details');
        },
        async getFloorPlanRemoteUsers() {
            try {
                const response = await this.$axios.get('/api/floor_plan/remote_users');
                if (response.data.response) {
                    this.floorPlanRemoteUsers = response.data.data || [];
                }
            } catch (error) {
                console.error('Error fetching floor plan remote users:', error);
            }
        },
        async addFloorPlanRemoteUser(unit, userId) {
            const response = await this.$axios.post('/api/floor_plan/remote_users', { unit, user_id: userId });
            if (response.data.response) {
                const remoteUser = response.data.data;
                const index = this.floorPlanRemoteUsers.findIndex(ru => ru.user.id === userId);
                if (index !== -1) {
                    this.floorPlanRemoteUsers.splice(index, 1, remoteUser);
                } else {
                    this.floorPlanRemoteUsers.push(remoteUser);
                }
                return remoteUser;
            }
            throw new Error(response.data.message || 'Failed to add remote user');
        },
        async removeFloorPlanRemoteUser(userId) {
            const response = await this.$axios.delete(`/api/floor_plan/remote_users/${userId}`);
            if (response.data.response) {
                const index = this.floorPlanRemoteUsers.findIndex(ru => ru.user.id === userId);
                if (index !== -1) {
                    this.floorPlanRemoteUsers.splice(index, 1);
                }
                return true;
            }
            throw new Error(response.data.message || 'Failed to remove remote user');
        },

        async getGoals() {
            try {
                const response = await this.$axios.get('api/goals-v2/');
                if (response.data.response && Array.isArray(response.data.data)) {
                    this.goals = response.data.data;
                }
            } catch (error) {
                console.error('Error fetching goals:', error);
            }
        },
        async getMilestones() {
            try {
                const response = await this.$axios.get('api/milestones');
                if (response.data.response && Array.isArray(response.data.data)) {
                    this.milestones = response.data.data;
                }
            } catch (error) {
                console.error('Error fetching milestones:', error);
            }
        },
        addGoal(goal) {
            this.goals.push(goal);
        },
        updateGoal({ goalId, updatedData }) {
            const index = this.goals.findIndex(g => g.id === goalId);
            if (index !== -1) {
                Object.assign(this.goals[index], updatedData);
            }
        },
        removeGoal(goalId) {
            const index = this.goals.findIndex(g => g.id === goalId);
            if (index !== -1) {
                this.goals.splice(index, 1);
            }
        },

        // Optimistic goal add method
        async optimisticAddGoal(goalData, apiCall) {
            if (!goalData) {
                throw new Error('Goal data is required');
            }

            // Create temporary goal with optimistic ID
            const tempGoal = {
                ...goalData,
                id: `temp_${Date.now()}`,
                created_at: new Date().toISOString()
            };

            try {
                // Add optimistically
                this.addGoal(tempGoal);

                // Call the API
                const result = await apiCall();

                // Remove temp goal and add real one
                this.removeGoal(tempGoal.id);

                if (result && result.goal) {
                    this.addGoal(result.goal);
                    return result;
                } else if (result && result.data) {
                    this.addGoal(result.data);
                    return result;
                }

                return result;
            } catch (error) {
                // Rollback on error
                console.error('Optimistic goal add failed, rolling back:', error);
                this.removeGoal(tempGoal.id);
                throw error;
            }
        },

        // Optimistic goal update method
        async optimisticUpdateGoal(goalId, updates, apiCall) {
            if (!goalId || !updates) {
                throw new Error('Goal ID and updates are required');
            }

            // Find the original goal
            const originalGoal = this.goals.find(g => g.id === goalId);
            if (!originalGoal) {
                throw new Error('Goal not found');
            }

            // Store original state for rollback
            const originalState = { ...originalGoal };

            try {
                // Apply optimistic update immediately
                this.updateGoal({ goalId, updatedData: updates });

                // Call the API
                const result = await apiCall();

                // If API returns updated data, sync it
                if (result && result.goal) {
                    this.updateGoal({ goalId, updatedData: result.goal });
                } else if (result && result.data) {
                    this.updateGoal({ goalId, updatedData: result.data });
                }

                return result;
            } catch (error) {
                // Rollback on error
                console.error('Optimistic goal update failed, rolling back:', error);
                this.updateGoal({ goalId, updatedData: originalState });
                throw error;
            }
        },

        // Optimistic goal delete method
        async optimisticDeleteGoal(goalId, apiCall) {
            if (!goalId) {
                throw new Error('Goal ID is required');
            }

            // Find the goal to delete
            const goalToDelete = this.goals.find(g => g.id === goalId);
            if (!goalToDelete) {
                throw new Error('Goal not found');
            }

            // Store for rollback
            const originalGoal = { ...goalToDelete };
            const originalIndex = this.goals.findIndex(g => g.id === goalId);

            try {
                // Remove optimistically
                this.removeGoal(goalId);

                // Call the API
                const result = await apiCall();

                return result;
            } catch (error) {
                // Rollback on error - restore goal at original position
                console.error('Optimistic goal delete failed, rolling back:', error);
                if (originalIndex !== -1) {
                    this.goals.splice(originalIndex, 0, originalGoal);
                } else {
                    this.addGoal(originalGoal);
                }
                throw error;
            }
        },
        async getBottlenecks() {
            try {
                // Compared server-side against stored due_date values, so this
                // boundary must be the company's today, not the viewer's.
                const today = this.$dateApi().format('YYYY-MM-DD');

                // Build where conditions for overdue tasks (bottlenecks)
                const where = [
                    ['status_id', '!=', 4], // Not completed
                    ['due_date', '<', today], // Overdue (due_date is in the past)
                ];

                const { data } = await this.$axios.get('api/tasks/get_tasks', where);

                if (data.data && Array.isArray(data.data)) {
                    // Sort by most overdue (oldest due_date first)
                    this.bottlenecks = data.data.sort((a, b) => {
                        const dateA = this.$date(a.due_date);
                        const dateB = this.$date(b.due_date);
                        return dateA.diff(dateB);
                    });
                } else {
                    this.bottlenecks = [];
                }
            } catch (error) {
                console.error('Error fetching bottlenecks:', error);
                this.bottlenecks = [];
            }
        },

        // Development management methods
        async getDevelopments(companyId = null) {
            try {
                const params = companyId ? { company_id: companyId } : {};
                const { data } = await this.$axios.get('api/developments', { params });

                if (data.response && Array.isArray(data.data)) {
                    this.developments = data.data;
                } else {
                    this.developments = [];
                }
                return this.developments;
            } catch (error) {
                console.error('Error fetching developments:', error);
                this.developments = [];
                return [];
            }
        },

        async createDevelopment(developmentData) {
            try {
                const { data } = await this.$axios.post('api/developments', developmentData);

                if (data.response && data.data) {
                    this.developments.push(data.data);
                    return data.data;
                }
                throw new Error(data.message || 'Failed to create development');
            } catch (error) {
                console.error('Error creating development:', error);
                throw error;
            }
        },

        async updateDevelopment(developmentId, developmentData) {
            try {
                const { data } = await this.$axios.put(`api/developments/${developmentId}`, developmentData);

                if (data.response && data.data) {
                    // Update in local array
                    const index = this.developments.findIndex(d => d.id === developmentId);
                    if (index !== -1) {
                        this.$set(this.developments, index, data.data);
                    }
                    return data.data;
                }
                throw new Error(data.message || 'Failed to update development');
            } catch (error) {
                console.error('Error updating development:', error);
                throw error;
            }
        },

        async deleteDevelopment(developmentId) {
            try {
                const { data } = await this.$axios.delete(`api/developments/${developmentId}`);

                if (data.response) {
                    // Remove from local array
                    const index = this.developments.findIndex(d => d.id === developmentId);
                    if (index !== -1) {
                        this.developments.splice(index, 1);
                    }
                    return true;
                }
                throw new Error(data.message || 'Failed to delete development');
            } catch (error) {
                console.error('Error deleting development:', error);
                throw error;
            }
        },

        getCompanyDevelopment(companyId) {
            // Return the development with current_stage = true for this company
            return this.developments.find(d => d.company_id === companyId && d.current_stage === true) || null;
        },

        // Company management methods
        addCompany(company) {
            this.companies.push(company);
        },
        updateCompany({ companyId, updatedData }) {
            const index = this.companies.findIndex(c => c.id === companyId);
            if (index !== -1) {
                Object.assign(this.companies[index], updatedData);
            }
        },
        removeCompany(companyId) {
            const index = this.companies.findIndex(c => c.id === companyId);
            if (index !== -1) {
                this.companies.splice(index, 1);
            }
        },

        // Optimistic company update method
        async optimisticUpdateCompany(companyId, updates, apiCall) {
            if (!companyId || !updates) {
                throw new Error('Company ID and updates are required');
            }

            // Find the original company
            const originalCompany = this.companies.find(c => c.id === companyId);
            if (!originalCompany) {
                throw new Error('Company not found');
            }

            // Store original state for rollback
            const originalState = { ...originalCompany };

            try {
                // Apply optimistic update immediately
                this.updateCompany({ companyId, updatedData: updates });

                // Call the API
                const result = await apiCall();

                // If API returns updated data, sync it
                if (result && result.company) {
                    this.updateCompany({ companyId, updatedData: result.company });
                }

                return result;
            } catch (error) {
                // Rollback on error
                console.error('Optimistic company update failed, rolling back:', error);
                this.updateCompany({ companyId, updatedData: originalState });
                throw error;
            }
        },

        // Overview methods (for admin/manager only)
        async loadOverviewTasks() {
            try {
                const response = await this.$axios.get('api/tasks/get_tasks_overview');

                if (response.data.response) {
                    this.all_overview_tasks = response.data.tasks || [];
                    this.overview_stats = {
                        total_tasks: response.data.total_tasks || 0,
                        in_progress_tasks: response.data.in_progress_tasks || 0,
                        completed_tasks: response.data.completed_tasks || 0,
                        overdue_tasks: response.data.overdue_tasks || 0,
                        by_role: response.data.by_role || [],
                    };
                    console.log('[loadOverviewTasks] Loaded stats with by_role:', this.overview_stats.by_role.length, 'roles');
                } else {
                    console.warn('[loadOverviewTasks] Failed:', response.data.message);
                }
            } catch (error) {
                console.error('[loadOverviewTasks] Error:', error);
                throw error;
            }
        },

        // Load all tasks grouped by role (admin only)
        async loadAllRoleTasksForAdmin() {
            try {
                console.log('[DefaultLayout] Loading all role tasks via bulk endpoint');
                const response = await this.$axios.get('api/tasks/get_all_tasks_by_roles', {});
                
                if (response.data.response) {
                    // Response is an array of {role_id, role_name, task_count, tasks[]}
                    const rolesArray = response.data.data || [];
                    console.log('[DefaultLayout] Loaded tasks for', rolesArray.length, 'roles');
                    
                    // Convert array to object keyed by role_id with Object.freeze to prevent deep reactivity
                    rolesArray.forEach(roleData => {
                        const roleId = roleData.role_id;
                        const tasks = roleData.tasks || [];
                        this.$set(this.roleTasksMap, roleId, Object.freeze(tasks));
                        console.log(`[DefaultLayout] Cached ${tasks.length} tasks for role ${roleId} (${roleData.role_name})`);
                    });
                    
                    console.log('[DefaultLayout] All role tasks loaded and cached');
                } else {
                    console.warn('[DefaultLayout] Failed to load role tasks:', response.data.message);
                }
            } catch (error) {
                console.error('[DefaultLayout] Error loading role tasks:', error);
                throw error;
            }
        },

        // Optimistic overview stats updates
        updateOverviewStatsForTaskCreation(task) {
            if (!task) return;

            this.overview_stats.total_tasks++;

            // Add to appropriate status counter
            const inProgressStatuses = [2]; // In Progress
            const completedStatuses = [3, 4]; // Done, Completed

            if (inProgressStatuses.includes(task.status_id)) {
                this.overview_stats.in_progress_tasks++;
            } else if (completedStatuses.includes(task.status_id)) {
                this.overview_stats.completed_tasks++;
            }

            // Check if overdue
            if (task.due_date && new Date(task.due_date) < new Date() && !completedStatuses.includes(task.status_id)) {
                this.overview_stats.overdue_tasks++;
            }

            // Add to overview tasks array if user is admin/manager
            if (this.get_user && (this.get_user.role_id === 1 || this.get_user.role_id === 11)) {
                this.all_overview_tasks.unshift(task);
            }
        },

        updateOverviewStatsForTaskUpdate(oldTask, updatedData) {
            if (!oldTask) return;

            const inProgressStatuses = [2];
            const completedStatuses = [3, 4];

            // Handle status changes
            if (updatedData.status_id && updatedData.status_id !== oldTask.status_id) {
                // Remove from old status count
                if (inProgressStatuses.includes(oldTask.status_id)) {
                    this.overview_stats.in_progress_tasks = Math.max(0, this.overview_stats.in_progress_tasks - 1);
                } else if (completedStatuses.includes(oldTask.status_id)) {
                    this.overview_stats.completed_tasks = Math.max(0, this.overview_stats.completed_tasks - 1);
                }

                // Add to new status count
                if (inProgressStatuses.includes(updatedData.status_id)) {
                    this.overview_stats.in_progress_tasks++;
                } else if (completedStatuses.includes(updatedData.status_id)) {
                    this.overview_stats.completed_tasks++;
                }

                // Update overdue count if task becomes completed
                if (completedStatuses.includes(updatedData.status_id)) {
                    const wasOverdue = oldTask.due_date && new Date(oldTask.due_date) < new Date();
                    if (wasOverdue) {
                        this.overview_stats.overdue_tasks = Math.max(0, this.overview_stats.overdue_tasks - 1);
                    }
                }
            }
        },

        updateOverviewStatsForTaskDeletion(task) {
            if (!task) return;

            this.overview_stats.total_tasks = Math.max(0, this.overview_stats.total_tasks - 1);

            const inProgressStatuses = [2];
            const completedStatuses = [3, 4];

            if (inProgressStatuses.includes(task.status_id)) {
                this.overview_stats.in_progress_tasks = Math.max(0, this.overview_stats.in_progress_tasks - 1);
            } else if (completedStatuses.includes(task.status_id)) {
                this.overview_stats.completed_tasks = Math.max(0, this.overview_stats.completed_tasks - 1);
            }

            // Update overdue count if applicable
            if (task.due_date && new Date(task.due_date) < new Date() && !completedStatuses.includes(task.status_id)) {
                this.overview_stats.overdue_tasks = Math.max(0, this.overview_stats.overdue_tasks - 1);
            }
        },

        updateOverviewStatsOptimistically(updates) {
            // Allow direct updates to overview stats from components
            if (updates) {
                Object.assign(this.overview_stats, updates);
            }
        },

        // Reference data loading methods
        async getPriorities() {
            try {
                const response = await this.$axios.get('/api/misc/get_prority');
                const { data } = response;
                this.priorities = data;
                // Also update Vuex for components that still use it
                this.$store.commit('tasks/set_priorities', data);
            } catch (error) {
                console.error('Error fetching priorities:', error);
            }
        },

        /**
         * The message template library. Small, static reference data - loaded
         * once at mount alongside priorities and statuses.
         */
        async getChatTemplates() {
            try {
                const response = await this.$axios.get('api/chat-templates');

                if (response.data && response.data.response) {
                    this.chatTemplates = response.data.data;
                }
            } catch (error) {
                // A missing template library must never block the inbox.
                console.error('Error fetching chat templates:', error);
            }
        },

        async getStatus() {
            try {
                const response = await this.$axios.get('/api/misc/get_status');
                const { data } = response;
                this.statuses = data;
                // Also update Vuex for components that still use it
                this.$store.commit('tasks/set_statuses', data);
            } catch (error) {
                console.error('Error fetching statuses:', error);
            }
        },

        async getTags() {
            try {
                const response = await this.$axios.get('api/tasks/get_tags');
                if (response.data.response) {
                    this.tags = response.data.data;
                    // Also update Vuex for components that still use it
                    this.$store.commit('tasks/SET_TAGS', response.data.data);
                }
            } catch (error) {
                console.error('Error fetching tags:', error);
            }
        },

        async getMeetingStatus() {
            try {
                const response = await this.$axios.get('/api/misc/get_meeting_statuses');
                const { data } = response;
                this.meeting_statuses = data.data;
                // Also update Vuex for components that still use it
                this.$store.commit('meetings/set_meeting_statuses', data.data);
            } catch (error) {
                console.error('Error fetching meeting statuses:', error);
            }
        },

        async getUserRoles() {
            try {
                const response = await this.$axios.get('/api/users/get_user_roles');
                const { data } = response;
                this.roles = data.data || data;
            } catch (error) {
                console.error('Error fetching user roles:', error);
            }
        },

        // Helper method to populate role objects on tasks
        populateTaskRoles(tasks) {
            if (!Array.isArray(tasks) || !Array.isArray(this.roles)) return tasks;
            
            return tasks.map(task => {
                // If task has role_id but no role object, populate it from roles array
                if (task.role_id && !task.role) {
                    const roleObj = this.roles.find(r => r.id === task.role_id);
                    if (roleObj) {
                        task.role = roleObj;
                    }
                }
                return task;
            });
        },


        async getUnreadMessages() {
            let payload = {
                receiver_id: this.get_user.id,
                is_read: false,
            };
            this.$axios.get("api/messages/get_messages", payload).then(({ data }) => {
                if (data.response) {
                    this.$store.dispatch("messages/set_unread_count", data.data.length);
                }
            });
        },
        async loadNotifications() {
            // Fallback method - now uses the split approach
            try {
                // Load both unread and read notifications
                await Promise.all([
                    this.loadUnreadNotifications(),
                    this.loadReadNotifications()
                ]);
            } catch (error) {
                console.error('Error loading notifications:', error);
            }
        },
        async loadUnreadNotifications() {
            // Prevent overlapping API calls
            if (this.isNotificationPollingRunning) {
                // console.log('[NotificationPolling] Previous call still running, skipping this interval');
                return;
            }

            this.isNotificationPollingRunning = true;

            try {
                if (!this.get_user?.id) return;

                let payload = {
                    receiver_id: this.get_user.id,
                    is_read: 0 // Only fetch unread notifications
                }
                const response = await this.$axios.get('api/notifications/get_notifications', payload);
                const { data } = response.data;

                if (data && Array.isArray(data)) {
                    // Get current read notifications to preserve them
                    const currentReadNotifications = this.notifications.filter(n => n.is_read === 1);

                    // Merge unread notifications from server with existing read notifications and sort by created_at
                    this.notifications = [...data, ...currentReadNotifications]
                        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

                    const newUnreadCount = data.length;
                    const previousCount = this.previousUnreadNotificationCount;

                    this.unreadNotificationCount = newUnreadCount;

                    // console.log(`[NotificationPolling] Loaded ${newUnreadCount} unread notifications (previous: ${previousCount})`);

                    // Only reload tasks if the unread count has changed
                    if (newUnreadCount !== previousCount) {
                        // console.log(`[NotificationPolling] Unread count changed from ${previousCount} to ${newUnreadCount} - reloading active tasks`);

                        // Reload active tasks (not completed) in background
                        this.getTasks(false).catch((error) => {
                            console.warn('[NotificationPolling] Failed to reload active tasks:', error);
                        });
                        // Overview stats only change when something new arrived
                        this.getUserTasksOverview();
                    } else {
                        // console.log(`[NotificationPolling] Unread count unchanged (${newUnreadCount}) - skipping active tasks reload`);
                    }

                    // Update the previous count for next comparison
                    this.previousUnreadNotificationCount = newUnreadCount;
                }
            } catch (error) {
                console.error('[NotificationPolling] Error loading unread notifications:', error);
            } finally {
                // Always reset the running flag when done
                this.isNotificationPollingRunning = false;
            }
        },
        async loadReadNotifications() {
            try {
                if (!this.get_user?.id || this.readNotificationsLoaded) return;

                let payload = {
                    receiver_id: this.get_user.id,
                    is_read: 1 // Only fetch read notifications
                }
                const response = await this.$axios.get('api/notifications/get_notifications', payload);
                const { data } = response.data;

                if (data && Array.isArray(data)) {
                    // Get current unread notifications to preserve them
                    const currentUnreadNotifications = this.notifications.filter(n => n.is_read === 0);

                    // Merge read notifications from server with existing unread notifications and sort by created_at
                    this.notifications = [...currentUnreadNotifications, ...data]
                        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
                    this.readNotificationsLoaded = true;

                    // console.log(`[NotificationPolling] Loaded ${data.length} read notifications in background`);
                }
            } catch (error) {
                console.error('[NotificationPolling] Error loading read notifications:', error);
            }
        },
        startNotificationPolling() {
            // Clear any existing interval
            if (this.notificationPollingInterval) {
                clearInterval(this.notificationPollingInterval);
            }

            // console.log('[NotificationPolling] Starting polling every 10 seconds for unread notifications');

            // Poll every 30s, and only while the tab is visible (hidden tabs were a
            // large share of API load). Catch up immediately when the tab returns.
            this.notificationPollingInterval = setInterval(() => {
                if (document.hidden) return;
                this.loadUnreadNotifications();
            }, 30000);

            if (!this._notificationVisibilityHandler) {
                this._notificationVisibilityHandler = () => {
                    if (!document.hidden) this.loadUnreadNotifications();
                };
                document.addEventListener('visibilitychange', this._notificationVisibilityHandler);
            }
        },
        stopNotificationPolling() {
            if (this._notificationVisibilityHandler) {
                document.removeEventListener('visibilitychange', this._notificationVisibilityHandler);
                this._notificationVisibilityHandler = null;
            }
            if (this.notificationPollingInterval) {
                clearInterval(this.notificationPollingInterval);
                this.notificationPollingInterval = null;
                // console.log('[NotificationPolling] Stopped notification polling');
            }
        },
        async refreshNotifications() {
            // Enhanced refresh with user feedback - now uses polling approach
            this.$toast.info('Syncing notifications with server...', { timeout: 2000 });

            try {
                // Force reload both unread and read notifications
                this.readNotificationsLoaded = false; // Reset flag to allow read notifications reload
                await Promise.all([
                    this.loadUnreadNotifications(),
                    this.loadReadNotifications()
                ]);

                // Restart polling to ensure fresh intervals
                this.startNotificationPolling();

                this.$toast.success('Notifications refreshed', { timeout: 2000 });
            } catch (error) {
                this.$toast.error('Failed to refresh notifications', { timeout: 3000 });
                console.error('Failed to refresh notifications:', error);
            }
        },
        async openNotifications() {
            // Just open the menu, don't mark as read automatically
            // User can click "Mark all read" button if desired
        },
        viewAllNotifications() {
            this.$router.push('/inbox');
        },
        async markAllNotificationsRead() {
            // Store original state for rollback
            const originalNotifications = this.notifications.map(n => ({ ...n }));
            const originalUnreadCount = this.unreadNotificationCount;

            // Optimistic update
            this.notifications.forEach((notification) => {
                notification.is_read = 1;
            });
            this.unreadNotificationCount = 0;

            try {
                await this.$axios.post(
                    `api/notifications/mark_notifications_as_read/${this.get_user.id}`
                );
                this.$toast.success('All notifications marked as read');
            } catch (error) {
                // Rollback on error
                this.notifications = originalNotifications;
                this.unreadNotificationCount = originalUnreadCount;
                console.error('Error marking notifications as read:', error);
                this.$toast.error('Failed to mark notifications as read');
            }
        },
        isLeaveApprovalNotification(item) {
            return typeof (item && item.content) === 'string' && item.content.includes('data-notify="leave-approval"');
        },
        handleNotificationClick(item) {
            // Close the notification menu
            this.notificationMenuOpen = false;

            // Handle regular notifications
            // Mark individual notification as read if unread
            if (item.is_read === 0) {
                this.markNotificationAsRead(item.id);
            }

            // Navigate to task if task_id exists
            if (item.task_id) {
                // Try to find task in local arrays first
                let task = this.findTaskInArrays(item.task_id);
                
                if (task) {
                    this.openTaskDetailsDialog(task);
                } else {
                    // Task not in local arrays - fetch from server
                    this.fetchAndOpenTask(item.task_id);
                }
                return;
            }

            // A leave / WFH request waiting on this approver opens the approvals page.
            if (this.isLeaveApprovalNotification(item)) {
                if (this.$route.path !== '/leaveApproval') this.$router.push('/leaveApproval');
                return;
            }

            // A chat mention points at a conversation rather than a task.
            if (item.conversation_id) {
                this.openConversation(item.conversation_id);
            }
        },

        /**
         * Open the chats page on a given conversation. Already being there is the
         * common case, so the query is updated rather than the route pushed.
         */
        openConversation(conversationId) {
            const query = { conversation: String(conversationId) };

            if (this.$route.path === '/chats') {
                if (this.$route.query.conversation !== query.conversation) {
                    this.$router.replace({ path: '/chats', query });
                }
                return;
            }

            this.$router.push({ path: '/chats', query });
        },
        
        async fetchAndOpenTask(taskId) {
            try {
                const response = await this.$axios.get(`api/tasks/get_task/${taskId}`);
                if (response.data.response && response.data.data) {
                    const task = response.data.data;
                    
                    // Add task to appropriate array based on whether user is creator or assignee
                    const currentUserId = this.get_user.id;
                    const isCreator = task.user_id === currentUserId;
                    const isAssignee = task.assignees?.some(a => a.user_id === currentUserId);
                    
                    if (isCreator && !this.all_personal_tasks.find(t => t.id === task.id)) {
                        this.all_personal_tasks.unshift(task);
                    }
                    if (isAssignee && !this.all_assigned_tasks.find(t => t.id === task.id)) {
                        this.all_assigned_tasks.unshift(task);
                    }
                    
                    // Open task details dialog
                    this.openTaskDetailsDialog(task);
                } else {
                    this.$toast.error('Task not found or has been deleted');
                }
            } catch (error) {
                console.error('Error fetching task:', error);
                this.$toast.error('Failed to load task details');
            }
        },
        
        async markNotificationAsRead(notificationId) {
            const notification = this.notifications.find(n => n.id === notificationId);
            if (!notification) return;

            // Store original state for rollback
            const originalReadState = notification.is_read;
            const originalUnreadCount = this.unreadNotificationCount;

            // Optimistic update
            notification.is_read = 1;
            this.unreadNotificationCount = Math.max(0, this.unreadNotificationCount - 1);

            try {
                await this.$axios.patch(`api/notifications/mark_read/${notificationId}`);
            } catch (error) {
                // Rollback on error
                notification.is_read = originalReadState;
                this.unreadNotificationCount = originalUnreadCount;
                console.error('Error marking notification as read:', error);
                this.$toast.error('Failed to mark notification as read');
            }
        },
        getNotificationIcon(item) {
            // Handle regular notifications
            if (this.isLeaveApprovalNotification(item)) return 'mdi-calendar-check';
            const content = item.content?.toLowerCase() || '';
            if (content.includes('assigned')) return 'mdi-account-plus';
            if (content.includes('updated')) return 'mdi-pencil';
            if (content.includes('tagged') || content.includes('mentioned')) return 'mdi-at';
            if (content.includes('commented') || content.includes('comment')) return 'mdi-comment';
            if (content.includes('completed')) return 'mdi-check-circle';
            if (content.includes('deleted')) return 'mdi-delete';
            if (content.includes('reassigned')) return 'mdi-account-switch';
            return 'mdi-bell';
        },
        getNotificationColor(item) {
            // Handle regular notifications
            const content = item.content?.toLowerCase() || '';
            if (content.includes('assigned')) return '#4CAF50';
            if (content.includes('updated')) return '#2196F3';
            if (content.includes('commented') || content.includes('comment')) return '#FF9800';
            if (content.includes('completed')) return '#66BB6A';
            if (content.includes('deleted')) return '#F44336';
            if (content.includes('reassigned')) return '#9C27B0';
            return '#757575';
        },
        formatNotificationDate(dateString) {
            if (!dateString) return '';
            const date = new Date(dateString);
            const now = new Date();
            const diffMs = now - date;
            const diffMins = Math.floor(diffMs / 60000);
            const diffHours = Math.floor(diffMs / 3600000);
            const diffDays = Math.floor(diffMs / 86400000);

            if (diffMins < 1) return 'Just now';
            if (diffMins < 60) return `${diffMins}m ago`;
            if (diffHours < 24) return `${diffHours}h ago`;
            if (diffDays < 7) return `${diffDays}d ago`;

            return date.toLocaleDateString();
        },
        openTaskDetailsDialog(item) {
            this.task_details_dialog = true;
            this.$store.dispatch("tasks/set_selected_task", item);
            this.original_selected_task = { ...item };
            setTimeout(() => {
                this.can_close_task_details_dialog = true;
            }, 100);
        },
        closeTaskDetailsDialog() {
            this.task_details_dialog = false;
            let selected_task = {
                name: this.get_selected_task.name,
                company_id: this.get_selected_task.company_id,
                project_id: this.get_selected_task.project_id,
                priority_id: this.get_selected_task.priority_id,
                status_id: this.get_selected_task.status_id,
                due_date: this.get_selected_task.due_date,
                // allocated_time: this.get_selected_task.allocated_time,
            };
            let original_task = {
                name: this.original_selected_task.name,
                company_id: this.original_selected_task.company_id,
                project_id: this.original_selected_task.project_id,
                priority_id: this.original_selected_task.priority_id,
                status_id: this.original_selected_task.status_id,
                due_date: this.original_selected_task.due_date,
                // allocated_time: this.original_selected_task.allocated_time,
            };
            if (JSON.stringify(selected_task) != JSON.stringify(original_task)) {
                this.updateTask(this.get_selected_task);
            }
            this.$store.dispatch("tasks/set_selected_task", {});
            this.original_selected_task = {};
        },
        async updateTask(item) {
            let payload = {
                company_id: item.company_id,
                assignee_id: item.assignee_id,
                project_id: item.project_id,
                name: item.name,
                status_id: item.status_id,
                description: item.description,
                priority_id: item.priority_id,
                due_date: item.due_date,
                allocated_time: item.allocated_time,
                tags: item.tags,
            };

            try {
                const response = await this.$axios.post(`api/tasks/update_task/${item.id}`, payload);

                if (response.data.response) {
                    this.$toast.success(response.data.message);

                    // ✅ Update the selected task with fresh data from server (including taskActivities)
                    if (response.data.data) {
                        this.$store.dispatch("tasks/set_selected_task", response.data.data);

                        // Also update the task in the arrays
                        this.updateTaskInArrays(item.id, response.data.data);
                    }
                } else {
                    this.$toast.error(response.data.message);
                }
            } catch (error) {
                console.error('Error updating task:', error);
                this.$toast.error('Failed to update task');
            }
        },
        getColor(priority_id) {
            switch (priority_id) {
                case 1:
                    return "red";
                case 2:
                    return "orange";
                case 3:
                    return "#FFC605";
                case 4:
                    return "blue";
                default:
                    return "grey";
            }
        },

        // Navigation methods for top tabs
        navigateToPage(tab) {
            if (tab.disabled) {
                return;
            }
            this.mobileNavOpen = false;
            this.$router.push(this.resolveTabRoute(tab));
        },

        // Some nav items (e.g. KPIs) route to a per-user path rather than a
        // fixed one — resolve those here instead of hardcoding a route string.
        resolveTabRoute(tab) {
            if (tab.dynamicRoute === 'my-kpis') {
                return `/directory/${this.get_user.id}/kpis`;
            }
            return tab.route;
        },

        isActiveTab(tab) {
            return this.$route.path === this.resolveTabRoute(tab);
        },

        hasAccess(tab) {
            // Task Approval is visible to all users — the page enforces manager-only access via the API
            if (tab.route === '/task_approval') {
                return true;
            }

            // Time Dashboard is hidden until we know the user has tracked time
            if (tab.requiresTrackedTime && !this.hasAgentTrackedTime) {
                return false;
            }

            // Special case: Recruitment - requires is_recruitment_access flag
            if (tab.requiresRecruitmentAccess) {
                return this.get_user && this.get_user.is_recruitment_access === 1;
            }

            // Sales assessments: recruitment access or Super Admin
            if (tab.requiresAssessmentAccess) {
                return hasAssessmentAccess(this.get_user);
            }

            // Company managers may open the Companies area (only for their own companies)
            if (tab.allowCompanyManagers && manageableCompanyIds(this.get_user, this.users).size > 0) {
                return true;
            }

            if (!tab.requiresRole) {
                return true;
            }

            // Allow specific user IDs regardless of role
            const allowedUserIds = [1, 177, 145];
            if (allowedUserIds.includes(this.get_user.id)) {
                return true;
            }

            // Per-tab user ID exceptions (e.g. Time Tracking access for user 47)
            if (tab.allowedUserIds && tab.allowedUserIds.includes(this.get_user.id)) {
                return true;
            }

            // Some tabs are also open to users who manage at least one other
            // user, regardless of role (e.g. Time Tracking for managers)
            if (tab.requiresManagesUsers && this.userManagesAtLeastOneUser) {
                return true;
            }

            // Check against role_members (multi-role support)
            // First check get_user (populated from login response)
            if (this.get_user.role_members && this.get_user.role_members.length > 0) {
                return this.get_user.role_members.some(m => tab.requiresRole.includes(m.user_role_id));
            }

            // Fallback: check users array (populated from getUsers API with role_members eager-loaded)
            const currentUser = this.users.find(u => u.id === this.get_user.id);
            if (currentUser && currentUser.role_members && currentUser.role_members.length > 0) {
                return currentUser.role_members.some(m => tab.requiresRole.includes(m.user_role_id));
            }

            // Final fallback to old single role_id
            return tab.requiresRole.includes(this.get_user.role_id);
        },

        getTabBadgeCount(tab) {
            if (!tab.badge) return 0;

            // Handle string references to data properties
            if (typeof tab.badge === 'string') {
                return this[tab.badge] || 0;
            }

            // Handle direct number values
            if (typeof tab.badge === 'number') {
                return tab.badge;
            }

            return 0;
        },

        setupChatListener() {
            if (!this.get_user || !this.get_user.id) {
                console.warn('[DefaultLayout] Cannot setup Pusher listeners: user not loaded');
                return;
            }

            // Check if Echo is available
            const echo = this.$echo;

            if (!echo) {
                console.warn('[DefaultLayout] Pusher/Echo not available. Real-time features are disabled.');
                console.warn('[DefaultLayout] To enable, ensure VUE_APP_PUSHER_KEY and VUE_APP_PUSHER_CLUSTER are set in .env');
                return;
            }

            try {
                const userId = this.get_user.id;

                // Log Pusher connection details
                if (echo.connector && echo.connector.pusher) {
                    const pusher = echo.connector.pusher;

                    // Connection state monitoring - surfaces reconnects/drops instead of failing silently
                    pusher.connection.bind('state_change', (states) => {
                        console.log(`[DefaultLayout] Pusher connection state: ${states.previous} -> ${states.current}`);
                    });

                    pusher.connection.bind('connected', () => {
                        console.log('[DefaultLayout] ✅ Pusher connected');
                    });

                    pusher.connection.bind('disconnected', () => {
                        console.warn('[DefaultLayout] ⚠️ Pusher disconnected');
                    });

                    pusher.connection.bind('error', (error) => {
                        console.error('[DefaultLayout] ❌ Pusher connection error:', error);
                    });
                }

                // ==========================================
                // CHANNEL 1: CHAT EVENTS (private-chat.{userId})
                // ==========================================
                const chatChannelName = `chat.${userId}`;

                const chatChannel = echo.private(chatChannelName);

                // Add subscription success/error handlers
                chatChannel.subscribed(() => {
                    console.log('[DefaultLayout] ✅ Subscribed to chat channel:', chatChannelName);
                });

                chatChannel.error((error) => {
                    console.error('[DefaultLayout] ❌ Error subscribing to chat channel:', error);
                });

                // Somebody removed a message: drop it wherever we are holding it.
                chatChannel.listen('.message-deleted', (data) => {
                    this.removeMessageLocally(data.conversation_id, {
                        id: data.id,
                        parent_id: data.parent_id || null,
                    });
                });

                // Event 1: New message received
                chatChannel.listen('.new-message', (data) => {
                    // Update conversation list with new message (use $set for reactivity)
                    const conversationIndex = this.conversations.findIndex(c => c.id === data.conversation_id);

                    if (conversationIndex !== -1) {
                        const conversation = this.conversations[conversationIndex];

                        // Use $set to ensure Vue detects the changes
                        this.$set(conversation, 'last_message', this.messagePreview(data));
                        this.$set(conversation, 'last_message_at', data.created_at);

                        // Only increment unread if message is from someone else
                        if (data.sender_id !== this.get_user.id) {
                            const newUnreadCount = (conversation.unread_count || 0) + 1;
                            this.$set(conversation, 'unread_count', newUnreadCount);
                        }

                        this.moveConversationToTop(conversationIndex);
                    } else {
                        // A conversation we don't know about yet (e.g. created while offline).
                        this.getConversations();
                    }

                    // Update total unread count (force reactivity)
                    this.unreadMessagesCount = this.conversations.reduce((sum, conv) => sum + (conv.unread_count || 0), 0);

                    // Add message to messages array (create array if it doesn't exist)
                    const conversationId = data.conversation_id;

                    if (!this.conversationMessages[conversationId]) {
                        this.$set(this.conversationMessages, conversationId, []);
                    }

                    const newMessage = {
                        id: data.id,
                        conversation_id: conversationId,
                        parent_id: data.parent_id || null,
                        sender_id: data.sender_id,
                        message: data.message,
                        // Carries the PMT Assistant's charts/cards so a broadcast
                        // reply renders rich, not as a plain markdown bubble.
                        pmt_payload: data.pmt_payload || null,
                        is_read: data.is_read ? 1 : 0, // Convert boolean to integer for consistency
                        created_at: data.created_at,
                        sender: data.sender,
                        attachments: data.attachments || [],
                        reactions: data.reactions || [],
                        task_links: data.task_links || [],
                        mentions: data.mentions || [],
                        thread: data.thread || null,
                        // Without this a received voice note renders as an empty bubble.
                        voice_note: data.voice_note || null,
                    };

                    this.mergeLinkedTasks([newMessage]);

                    // A reply never joins the main flow: it lands in its thread,
                    // and bumps the summary strip under the root message.
                    if (newMessage.parent_id) {
                        this.appendThreadReply(conversationId, newMessage);
                        return;
                    }

                    // Add message and create new array reference for reactivity
                    const messages = this.conversationMessages[conversationId];

                    // Check if message already exists (avoid duplicates from optimistic updates)
                    const messageExists = messages.some(m => m.id === data.id);
                    // While detached, the visible list has a gap to the present -
                    // appending here would stitch a message across it. The sidebar
                    // still updates below, so the conversation rises and shows a count.
                    if (!messageExists && !this.isConversationDetached(conversationId)) {
                        messages.push(newMessage);
                        this.$set(this.conversationMessages, conversationId, [...messages]);
                    }

                    // A message from a sender clears their typing indicator.
                    this.clearTypingUser(conversationId, data.sender_id);

                    // Alert only if the message is from someone else.
                    if (data.sender_id !== this.get_user.id) {
                        this.playNotificationSound();
                        // A desktop notification for when the tab is not in front;
                        // the in-app toast covers the case where it is.
                        this.showDesktopNotification(data);
                        this.$toast.info(`New message from ${data.sender?.name || 'Unknown'}`, {
                            timeout: 3000,
                            position: 'bottom-right',
                        });
                    }
                });

                // Event 1b: Reaction added or removed on a message
                chatChannel.listen('.message-reaction', (data) => {
                    this.applyReaction(data.conversation_id, data.message_id, data.reactions);
                });

                // Event 1d: A message was pinned or unpinned by someone else.
                // Only the loaded page has a bubble to decorate; the Board refetches on open.
                chatChannel.listen('.message-pinned', (data) => {
                    const message = this.findMessageInConversation(data.conversation_id, data.message_id);

                    if (message) {
                        this.$set(message, 'pinned', {
                            pinned_at: data.pinned_at,
                            pinned_by: data.pinned_by,
                        });
                    }
                });

                chatChannel.listen('.message-unpinned', (data) => {
                    const message = this.findMessageInConversation(data.conversation_id, data.message_id);

                    if (message) this.$set(message, 'pinned', null);
                });

                chatChannel.listen('.voice-note-transcribed', (data) => {
                    this.patchVoiceNote(data.message_id, {
                        transcript: data.transcript,
                        transcript_status: data.transcript_status,
                    });
                });

                // Event 1c: A task card was attached to an existing message
                chatChannel.listen('.message-task-linked', (data) => {
                    this.applyTaskLinks(data.conversation_id, data.message_id, data.task_links);
                });

                // Event 2: Conversation metadata updated.
                // Sent both when a message lands (last_message changes) and when a
                // group's name or membership changes, so merge whatever arrived.
                chatChannel.listen('.conversation-updated', (data) => {
                    const conversationIndex = this.conversations.findIndex(c => c.id === data.id);

                    if (conversationIndex !== -1) {
                        const conversation = this.conversations[conversationIndex];

                        // Use $set for each property to ensure reactivity
                        this.$set(conversation, 'last_message', data.last_message);
                        this.$set(conversation, 'last_message_at', data.last_message_at);
                        this.$set(conversation, 'unread_count', data.unread_count);

                        if (data.name !== undefined) this.$set(conversation, 'name', data.name);
                        if (data.participants) this.$set(conversation, 'participants', data.participants);
                        if (data.created_by !== undefined) this.$set(conversation, 'created_by', data.created_by);

                        this.moveConversationToTop(conversationIndex);
                    }

                    // Update total unread count
                    this.unreadMessagesCount = this.conversations.reduce((sum, conv) => sum + (conv.unread_count || 0), 0);
                });

                // Event 2b: We were removed from a conversation, or left it elsewhere
                chatChannel.listen('.conversation-removed', (data) => {
                    this.removeConversationLocally(data.conversation_id);
                });

                // Event 3: New conversation created
                chatChannel.listen('.conversation-created', (data) => {
                    // Skip if already present (e.g. creator's own refreshConversations() beat the broadcast)
                    const alreadyExists = this.conversations.some(c => c.id === data.id);
                    if (alreadyExists) {
                        return;
                    }

                    // Add conversation to beginning of list (unshift is reactive)
                    this.conversations.unshift(data);
                    this.seedReadReceipts([data]);

                    this.$toast.success('New conversation started', {
                        timeout: 2000,
                        position: 'bottom-right',
                    });
                });

                // Event 4: Conversation marked as read.
                // Sent to every participant, so who did the reading decides the meaning:
                // ourselves on another device (clear our badge) vs. the other party (read receipt).
                chatChannel.listen('.conversation-read', (data) => {
                    if (data.user_id === this.get_user.id) {
                        const conversationIndex = this.conversations.findIndex(c => c.id === data.conversation_id);

                        if (conversationIndex !== -1) {
                            // Use $set to ensure reactivity
                            this.$set(this.conversations[conversationIndex], 'unread_count', 0);
                        }

                        // Update total unread count
                        this.unreadMessagesCount = this.conversations.reduce((sum, conv) => sum + (conv.unread_count || 0), 0);
                        return;
                    }

                    // The other participant read our messages: record it for the "Seen" marker.
                    const receipts = this.conversationReadReceipts[data.conversation_id] || {};
                    this.$set(this.conversationReadReceipts, data.conversation_id, {
                        ...receipts,
                        [data.user_id]: data.read_at,
                    });
                });

                // ==========================================
                // CHANNEL 2: APP-WIDE PRESENCE (presence-online)
                // ==========================================
                echo.join('online')
                    .here((members) => {
                        this.onlineUserIds = members.map(m => Number(m.id));
                    })
                    .joining((member) => {
                        const id = Number(member.id);
                        if (!this.onlineUserIds.includes(id)) {
                            this.onlineUserIds.push(id);
                        }
                    })
                    .leaving((member) => {
                        const id = Number(member.id);
                        this.onlineUserIds = this.onlineUserIds.filter(uid => uid !== id);
                    })
                    .error((error) => {
                        console.error('[DefaultLayout] ❌ Error joining presence channel:', error);
                    });

            } catch (error) {
                console.error('[DefaultLayout] Failed to set up Pusher chat listener:', error);
            }
        },

        /**
         * Move a conversation to the top of the sidebar.
         *
         * Conversations are already ordered by recency, so a new message only ever
         * promotes one entry. Re-sorting the whole array on every message re-renders
         * the entire list for no reason.
         */
        moveConversationToTop(index) {
            if (index <= 0) return;
            const [conversation] = this.conversations.splice(index, 1);
            this.conversations.unshift(conversation);
        },

        /**
         * Sidebar preview text for a broadcast message payload.
         * Mirrors MessageService::lastMessagePreview() on the backend.
         */
        messagePreview(message) {
            if (message.message && message.message.trim() !== '') {
                return message.message;
            }

            const attachments = message.attachments || [];
            if (attachments.length) {
                const noun = attachments.every(a => a.is_image) ? 'photo' : 'file';
                return attachments.length === 1
                    ? `Sent a ${noun}`
                    : `Sent ${attachments.length} ${noun}s`;
            }

            if (message.template_response) {
                return `Sent a ${message.template_response.template_name}`;
            }

            const tasks = message.task_links || [];
            if (tasks.length) {
                return tasks.length === 1 ? 'Shared a task' : `Shared ${tasks.length} tasks`;
            }

            return '';
        },

        /**
         * Ask the server to summarise a conversation.
         *
         * Read-only: the summary is returned for display and never posted into
         * the conversation. Throws with a user-safe message on failure.
         */
        async summarizeConversation(conversationId, mode) {
            const response = await this.$axios.post(`api/conversations/${conversationId}/summarize`, { mode });

            if (!response.data || !response.data.response) {
                throw new Error(response.data?.message || 'Could not generate a summary');
            }

            return response.data.data;
        },

        /**
         * Propose action items found in a conversation. Creates nothing.
         */
        async extractActionItems(conversationId) {
            const response = await this.$axios.post(`api/conversations/${conversationId}/action-items`);

            if (!response.data || !response.data.response) {
                throw new Error(response.data?.message || 'Could not extract action items');
            }

            return response.data.data;
        },

        /**
         * The distinct tasks inside one message, so "do X, then Y" can become two
         * tasks instead of one. Reads a voice note's transcript too.
         */
        async extractTasksFromMessage(messageId) {
            const response = await this.$axios.post(`api/messages/extract_tasks/${messageId}`);

            if (!response.data || !response.data.response) {
                throw new Error(response.data?.message || 'Could not read tasks from that message');
            }

            return response.data.data?.items || [];
        },

        /**
         * Classify the unread conversations in the user's own inbox.
         *
         * Cached server-side against the newest message in each conversation, so
         * calling this on every inbox load only costs a request when something
         * new has actually arrived. Pass force to re-classify regardless.
         */
        async triageInbox(force = false) {
            const response = await this.$axios.post('api/ai/triage', force ? { force: true } : {});

            if (!response.data || !response.data.response) {
                throw new Error(response.data?.message || 'Could not triage the inbox');
            }

            return response.data.data;
        },

        /**
         * Rewrite a message draft. Returns the suggestion; it is never sent.
         */
        async composeDraft(text, action, options = {}) {
            const response = await this.$axios.post('api/ai/compose', { text, action, ...options });

            if (!response.data || !response.data.response) {
                throw new Error(response.data?.message || 'Could not rewrite that draft');
            }

            return response.data.data;
        },

        /**
         * Seed read receipts from conversation payloads.
         *
         * Without this the "Seen" markers would only ever appear from a live
         * conversation-read broadcast, and would vanish on every page refresh.
         */
        seedReadReceipts(conversations) {
            (conversations || []).forEach(conversation => {
                if (!conversation.read_receipts) return;

                const existing = this.conversationReadReceipts[conversation.id] || {};
                this.$set(this.conversationReadReceipts, conversation.id, {
                    ...existing,
                    ...conversation.read_receipts,
                });
            });
        },

        /**
         * Create a group conversation and place it at the top of the sidebar.
         */
        async createGroupConversation(name, userIds) {
            const response = await this.$axios.post('api/conversations/create_conversation', {
                type: 'group',
                name,
                user_ids: userIds,
            });

            if (!response.data || !response.data.response) {
                throw new Error(response.data?.message || 'Failed to create group');
            }

            const conversation = response.data.conversation;

            this.seedReadReceipts([conversation]);

            if (!this.conversations.some(c => c.id === conversation.id)) {
                this.conversations.unshift(conversation);
            }

            return conversation;
        },

        /**
         * Replace a conversation we already hold with the server's version.
         */
        applyConversationUpdate(conversation) {
            const index = this.conversations.findIndex(c => c.id === conversation.id);

            this.seedReadReceipts([conversation]);

            if (index === -1) {
                this.conversations.unshift(conversation);
                return;
            }

            // Preserve the unread count we track locally: the payload is computed for
            // whoever the broadcast was addressed to, and a REST reply reflects only us.
            this.$set(this.conversations, index, { ...this.conversations[index], ...conversation });
        },

        /**
         * Drop a conversation the user is no longer part of.
         */
        removeConversationLocally(conversationId) {
            const index = this.conversations.findIndex(c => c.id === conversationId);
            if (index !== -1) {
                this.conversations.splice(index, 1);
            }

            this.$delete(this.conversationMessages, conversationId);
            this.$delete(this.messagesHasMore, conversationId);
            this.$delete(this.messagesHasNewer, conversationId);
            this.$delete(this.conversationDetached, conversationId);

            this.unreadMessagesCount = this.conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0);

            if (this.activeConversationId === conversationId) {
                this.unwatchConversationPresence();
                this.activeConversationId = null;
            }
        },

        async addGroupParticipants(conversationId, userIds) {
            const response = await this.$axios.post(`api/conversations/${conversationId}/participants`, {
                user_ids: userIds,
            });

            if (!response.data || !response.data.response) {
                throw new Error(response.data?.message || 'Failed to add people');
            }

            this.applyConversationUpdate(response.data.conversation);
            return response.data;
        },

        async removeGroupParticipant(conversationId, userId) {
            const response = await this.$axios.delete(`api/conversations/${conversationId}/participants/${userId}`);

            if (!response.data || !response.data.response) {
                throw new Error(response.data?.message || 'Failed to remove person');
            }

            this.applyConversationUpdate(response.data.conversation);
            return response.data;
        },

        async setGroupParticipantRole(conversationId, userId, role) {
            const response = await this.$axios.post(
                `api/conversations/${conversationId}/participants/${userId}/role`,
                { role }
            );

            if (!response.data || !response.data.response) {
                throw new Error(response.data?.message || 'Failed to update role');
            }

            this.applyConversationUpdate(response.data.conversation);
            return response.data;
        },

        async renameGroupConversation(conversationId, name) {
            const response = await this.$axios.post(`api/conversations/${conversationId}/rename`, { name });

            if (!response.data || !response.data.response) {
                throw new Error(response.data?.message || 'Failed to rename group');
            }

            this.applyConversationUpdate(response.data.conversation);
            return response.data;
        },

        async leaveGroupConversation(conversationId) {
            const response = await this.$axios.post(`api/conversations/${conversationId}/leave`);

            if (!response.data || !response.data.response) {
                throw new Error(response.data?.message || 'Failed to leave group');
            }

            this.removeConversationLocally(conversationId);
            return response.data;
        },

        /**
         * Seed the linked-task map from message payloads.
         *
         * Messages arrive with a full task summary on each link; we keep one copy per
         * task id so cards stay consistent with each other and with the task drawer.
         */
        mergeLinkedTasks(messages) {
            (messages || []).forEach(message => {
                (message.task_links || []).forEach(link => {
                    if (link.task) {
                        this.$set(this.linkedTasks, link.task_id, link.task);
                    }
                });
            });
        },

        /**
         * Patch one linked task, so every card showing it re-renders.
         */
        patchLinkedTask(taskId, updates) {
            const current = this.linkedTasks[taskId];
            if (!current) return;
            this.$set(this.linkedTasks, taskId, { ...current, ...updates });
        },

        /**
         * Search the tasks the current user may reference, for the chat task picker.
         */
        async searchTasks(query) {
            try {
                const response = await this.$axios.get('api/tasks/search', { params: { q: query } });
                return response.data?.response ? response.data.data : [];
            } catch (error) {
                console.error('[DefaultLayout] Task search failed:', error);
                return [];
            }
        },

        /**
         * Attach tasks to a message that has already been sent.
         */
        async linkTasksToMessage(conversationId, messageId, taskIds) {
            const response = await this.$axios.post(`api/messages/link_task/${messageId}`, { task_ids: taskIds });

            if (!response.data || !response.data.response) {
                throw new Error(response.data?.message || 'Failed to link task');
            }

            this.applyTaskLinks(conversationId, messageId, response.data.data.task_links);
            return response.data.data;
        },

        /**
         * Replace the task links on a message we already hold.
         */
        applyTaskLinks(conversationId, messageId, taskLinks) {
            (taskLinks || []).forEach(link => {
                if (link.task) this.$set(this.linkedTasks, link.task_id, link.task);
            });

            const messages = this.conversationMessages[conversationId];
            if (!messages) return;

            const index = messages.findIndex(m => m.id === messageId);
            if (index === -1) return;

            this.$set(messages[index], 'task_links', taskLinks);
        },

        /**
         * Open the task detail drawer for a task referenced in chat.
         *
         * Tasks the user owns are already loaded; anything else is fetched, since a
         * card can reference a task that never appears in this user's own lists.
         */
        async openTaskDetails(taskId) {
            const existing = this.findTaskInArrays(taskId);
            if (existing) {
                this.openTaskDetailsDialog(existing);
                return;
            }

            try {
                const response = await this.$axios.get(`api/tasks/get_task/${taskId}`);
                const task = response.data?.data;

                if (task) {
                    this.openTaskDetailsDialog(task);
                } else {
                    this.$toast.error('Could not open that task');
                }
            } catch (error) {
                console.error('[DefaultLayout] Failed to load task:', error);
                this.$toast.error('Could not open that task');
            }
        },

        /**
         * Change a linked task's status from its card, rolling back if the server refuses.
         */
        async updateLinkedTaskStatus(taskId, statusId) {
            const original = this.linkedTasks[taskId];
            if (!original) return;

            const status = (this.statuses || []).find(s => s.id === statusId);
            this.patchLinkedTask(taskId, {
                status_id: statusId,
                status: status ? { id: status.id, name: status.name } : original.status,
            });

            try {
                const response = await this.$axios.post(`api/tasks/update_task/${taskId}`, { status_id: statusId });

                if (!response.data || !response.data.response) {
                    throw new Error(response.data?.message || 'Failed to update task');
                }

                // Keep the task lists and the drawer in step with the card.
                this.updateTaskInArrays(taskId, { status_id: statusId });
            } catch (error) {
                console.error('[DefaultLayout] Failed to update linked task status:', error);
                this.$set(this.linkedTasks, taskId, original);
                this.$toast.error(error.message || 'You are not allowed to update this task');
            }
        },

        /**
         * Change a linked task's assignees from its card.
         */
        async updateLinkedTaskAssignees(taskId, userIds) {
            const original = this.linkedTasks[taskId];
            if (!original) return;

            const assignees = userIds
                .map(id => this.users.find(u => u.id === id))
                .filter(Boolean)
                .map(u => ({ id: u.id, name: u.name, profile_picture: u.profile_picture || null }));

            this.patchLinkedTask(taskId, { assignees });

            try {
                const response = await this.$axios.post(`api/tasks/manage_assignees/${taskId}`, { assignee_ids: userIds });

                if (!response.data || !response.data.response) {
                    throw new Error(response.data?.message || 'Failed to update assignees');
                }
            } catch (error) {
                console.error('[DefaultLayout] Failed to update linked task assignees:', error);
                this.$set(this.linkedTasks, taskId, original);
                this.$toast.error(error.message || 'You are not allowed to assign this task');
            }
        },

        /**
         * Replace the reaction set on a message we already hold.
         */
        applyReaction(conversationId, messageId, reactions) {
            const messages = this.conversationMessages[conversationId];
            if (!messages) return;

            const index = messages.findIndex(m => m.id === messageId);
            if (index === -1) return;

            this.$set(messages[index], 'reactions', reactions);
        },

        /**
         * Ask the browser, once, for permission to show desktop notifications.
         * A no-op if the API is missing or the user already answered.
         *
         * Several browsers only honour requestPermission() inside a user
         * gesture, so it is armed on the next click or keypress rather than
         * fired on load, then disarmed after one shot.
         */
        requestNotificationPermission() {
            if (!('Notification' in window) || Notification.permission !== 'default') return;

            const ask = () => {
                window.removeEventListener('click', ask);
                window.removeEventListener('keydown', ask);
                Notification.requestPermission().catch(() => { });
            };

            window.addEventListener('click', ask);
            window.addEventListener('keydown', ask);
        },

        /**
         * A short line describing an incoming message for a desktop notification.
         * Voice notes, photos, and forms have no text, so they get a label.
         */
        messageNotificationBody(data) {
            if (data.message && data.message.trim()) return data.message.trim();
            if (data.voice_note) return '🎤 Voice message';
            if (data.attachments && data.attachments.length) {
                const allImages = data.attachments.every(
                    a => a.is_image || (a.mime_type || '').startsWith('image/')
                );
                return allImages ? '📷 Photo' : '📎 Attachment';
            }
            if (data.template_response) return `📋 ${data.template_response.template_name || 'Form'}`;
            if (data.task_links && data.task_links.length) return '🔗 Shared a task';
            return 'New message';
        },

        /**
         * Pop a desktop notification for a message from someone else - the way
         * Google Chat does - so it reaches the user when the tab is backgrounded
         * or the browser is behind another window. Skipped when the tab is
         * already in front, where the in-app toast is enough. Clicking it focuses
         * the window and opens the conversation.
         */
        showDesktopNotification(data) {
            if (!('Notification' in window) || Notification.permission !== 'granted') return;
            if (document.visibilityState === 'visible' && document.hasFocus()) return;

            try {
                const notification = new Notification(data.sender?.name || 'New message', {
                    body: this.messageNotificationBody(data),
                    icon: this.getProfilePictureUrl(data.sender?.profile_picture) || '/favicon-32x32.png',
                    // One notification per conversation, refreshed as messages arrive,
                    // rather than a stack of them.
                    tag: `chat-${data.conversation_id}`,
                    renotify: true,
                    // We play our own sound, so silence the system's.
                    silent: true,
                });

                notification.onclick = () => {
                    window.focus();
                    const already = this.$route.path === '/chats'
                        && String(this.$route.query.conversation) === String(data.conversation_id);
                    if (!already) {
                        this.$router.push({
                            path: '/chats',
                            query: { conversation: data.conversation_id },
                        }).catch(() => { });
                    }
                    notification.close();
                };
            } catch (error) {
                // Mobile Chrome throws when constructing a Notification without a
                // service worker; degrade quietly to the sound + toast.
                console.warn('[Notification] could not display:', error.message);
            }
        },

        //joo changes to play sound on notification
        playNotificationSound() {
            try {
                const audio = new Audio('/notification-sound.mp3');
                audio.volume = 0.5; // Set volume to 50%
                
                // Attempt to play the sound
                const playPromise = audio.play();
                
                if (playPromise !== undefined) {
                    playPromise
                        .then(() => {
                            // Audio playback started successfully
                            // console.log('[Notification Sound] Played successfully');
                        })
                        .catch(error => {
                            // Auto-play was prevented by browser policy
                            // This is expected on first load before user interaction
                            if (error.name === 'NotAllowedError') {
                                // Silently ignore - browser requires user interaction first
                                // User will hear sound after their first click/interaction
                            } else {
                                console.warn('[Notification Sound] Playback failed:', error.message);
                            }
                        });
                }
            } catch (error) {
                console.error('Error creating notification sound:', error);
            }
        },

        async logout() {
            // Old implementation (caused navigation errors):
            // await this.$axios.post("api/logout")
            // localStorage.clear();
            // this.$router.push({ name: "Login" });
            
            try {
                await this.$axios.post("api/logout")
            } catch (error) {
                console.error('Logout API error:', error);
            }
            
            localStorage.clear();

            // localStorage.clear() does not touch the in-memory store, and this
            // is a SPA so nothing reloads. Reset the display preferences too,
            // otherwise they linger for whoever logs in next on this machine.
            this.$store.dispatch('settings/adoptUserPreferences', null);

            // Use replace to avoid navigation history issues
            if (this.$route.name !== 'Login') {
                this.$router.replace({ name: "Login" }).catch(() => {});
            }
        },

        async exitImpersonation() {
            if (!this.is_impersonating) return;
            this.exitingImpersonation = true;
            try {
                // Best-effort: revoke the impersonation token on the server
                try {
                    await this.$axios.post('api/impersonate/stop');
                } catch (error) {
                    console.error('Stop impersonation API error:', error);
                }

                const original = this.get_impersonator;

                // Clear impersonator state first so the banner hides immediately
                this.$store.dispatch('auth/set_impersonator', null);

                // Restore original admin's session
                if (original && original.user && original.token) {
                    this.$store.dispatch('auth/set_user', original.user);
                    this.$store.dispatch('auth/set_token', original.token);
                    this.$toast.success('Returned to your account');
                    this.$router.push({ name: 'Dashboard' }).catch(() => { });
                    setTimeout(() => window.location.reload(), 100);
                } else {
                    // No saved admin session — fall back to login
                    localStorage.clear();
                    this.$router.replace({ name: 'Login' }).catch(() => { });
                }
            } finally {
                this.exitingImpersonation = false;
            }
        },

        // Added from leave.service.js
        /**
         * get to approve leave request
         */
        async getToApproveLeaveRequest() {
            try {
                const response = await this.$axios.get('/api/leave/approval')
                const { data } = response
                this.$store.commit('leave/SET_TO_APPROVE_LEAVE_REQUEST', data)
            } catch (error) {
                console.error("Error fetching leave request", error)
            }
        },

        // Added from overtime.service.js
        /**
         * get all to approve overtime data
         */
        async getToApproveOvertime() {
            try {
                const response = await this.$axios.get("api/overtime/admin");
                const { data } = response;
                // api/overtime/admin returns every pending overtime request company-wide
                // (it only filters by status, not approver) — scope it down to requests
                // this user is actually the approver for, matching how the approvals hub
                // itself counts pending overtime (see ApprovalTab.vue/OvertimeAdminApproval.vue),
                // otherwise the badge lights up for overtime awaiting a *different* manager.
                const myPendingOvertime = (data || []).filter(item => item.approver_id === this.get_user.id);
                this.$store.dispatch("leave/setToApproveOTData", myPendingOvertime);
            } catch (error) {
                console.error("Error fetching overtime data", error);
            }
        },

        // Re-pulls both "to approve" counts feeding the settings-menu gear
        // badge. Called by the approvals hub/tabs after a decision is made,
        // and on (re)entering the hub, since these are only fetched once at
        // app mount otherwise.
        async refreshApprovalCounts() {
            await Promise.all([this.getToApproveLeaveRequest(), this.getToApproveOvertime()]);
        },




        // User service methods
        async getUsers() {
            try {
                const response = await this.$axios.get('/api/users/get_users')
                const { data } = response.data
                this.users = data
                return data
            } catch (error) {
                console.error('Error fetching users', error)
            }
        },

        // Role members service methods (uses user_role_members table)
        async getAllManagers() {
            try {
                const [membersResponse, roleManagersResponse] = await Promise.all([
                    this.$axios.get('api/users/get_all_managers'),
                    this.$axios.get('api/users/get_role_managers'),
                ]);
                if (membersResponse.data.response) {
                    this.allManagers = membersResponse.data.data || [];
                }
                if (roleManagersResponse.data.response) {
                    this.roleManagers = roleManagersResponse.data.data || [];
                }
                return this.allManagers;
            } catch (error) {
                console.error('Error fetching managers', error);
                return [];
            }
        },

        // Leave management service methods
        async getLeaveBalances() {
            try {
                this.isLoadingLeaveBalances = true;
                const response = await this.$axios.get('api/leave/all_balances');
                console.log('[DefaultLayout] Leave balances response:', response.data);
                
                if (response.data.response) {
                    this.leaveBalances = response.data.data || [];
                    console.log('[DefaultLayout] Leave balances loaded:', this.leaveBalances.length);
                    return this.leaveBalances;
                }
                return [];
            } catch (error) {
                console.error('[DefaultLayout] Error fetching leave balances:', error);
                return [];
            } finally {
                this.isLoadingLeaveBalances = false;
            }
        },

        // SOD/EOD (Start of Day / End of Day) service methods
        async getSodEodStatus() {
            try {
                const response = await this.$axios.get('api/agent/sod-eod/status');
                if (response.data.response) {
                    // The status endpoint's fields (sod_required, sod_submitted, etc.)
                    // are top-level on the response body, not nested under a `data`
                    // key — matches every other agent/* endpoint's shape.
                    this.sodEodStatus = response.data || null;
                    return this.sodEodStatus;
                }
                return null;
            } catch (error) {
                console.error('[DefaultLayout] Error fetching SOD/EOD status:', error);
                return null;
            }
        },
        async getTodaySod() {
            try {
                const response = await this.$axios.get('api/agent/sod/today');
                if (response.data.response) {
                    this.todaySod = response.data.sod_submission || null;
                    return this.todaySod;
                }
                return null;
            } catch (error) {
                console.error('[DefaultLayout] Error fetching today\'s SOD:', error);
                return null;
            }
        },
        async getTodayEodPrefill() {
            try {
                const response = await this.$axios.get('api/agent/eod/today');
                if (response.data.response) {
                    // Keep the whole body (planned_tasks, planned_count, completed_count,
                    // sod_submission, eod_submission) — EodDialog needs all of it, not
                    // just the eod_submission sub-object.
                    this.todayEodPrefill = response.data || null;
                    return this.todayEodPrefill;
                }
                return null;
            } catch (error) {
                console.error('[DefaultLayout] Error fetching EOD prefill:', error);
                return null;
            }
        },
        // Submit or resubmit today's SOD. `submitted_at` is stamped here (not left
        // for the server to default) so it always reflects the moment the browser
        // actually sent the request — the field the backend uses for early/late
        // display and for §2's cross-surface conflict resolution.
        async submitSod(payload) {
            const body = { ...payload, submitted_at: new Date().toISOString() };
            const response = await this.$axios.post('api/agent/sod', body);
            if (!response.data.response) {
                throw new Error(response.data.message || 'Failed to submit Start of Day');
            }
            // Re-fetch rather than trust the POST body into the cache: this is
            // correct whether the submission was applied or was superseded by a
            // newer one made elsewhere (§2) — either way the GET reflects the
            // actual current server state.
            await this.getTodaySod();
            this.getSodEodStatus();
            return response.data;
        },
        // Submit or resubmit today's EOD. Same rationale as submitSod.
        async submitEod(payload) {
            const body = { ...payload, submitted_at: new Date().toISOString() };
            const response = await this.$axios.post('api/agent/eod', body);
            if (!response.data.response) {
                throw new Error(response.data.message || 'Failed to submit End of Day');
            }
            await this.getTodayEodPrefill();
            this.getSodEodStatus();
            return response.data;
        },
        // Admin-only support/testing tool — deletes today's own SOD/EOD row
        // entirely (server enforces the admin check too; this is a
        // destructive shortcut for clearing bad/test data, not a feature for
        // regular employee self-service).
        async clearSod() {
            const response = await this.$axios.delete('api/agent/sod');
            if (!response.data.response) {
                throw new Error(response.data.message || 'Failed to clear Start of Day');
            }
            await this.getTodaySod();
            this.getSodEodStatus();
            return response.data;
        },
        async clearEod() {
            const response = await this.$axios.delete('api/agent/eod');
            if (!response.data.response) {
                throw new Error(response.data.message || 'Failed to clear End of Day');
            }
            await this.getTodayEodPrefill();
            this.getSodEodStatus();
            return response.data;
        },
        async getMySodEodHistory() {
            try {
                this.isLoadingMySodEodHistory = true;
                const response = await this.$axios.get('api/agent/sod-eod/history');
                if (response.data.response) {
                    this.mySodEodHistory = response.data.items || [];
                    return this.mySodEodHistory;
                }
                return [];
            } catch (error) {
                console.error('[DefaultLayout] Error fetching SOD/EOD history:', error);
                return [];
            } finally {
                this.isLoadingMySodEodHistory = false;
            }
        },
        async getManagedSodEodHistory(userId) {
            if (!userId) return [];
            try {
                const response = await this.$axios.get(`api/agent/sod-eod/history/${userId}`);
                if (response.data.response) {
                    this.managedSodEodHistory = response.data.items || [];
                    return this.managedSodEodHistory;
                }
                return [];
            } catch (error) {
                console.error('[DefaultLayout] Error fetching managed SOD/EOD history:', error);
                return [];
            }
        },
        // SOD/EOD reporting: date-ranged summary/trend/workload/incomplete-reason/
        // consistency/tracked-time aggregates, computed server-side. Separate
        // from the unbounded history above (which still feeds the calendar and
        // per-day list) so the report widgets can use a smaller, targeted range.
        async getSodEodReport(dateFrom, dateTo) {
            try {
                this.isLoadingSodEodReport = true;
                const response = await this.$axios.get('api/agent/sod-eod/report', { params: { date_from: dateFrom, date_to: dateTo } });
                if (response.data.response) {
                    this.sodEodReport = response.data;
                    return this.sodEodReport;
                }
                return null;
            } catch (error) {
                console.error('[DefaultLayout] Error fetching SOD/EOD report:', error);
                return null;
            } finally {
                this.isLoadingSodEodReport = false;
            }
        },
        // A managed user's full report (manager's drill-down from the team page) —
        // same shape/endpoint family as getSodEodReport, manager-scoped server-side.
        async getManagedSodEodReport(userId, dateFrom, dateTo) {
            if (!userId) return null;
            try {
                this.isLoadingSodEodReport = true;
                const response = await this.$axios.get(`api/agent/sod-eod/report/${userId}`, { params: { date_from: dateFrom, date_to: dateTo } });
                if (response.data.response) {
                    this.managedSodEodReport = response.data;
                    return this.managedSodEodReport;
                }
                return null;
            } catch (error) {
                console.error('[DefaultLayout] Error fetching managed SOD/EOD report:', error);
                return null;
            } finally {
                this.isLoadingSodEodReport = false;
            }
        },
        // Manager/admin team-wide report for the selected date range — server
        // aggregates across the whole scope in one pass (see backend notes),
        // never a per-user loop from the client.
        async getTeamSodEodReport(dateFrom, dateTo) {
            try {
                this.isLoadingTeamSodEodReport = true;
                const response = await this.$axios.get('api/agent/sod-eod/team-report', { params: { date_from: dateFrom, date_to: dateTo } });
                if (response.data.response) {
                    this.teamSodEodReport = response.data;
                    return this.teamSodEodReport;
                }
                return null;
            } catch (error) {
                console.error('[DefaultLayout] Error fetching team SOD/EOD report:', error);
                return null;
            } finally {
                this.isLoadingTeamSodEodReport = false;
            }
        },
        // Re-poll status every 20 minutes while the tab is open, so the EOD header
        // slot/auto-popup can still activate later in the same session (crossing
        // eod_prompt_hour) without requiring a page reload.
        startSodEodPolling() {
            this.stopSodEodPolling();
            this.sodEodPollInterval = setInterval(() => {
                this.getSodEodStatus().then(() => this.maybeAutoOpenSodEod());
            }, 20 * 60 * 1000); // 20 minutes
        },
        stopSodEodPolling() {
            if (this.sodEodPollInterval) {
                clearInterval(this.sodEodPollInterval);
                this.sodEodPollInterval = null;
            }
        },
        // Automatic popup trigger. Only ever opens the header-identical dialogs —
        // never a separate form. Respects the per-work-date localStorage snooze;
        // the header action (openSodDialog/openEodDialog) intentionally ignores it.
        maybeAutoOpenSodEod() {
            const status = this.sodEodStatus;
            if (!status || !status.work_date) return;
            if (this.sodDialogOpen || this.eodDialogOpen) return; // don't stack popups

            const workDate = status.work_date;

            if (status.sod_required && !status.sod_submitted && !this.isSodEodSnoozed('sod', workDate)) {
                this.sodDialogOpen = true;
                return;
            }

            // The automatic EOD popup fires a fixed delay after SOD was
            // actually submitted (not a fixed clock hour, and not merely
            // "SOD is submitted" — that flag stays true for the rest of the
            // day, which previously made the popup fire immediately on the
            // next status refresh after submitting SOD). No SOD submission
            // today means no anchor to delay from, so the automatic popup
            // simply never fires that day; the header button is unaffected
            // and still lets the user open EOD manually at any time.
            if (!status.sod_submitted || !status.sod_submitted_at) return;
            const delayHours = status.eod_prompt_delay_hours ?? 8;
            const eodEligibleAt = new Date(status.sod_submitted_at).getTime() + delayHours * 60 * 60 * 1000;
            const eodEligible = Date.now() >= eodEligibleAt;
            if (eodEligible && status.eod_required && !status.eod_submitted && !this.isSodEodSnoozed('eod', workDate)) {
                this.eodDialogOpen = true;
            }
        },
        // One key per kind holding { workDate, until } — each snooze overwrites
        // the previous one, so nothing accumulates day over day. A snooze from
        // a different work date never matches, so each new day starts un-snoozed.
        isSodEodSnoozed(kind, workDate) {
            try {
                const snooze = JSON.parse(window.localStorage.getItem(`${kind}_snooze`) || 'null');
                return !!snooze && snooze.workDate === workDate && Date.now() < Number(snooze.until);
            } catch (error) {
                return false;
            }
        },
        // Removes the old per-date `sod_snooze_until_<date>` / `eod_snooze_until_<date>`
        // keys left behind by the previous scheme.
        cleanupLegacySodEodSnoozeKeys() {
            try {
                const legacy = [];
                for (let i = 0; i < window.localStorage.length; i++) {
                    const key = window.localStorage.key(i);
                    if (/^(sod|eod)_snooze_until_/.test(key)) legacy.push(key);
                }
                legacy.forEach((key) => window.localStorage.removeItem(key));
            } catch (error) {
                // localStorage unavailable — nothing to clean.
            }
        },
        // durationMs omitted = snoozed for the rest of the work date.
        snoozeSodEod(kind, workDate, durationMs = null) {
            if (!workDate) return;
            try {
                const until = durationMs == null ? Number.MAX_SAFE_INTEGER : Date.now() + durationMs;
                window.localStorage.setItem(`${kind}_snooze`, JSON.stringify({ workDate, until }));
            } catch (error) {
                // localStorage unavailable (private mode, quota, etc.) — snoozing is
                // a convenience only, safe to silently skip.
            }
        },
        // Header actions — always available regardless of snooze state.
        openSodDialog() {
            this.sodDialogOpen = true;
        },
        openEodDialog() {
            this.eodDialogOpen = true;
        },
        // Dismiss (Cancel/X) handlers — snooze the *automatic* re-trigger only.
        // SOD stays dismissed for the rest of the day; EOD re-prompts after 30 min.
        handleSodDismissed() {
            this.snoozeSodEod('sod', this.sodEodStatus?.work_date);
        },
        handleEodDismissed() {
            this.snoozeSodEod('eod', this.sodEodStatus?.work_date, 30 * 60 * 1000);
        },
        handleSodSubmitted() {
            this.getSodEodStatus();
        },
        handleEodSubmitted() {
            this.getSodEodStatus();
        },

        async getTasks(forceCompleted = false) {
            // If forceCompleted is true, (re)load only completed tasks
            if (forceCompleted) {
                return this.loadCompletedTasks();
            }

            try {
                const user = this.$store.getters["auth/get_user"];
                if (!user?.id) return [];

                // Always load only active (non-completed) tasks here. Completed
                // tasks are loaded separately (and paginated) via loadCompletedTasks().
                const activePayload = {
                    user_id: user.id,
                    is_completed: 0,
                };

                const activeResponse = await this.$axios.post('api/tasks/get_user_tasks', activePayload);

                if (activeResponse.data.response) {
                    this.all_personal_tasks = this.populateTaskRoles(activeResponse.data.personal_tasks || []);
                    this.all_assigned_tasks = this.populateTaskRoles(activeResponse.data.assigned_tasks || []);
                    return [...this.all_personal_tasks, ...this.all_assigned_tasks];
                }
                return [];
            } catch (error) {
                console.error('error fetching tasks', error);
                return [];
            }
        },

        async getUnapprovedTasks() {
            // Load tasks with status_id = 8 (pending approval) and status_id = 9 (rejected)
            try {
                const user = this.$store.getters["auth/get_user"];
                if (!user?.id) {
                    this.all_unapproved_tasks = [];
                    this.all_rejected_tasks = [];
                    return [];
                }

                const unapprovedPayload = {
                    status_id: 8, // Unapproved status
                };

                const [unapprovedResponse, rejectedResponse] = await Promise.all([
                    this.$axios.get('api/tasks/get_unapproved_tasks', unapprovedPayload),
                    this.$axios.get('api/tasks/get_unapproved_tasks', { status_id: 9 }),
                ]);

                if (unapprovedResponse.data.response) {
                    this.all_unapproved_tasks = unapprovedResponse.data.data || [];
                    if (unapprovedResponse.data.is_manager !== undefined) {
                        this.isManagerForApproval = unapprovedResponse.data.is_manager;
                    }
                } else {
                    this.all_unapproved_tasks = [];
                }

                if (rejectedResponse.data.response) {
                    this.all_rejected_tasks = rejectedResponse.data.data || [];
                } else {
                    this.all_rejected_tasks = [];
                }

                return this.all_unapproved_tasks;
            } catch (error) {
                console.error('Error fetching unapproved tasks', error);
                this.all_unapproved_tasks = [];
                this.all_rejected_tasks = [];
                return [];
            }
        },

        async getTasksForUser(userId, forceCompleted = false, isViewing = false) {
            // Fetch tasks for a specific user (used by admin to view other users' tasks)
            try {
                if (!userId) return [];

                // Check if current user is CEO - if so, always set is_viewing to 0
                const currentUser = this.$store.getters["auth/get_user"];
                const ceoEmail = process.env.VUE_APP_CEO_EMAIL;
                const isCEO = currentUser?.email === ceoEmail;

                const payload = {
                    user_id: userId,
                    is_completed: forceCompleted ? 1 : 0,
                    is_viewing: (isCEO || !isViewing) ? 0 : 1,
                };

                const response = await this.$axios.post('api/tasks/get_user_tasks', payload);

                if (response.data.response) {
                    this.all_personal_tasks = response.data.personal_tasks || [];
                    this.all_assigned_tasks = response.data.assigned_tasks || [];
                    return [...this.all_personal_tasks, ...this.all_assigned_tasks];
                }
                return [];
            } catch (error) {
                console.error('Error fetching tasks for user', error);
                return [];
            }
        },

        // Loads the first page (latest COMPLETED_TASKS_PAGE_SIZE) of completed
        // personal + assigned tasks, ordered by most recently completed.
        // Completed tasks are kept in their own arrays (never merged into
        // all_personal_tasks / all_assigned_tasks) so the dashboard can page
        // through them independently of the active task lists.
        async loadCompletedTasks() {
            const user = this.$store.getters["auth/get_user"];
            if (!user?.id) return [];

            this.isLoadingCompletedPersonalTasks = true;
            this.isLoadingCompletedAssignedTasks = true;

            try {
                const payload = {
                    user_id: user.id,
                    is_completed: 1,
                    scope: 'both',
                    limit: this.COMPLETED_TASKS_PAGE_SIZE,
                    offset: 0,
                };

                const response = await this.$axios.post('api/tasks/get_user_tasks', payload);

                if (response.data.response) {
                    this.all_completed_personal_tasks = response.data.personal_tasks || [];
                    this.all_completed_assigned_tasks = response.data.assigned_tasks || [];
                    this.completedPersonalTotal = response.data.personal_tasks_total ?? this.all_completed_personal_tasks.length;
                    this.completedAssignedTotal = response.data.assigned_tasks_total ?? this.all_completed_assigned_tasks.length;
                    this.completedPersonalOffset = this.all_completed_personal_tasks.length;
                    this.completedAssignedOffset = this.all_completed_assigned_tasks.length;
                    this.completedPersonalHasMore = this.completedPersonalOffset < this.completedPersonalTotal;
                    this.completedAssignedHasMore = this.completedAssignedOffset < this.completedAssignedTotal;

                    return [...this.all_completed_personal_tasks, ...this.all_completed_assigned_tasks];
                }
                return [];
            } catch (error) {
                console.error('Error loading completed tasks:', error);
                return [];
            } finally {
                this.isLoadingCompletedPersonalTasks = false;
                this.isLoadingCompletedAssignedTasks = false;
            }
        },
        async loadMoreCompletedPersonalTasks() {
            if (this.isLoadingCompletedPersonalTasks || !this.completedPersonalHasMore) return;
            const user = this.$store.getters["auth/get_user"];
            if (!user?.id) return;

            this.isLoadingCompletedPersonalTasks = true;
            try {
                const payload = {
                    user_id: user.id,
                    is_completed: 1,
                    scope: 'personal',
                    limit: this.COMPLETED_TASKS_PAGE_SIZE,
                    offset: this.completedPersonalOffset,
                };

                const response = await this.$axios.post('api/tasks/get_user_tasks', payload);

                if (response.data.response) {
                    const newTasks = response.data.personal_tasks || [];
                    this.all_completed_personal_tasks = [...this.all_completed_personal_tasks, ...newTasks];
                    this.completedPersonalTotal = response.data.personal_tasks_total ?? this.completedPersonalTotal;
                    this.completedPersonalOffset = this.all_completed_personal_tasks.length;
                    this.completedPersonalHasMore = this.completedPersonalOffset < this.completedPersonalTotal;
                }
            } catch (error) {
                console.error('Error loading more completed personal tasks:', error);
            } finally {
                this.isLoadingCompletedPersonalTasks = false;
            }
        },
        async loadMoreCompletedAssignedTasks() {
            if (this.isLoadingCompletedAssignedTasks || !this.completedAssignedHasMore) return;
            const user = this.$store.getters["auth/get_user"];
            if (!user?.id) return;

            this.isLoadingCompletedAssignedTasks = true;
            try {
                const payload = {
                    user_id: user.id,
                    is_completed: 1,
                    scope: 'assigned',
                    limit: this.COMPLETED_TASKS_PAGE_SIZE,
                    offset: this.completedAssignedOffset,
                };

                const response = await this.$axios.post('api/tasks/get_user_tasks', payload);

                if (response.data.response) {
                    const newTasks = response.data.assigned_tasks || [];
                    this.all_completed_assigned_tasks = [...this.all_completed_assigned_tasks, ...newTasks];
                    this.completedAssignedTotal = response.data.assigned_tasks_total ?? this.completedAssignedTotal;
                    this.completedAssignedOffset = this.all_completed_assigned_tasks.length;
                    this.completedAssignedHasMore = this.completedAssignedOffset < this.completedAssignedTotal;
                }
            } catch (error) {
                console.error('Error loading more completed assigned tasks:', error);
            } finally {
                this.isLoadingCompletedAssignedTasks = false;
            }
        },
        updateUser(updatedUser) {
            const index = this.users.findIndex(user => user.id === updatedUser.id);
            if (index !== -1) {
                this.$set(this.users, index, updatedUser);
            }
        },
        addUser(newUser) {
            this.users.push(newUser);
        },
        removeUser(userId) {
            const index = this.users.findIndex(user => user.id === userId);
            if (index !== -1) {
                this.users.splice(index, 1);
            }
        },
        // Task mutation methods - centralized in DefaultLayout
        addPersonalTask(task) {
            this.all_personal_tasks.unshift(task);
            
            // If task is unapproved (status_id = 8), also add to unapproved tasks array
            if (task.status_id === 8) {
                this.all_unapproved_tasks.unshift(task);
            }
            
            // Update user stats optimistically
            const currentStats = { ...this.$store.getters['auth/get_user_stats'] };

            // Increment appropriate status count based on task status
            if (task.status_id === 1) { // Todo
                currentStats.todo_tasks = (currentStats.todo_tasks || 0) + 1;
            } else if (task.status_id === 2) { // In Progress
                currentStats.in_progress_tasks = (currentStats.in_progress_tasks || 0) + 1;
            } else if (task.status_id === 3) { // In Review
                currentStats.in_review_tasks = (currentStats.in_review_tasks || 0) + 1;
            } else if (task.status_id === 4) { // Completed
                currentStats.completed_tasks = (currentStats.completed_tasks || 0) + 1;
            } else if (task.status_id === 5) { // Cancelled
                currentStats.cancelled_tasks = (currentStats.cancelled_tasks || 0) + 1;
            } else if (task.status_id === 6) { // Blocked
                currentStats.blocked_tasks = (currentStats.blocked_tasks || 0) + 1;
            }

            this.$store.dispatch('auth/set_user_stats', currentStats);
            // Update overview stats optimistically
            this.updateOverviewStatsForTaskCreation(task);
        },
        addAssignedTask(task) {
            this.all_assigned_tasks.unshift(task);
            
            // If task is unapproved (status_id = 8), also add to unapproved tasks array
            if (task.status_id === 8) {
                this.all_unapproved_tasks.unshift(task);
            }

            // Only update user stats if current user is included as an assignee
            const currentUser = this.$store.getters["auth/get_user"];
            const isUserAssigned = task.assignees && task.assignees.some(assignee =>
                assignee.user_id === currentUser.id ||
                (assignee.user && assignee.user.id === currentUser.id)
            );

            if (isUserAssigned) {
                // Update user stats optimistically
                const currentStats = { ...this.$store.getters['auth/get_user_stats'] };

                // Increment appropriate status count based on task status
                if (task.status_id === 1) { // Todo
                    currentStats.todo_tasks = (currentStats.todo_tasks || 0) + 1;
                } else if (task.status_id === 2) { // In Progress
                    currentStats.in_progress_tasks = (currentStats.in_progress_tasks || 0) + 1;
                } else if (task.status_id === 3) { // In Review
                    currentStats.in_review_tasks = (currentStats.in_review_tasks || 0) + 1;
                } else if (task.status_id === 4) { // Completed
                    currentStats.completed_tasks = (currentStats.completed_tasks || 0) + 1;
                } else if (task.status_id === 5) { // Cancelled
                    currentStats.cancelled_tasks = (currentStats.cancelled_tasks || 0) + 1;
                } else if (task.status_id === 6) { // Blocked
                    currentStats.blocked_tasks = (currentStats.blocked_tasks || 0) + 1;
                }

                this.$store.dispatch('auth/set_user_stats', currentStats);
            }

            // Update overview stats optimistically
            this.updateOverviewStatsForTaskCreation(task);
        },
        updateTaskInArrays(taskId, updatedData) {
            let updated = false;
            let oldTask = null;

            // Look across active + completed arrays (personal/assigned are mutually
            // exclusive by construction, as are active/completed for a given task).
            const locations = [
                { arr: this.all_personal_tasks, category: 'personal' },
                { arr: this.all_assigned_tasks, category: 'assigned' },
                { arr: this.all_completed_personal_tasks, category: 'personal' },
                { arr: this.all_completed_assigned_tasks, category: 'assigned' },
            ];
            let sourceArr = null;
            let sourceIndex = -1;
            let category = null;
            for (const loc of locations) {
                const idx = loc.arr.findIndex(task => task.id === taskId);
                if (idx !== -1) {
                    sourceArr = loc.arr;
                    sourceIndex = idx;
                    category = loc.category;
                    break;
                }
            }

            if (sourceArr) {
                oldTask = { ...sourceArr[sourceIndex] };
                const updatedTask = { ...sourceArr[sourceIndex], ...updatedData };

                const wasCompleted = oldTask.status_id === 4;
                const willBeCompleted = updatedData.status_id !== undefined
                    ? updatedData.status_id === 4
                    : wasCompleted;

                if (wasCompleted !== willBeCompleted) {
                    // Task is transitioning into/out of "completed" — move it between
                    // the active and completed arrays for its category.
                    sourceArr.splice(sourceIndex, 1);
                    if (willBeCompleted) {
                        const destArr = category === 'personal' ? this.all_completed_personal_tasks : this.all_completed_assigned_tasks;
                        destArr.unshift(updatedTask);
                        if (category === 'personal') {
                            this.completedPersonalTotal++;
                            this.completedPersonalOffset = this.all_completed_personal_tasks.length;
                        } else {
                            this.completedAssignedTotal++;
                            this.completedAssignedOffset = this.all_completed_assigned_tasks.length;
                        }
                    } else {
                        const destArr = category === 'personal' ? this.all_personal_tasks : this.all_assigned_tasks;
                        destArr.unshift(updatedTask);
                        if (category === 'personal') {
                            this.completedPersonalTotal = Math.max(0, this.completedPersonalTotal - 1);
                            this.completedPersonalOffset = this.all_completed_personal_tasks.length;
                        } else {
                            this.completedAssignedTotal = Math.max(0, this.completedAssignedTotal - 1);
                            this.completedAssignedOffset = this.all_completed_assigned_tasks.length;
                        }
                    }
                } else {
                    this.$set(sourceArr, sourceIndex, updatedTask);
                }
                updated = true;
            }

            // Handle unapproved tasks array
            const unapprovedTaskIndex = this.all_unapproved_tasks.findIndex(task => task.id === taskId);
            if (unapprovedTaskIndex !== -1) {
                if (!oldTask) oldTask = { ...this.all_unapproved_tasks[unapprovedTaskIndex] };
                
                // If status is changing from 8 to something else, remove from unapproved array
                if (updatedData.status_id && updatedData.status_id !== 8) {
                    const removedTask = { ...this.all_unapproved_tasks[unapprovedTaskIndex], ...updatedData };
                    this.all_unapproved_tasks.splice(unapprovedTaskIndex, 1);
                    // If rejected (status_id=9), move to rejected array
                    if (updatedData.status_id === 9) {
                        this.all_rejected_tasks.unshift(removedTask);
                    }
                } else {
                    // Otherwise update the task in the array
                    const updatedTask = { ...this.all_unapproved_tasks[unapprovedTaskIndex], ...updatedData };
                    this.$set(this.all_unapproved_tasks, unapprovedTaskIndex, updatedTask);
                }
                updated = true;
            }

            if (updated) {
                // Update user stats if status changed
                if (oldTask && updatedData.status_id && oldTask.status_id !== updatedData.status_id) {
                    const currentStats = { ...this.$store.getters['auth/get_user_stats'] };

                    // Remove from old status count
                    if (oldTask.status_id === 1) { // Todo
                        currentStats.todo_tasks = Math.max(0, (currentStats.todo_tasks || 0) - 1);
                    } else if (oldTask.status_id === 2) { // In Progress
                        currentStats.in_progress_tasks = Math.max(0, (currentStats.in_progress_tasks || 0) - 1);
                    } else if (oldTask.status_id === 3) { // In Review
                        currentStats.in_review_tasks = Math.max(0, (currentStats.in_review_tasks || 0) - 1);
                    } else if (oldTask.status_id === 4) { // Completed
                        currentStats.completed_tasks = Math.max(0, (currentStats.completed_tasks || 0) - 1);
                    } else if (oldTask.status_id === 5) { // Cancelled
                        currentStats.cancelled_tasks = Math.max(0, (currentStats.cancelled_tasks || 0) - 1);
                    } else if (oldTask.status_id === 6) { // Blocked
                        currentStats.blocked_tasks = Math.max(0, (currentStats.blocked_tasks || 0) - 1);
                    }

                    // Add to new status count
                    if (updatedData.status_id === 1) { // Todo
                        currentStats.todo_tasks = (currentStats.todo_tasks || 0) + 1;
                    } else if (updatedData.status_id === 2) { // In Progress
                        currentStats.in_progress_tasks = (currentStats.in_progress_tasks || 0) + 1;
                    } else if (updatedData.status_id === 3) { // In Review
                        currentStats.in_review_tasks = (currentStats.in_review_tasks || 0) + 1;
                    } else if (updatedData.status_id === 4) { // Completed
                        currentStats.completed_tasks = (currentStats.completed_tasks || 0) + 1;
                    } else if (updatedData.status_id === 5) { // Cancelled
                        currentStats.cancelled_tasks = (currentStats.cancelled_tasks || 0) + 1;
                    } else if (updatedData.status_id === 6) { // Blocked
                        currentStats.blocked_tasks = (currentStats.blocked_tasks || 0) + 1;
                    }

                    this.$store.dispatch('auth/set_user_stats', currentStats);
                }
                // Update overview stats optimistically
                if (oldTask) {
                    this.updateOverviewStatsForTaskUpdate(oldTask, updatedData);
                }
                // Also update overview tasks array if it exists
                const overviewTaskIndex = this.all_overview_tasks.findIndex(task => task.id === taskId);
                if (overviewTaskIndex !== -1) {
                    const updatedOverviewTask = { ...this.all_overview_tasks[overviewTaskIndex], ...updatedData };
                    this.$set(this.all_overview_tasks, overviewTaskIndex, updatedOverviewTask);
                }
            }
        },

        // Optimistic task mutation methods
        removeTaskFromArrays(taskId) {
            const locations = [
                { arr: this.all_personal_tasks, key: 'personal' },
                { arr: this.all_assigned_tasks, key: 'assigned' },
                { arr: this.all_completed_personal_tasks, key: 'completedPersonal' },
                { arr: this.all_completed_assigned_tasks, key: 'completedAssigned' },
            ];

            let removedTask = null;
            let location = null;
            for (const loc of locations) {
                const idx = loc.arr.findIndex(task => task.id === taskId);
                if (idx !== -1) {
                    removedTask = loc.arr.splice(idx, 1)[0];
                    location = loc.key;
                    break;
                }
            }

            if (removedTask) {
                // Stash where it came from so a failed API call can restore it
                // to the right array (active vs completed, personal vs assigned).
                removedTask = { ...removedTask, __removedFrom: location };

                if (location === 'completedPersonal') {
                    this.completedPersonalTotal = Math.max(0, this.completedPersonalTotal - 1);
                    this.completedPersonalOffset = this.all_completed_personal_tasks.length;
                } else if (location === 'completedAssigned') {
                    this.completedAssignedTotal = Math.max(0, this.completedAssignedTotal - 1);
                    this.completedAssignedOffset = this.all_completed_assigned_tasks.length;
                }
                // Update user stats optimistically
                const currentStats = { ...this.$store.getters['auth/get_user_stats'] };

                // Decrement appropriate status count based on removed task status
                if (removedTask.status_id === 1) { // Todo
                    currentStats.todo_tasks = Math.max(0, (currentStats.todo_tasks || 0) - 1);
                } else if (removedTask.status_id === 2) { // In Progress
                    currentStats.in_progress_tasks = Math.max(0, (currentStats.in_progress_tasks || 0) - 1);
                } else if (removedTask.status_id === 3) { // In Review
                    currentStats.in_review_tasks = Math.max(0, (currentStats.in_review_tasks || 0) - 1);
                } else if (removedTask.status_id === 4) { // Completed
                    currentStats.completed_tasks = Math.max(0, (currentStats.completed_tasks || 0) - 1);
                } else if (removedTask.status_id === 5) { // Cancelled
                    currentStats.cancelled_tasks = Math.max(0, (currentStats.cancelled_tasks || 0) - 1);
                } else if (removedTask.status_id === 6) { // Blocked
                    currentStats.blocked_tasks = Math.max(0, (currentStats.blocked_tasks || 0) - 1);
                }

                this.$store.dispatch('auth/set_user_stats', currentStats);
                // Update overview stats optimistically
                this.updateOverviewStatsForTaskDeletion(removedTask);
                // Also remove from overview tasks array
                const overviewTaskIndex = this.all_overview_tasks.findIndex(task => task.id === taskId);
                if (overviewTaskIndex !== -1) {
                    this.all_overview_tasks.splice(overviewTaskIndex, 1);
                }
            }

            return removedTask;
        },

        restoreTaskToArrays(task, location = 'personal') {
            // Accept the legacy boolean isPersonal signature too.
            if (location === true) location = 'personal';
            if (location === false) location = 'assigned';

            const { __removedFrom, ...cleanTask } = task;
            const targetLocation = location || __removedFrom || 'personal';

            switch (targetLocation) {
                case 'assigned':
                    this.all_assigned_tasks.unshift(cleanTask);
                    break;
                case 'completedPersonal':
                    this.all_completed_personal_tasks.unshift(cleanTask);
                    this.completedPersonalTotal++;
                    this.completedPersonalOffset = this.all_completed_personal_tasks.length;
                    break;
                case 'completedAssigned':
                    this.all_completed_assigned_tasks.unshift(cleanTask);
                    this.completedAssignedTotal++;
                    this.completedAssignedOffset = this.all_completed_assigned_tasks.length;
                    break;
                case 'personal':
                default:
                    this.all_personal_tasks.unshift(cleanTask);
            }
            // Note: Stats rollback is handled separately in optimisticDeleteTask
            // to avoid double-counting when both task and stats are restored
        },

        // Optimistic task operations that update UI immediately then sync with API
        async optimisticUpdateTask(taskId, updates, apiCall) {
            // Store original data for rollback
            const originalTask = this.findTaskInArrays(taskId);
            if (!originalTask) {
                return false;
            }

            const originalData = { ...originalTask };
            // Store original stats for rollback
            const originalStats = { ...this.$store.getters['auth/get_user_stats'] };
            const originalOverviewStats = { ...this.overview_stats };

            // Detect whether THIS update transitions the task into (or out of)
            // the "completed" state (status_id = 4). Any such transition on the
            // backend awards or rescinds growth-score task pts, so we must tell
            // the Employee Growth cards to refresh after the API call succeeds.
            const wasCompleted  = originalData.status_id === 4;
            const willBeCompleted = updates && updates.status_id !== undefined
                ? updates.status_id === 4
                : wasCompleted;
            const growthTransition = wasCompleted !== willBeCompleted;

            // Apply optimistic update immediately (this will also update stats)
            this.updateTaskInArrays(taskId, updates);

            try {
                // Make API call
                const result = await apiCall();

                // If the completion state changed, notify all Employee Growth
                // cards so the growth score, leaderboards, role-profile chip,
                // habit context, KPI context, etc. all re-fetch from the server.
                // Backend already awarded/removed pts inside the task-update
                // endpoint; this just forces the UI to pull the new numbers.
                if (growthTransition) {
                    this.$root.$emit('growth:refresh');
                }

                // Status changed — re-pull the Dashboard's "My Statistics" counts from
                // the server rather than maintaining optimistic math for them client-side.
                if (updates && updates.status_id !== undefined) {
                    this.getMyTaskStats();
                }

                return result;
            } catch (error) {
                // Rollback task data
                this.updateTaskInArrays(taskId, originalData);
                // Rollback stats
                this.$store.dispatch('auth/set_user_stats', originalStats);
                this.overview_stats = originalOverviewStats;
                console.error(`[optimisticUpdateTask] API call failed for task ${taskId}, rolled back task and stats:`, error);
                throw error;
            }
        },

        async optimisticDeleteTask(taskId, apiCall) {
            // Store original stats for rollback
            const originalStats = { ...this.$store.getters['auth/get_user_stats'] };
            const originalOverviewStats = { ...this.overview_stats };

            // Remove task optimistically (this will also update stats)
            const removedTask = this.removeTaskFromArrays(taskId);
            if (!removedTask) {
                return false;
            }

            try {
                // Make API call
                const result = await apiCall();
                this.getMyTaskStats();
                return result;
            } catch (error) {
                // Rollback task
                this.restoreTaskToArrays(removedTask, removedTask.__removedFrom);
                // Rollback stats
                this.$store.dispatch('auth/set_user_stats', originalStats);
                this.overview_stats = originalOverviewStats;
                console.error(`[optimisticDeleteTask] API call failed for task ${taskId}, rolled back task and stats:`, error);
                throw error;
            }
        },

        async optimisticCreateTask(taskData, isPersonal, apiCall) {
            // Generate temporary ID for optimistic update
            const tempId = `temp_${Date.now()}_${Math.random()}`;
            const currentUser = this.$store.getters['auth/get_user'];

            // Create optimistic task object
            const optimisticTask = {
                id: tempId,
                name: taskData.name,
                description: taskData.description || '',
                status_id: taskData.status_id || null,
                priority_id: taskData.priority_id || null,
                due_date: taskData.due_date || null,
                company_id: taskData.company_id || null,
                meeting_id: taskData.meeting_id || null,
                created_by: currentUser.id,
                creator: currentUser,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                assignees: (taskData.assignee_ids || []).map(id => {
                    const user = this.users.find(u => u.id === id);
                    return { user_id: id, user: user };
                })
            };

            // Store original stats for rollback
            const originalStats = { ...this.$store.getters['auth/get_user_stats'] };
            const originalOverviewStats = { ...this.overview_stats };

            // Add task optimistically
            if (isPersonal) {
                this.addPersonalTask(optimisticTask);
            } else {
                this.addAssignedTask(optimisticTask);
            }

            try {
                // Make API call
                const result = await apiCall();

                // Replace temp task with real task from API by updating in place
                if (result && result.data) {
                    const realTask = result.data;
                    // Find the temp task and update it with real data (including real ID)
                    const tempTask = this.findTaskInArrays(tempId);
                    if (tempTask) {
                        // Update the task in place with all properties from real task
                        Object.assign(tempTask, realTask);
                    } else {
                        console.warn(`[optimisticCreateTask] Could not find temp task ${tempId}, adding real task`);
                        // Fallback: add the real task if temp not found
                        if (isPersonal) {
                            this.addPersonalTask(realTask);
                        } else {
                            this.addAssignedTask(realTask);
                        }
                    }
                }

                this.getMyTaskStats();
                return result;
            } catch (error) {
                // Rollback - remove the optimistic task
                this.removeTaskFromArrays(tempId);
                // Rollback stats
                this.$store.dispatch('auth/set_user_stats', originalStats);
                this.overview_stats = originalOverviewStats;
                console.error('[optimisticCreateTask] API call failed, rolled back task and stats:', error);
                throw error;
            }
        },

        async optimisticBulkUpdateTasks(taskIds, updates, apiCall) {
            if (!taskIds || taskIds.length === 0 || !updates) {
                console.error('[DefaultLayout] Invalid bulk update parameters');
                return;
            }

            // Store original task data for rollback
            const originalTasksData = taskIds.map(taskId => {
                const task = this.findTaskInArrays(taskId);
                return task ? { ...task } : null;
            }).filter(Boolean);

            // Track whether any task's completion state changes, so we can
            // tell the Employee Growth cards to re-fetch once the API returns.
            const growthTransition = updates.status_id !== undefined
                && originalTasksData.some(t => (t.status_id === 4) !== (updates.status_id === 4));

            try {
                // Optimistic update: Update all tasks immediately
                taskIds.forEach(taskId => {
                    this.updateTaskInArrays(taskId, updates);
                });

                // Call the API
                const result = await apiCall();

                if (growthTransition) {
                    this.$root.$emit('growth:refresh');
                }

                if (updates.status_id !== undefined) {
                    this.getMyTaskStats();
                }

                return result;
            } catch (error) {
                console.error('[DefaultLayout] Bulk update API failed, rolling back:', error);

                // Rollback: Restore original data for all tasks
                originalTasksData.forEach(originalTask => {
                    if (originalTask && originalTask.id) {
                        this.updateTaskInArrays(originalTask.id, originalTask);
                    }
                });

                throw error;
            }
        },

        async optimisticBulkDeleteTasks(taskIds, apiCall) {
            if (!taskIds || taskIds.length === 0) {
                console.error('[DefaultLayout] Invalid bulk delete parameters');
                return;
            }

            // Store original task data and their locations for rollback
            const removedTasksData = taskIds.map(taskId => {
                const removedTask = this.removeTaskFromArrays(taskId);
                return removedTask ? { task: removedTask, location: removedTask.__removedFrom } : null;
            }).filter(Boolean);

            try {
                // Call the API
                const result = await apiCall();
                this.getMyTaskStats();
                return result;
            } catch (error) {
                console.error('[DefaultLayout] Bulk delete API failed, rolling back:', error);

                // Rollback: Restore all removed tasks
                removedTasksData.forEach(({ task, location }) => {
                    if (task) {
                        this.restoreTaskToArrays(task, location);
                    }
                });

                throw error;
            }
        },

        findTaskInArrays(taskId) {
            return this.all_personal_tasks.find(task => task.id === taskId) ||
                this.all_assigned_tasks.find(task => task.id === taskId) ||
                this.all_completed_personal_tasks.find(task => task.id === taskId) ||
                this.all_completed_assigned_tasks.find(task => task.id === taskId);
        },

        async getUser(id) {
            try {
                const response = await this.$axios.get(`/api/users/${id}`)
                const { data } = response
                this.$store.commit('auth/set_user', data)
            } catch (error) {
                console.error('error on fetching user information', error);
            }
        },
        async getUserTasksOverview(user_id = null) {
            try {
                // Use provided user_id or get current user
                const userId = user_id || this.$store.getters["auth/get_user"].id;
                const response = await this.$axios.get(`/api/tasks/get_user_tasks_overview/${userId}`)
                const { data } = response;

                // Store in Vuex if it's for the current user
                const currentUser = this.$store.getters["auth/get_user"];
                if (!user_id || user_id === currentUser.id) {
                    this.$store.dispatch('auth/set_user_stats', data);
                }

                return data;
            } catch (error) {
                console.error('error fetching user tasks overview', error)
                return {
                    total_tasks: 0,
                    in_progress_tasks: 0,
                    completed_tasks: 0,
                    overdue_tasks: 0,
                };
            }
        },
        // Scoped stats for the Dashboard's "My Statistics" card — assignee-only
        // (matches "My Task List"), computed server-side in a single aggregate
        // query. No optimistic math on the frontend: any action that can change
        // these counts (status change, create, delete) just re-calls this.
        async getMyTaskStats() {
            const user = this.$store.getters["auth/get_user"];
            if (!user?.id) return;

            this.isLoadingMyTaskStats = true;
            try {
                const response = await this.$axios.get(`/api/tasks/get_user_tasks_overview/${user.id}`, { scope: 'personal' });
                if (response.data.response) {
                    this.myTaskStats = {
                        todo_tasks: response.data.todo_tasks || 0,
                        in_progress_tasks: response.data.in_progress_tasks || 0,
                        completed_tasks: response.data.completed_tasks || 0,
                        overdue_tasks: response.data.overdue_tasks || 0,
                    };
                }
            } catch (error) {
                console.error('Error fetching my task stats:', error);
            } finally {
                this.isLoadingMyTaskStats = false;
            }
        },

        // ==================== INBOX/MESSAGING METHODS ====================

        /**
         * Get all conversations for the current user
         */
        async getConversations() {
            try {
                const response = await this.$axios.get('api/conversations/get_conversations')

                if (response.data && response.data.conversations) {
                    this.conversations = response.data.conversations
                    this.seedReadReceipts(response.data.conversations)

                    // Calculate total unread count
                    this.unreadMessagesCount = this.conversations.reduce(
                        (total, conv) => total + (conv.unread_count || 0),
                        0
                    )

                    // console.log(`[getConversations] Loaded ${this.conversations.length} conversations, ${this.unreadMessagesCount} unread messages`);
                }
            } catch (error) {
                console.error('[DefaultLayout] Failed to load conversations:', error)
            }
        },

        /**
         * Load the most recent page of messages for a conversation.
         */
        async getConversationMessages(conversationId) {
            if (!conversationId) return

            try {
                const response = await this.$axios.get(`api/messages/get_messages/${conversationId}`)

                if (response.data && response.data.messages) {
                    // Store messages in object keyed by conversation_id
                    this.$set(this.conversationMessages, conversationId, response.data.messages)
                    this.$set(this.messagesHasMore, conversationId, !!response.data.has_more)
                    this.mergeLinkedTasks(response.data.messages)
                }
            } catch (error) {
                console.error('[DefaultLayout] Failed to load messages:', error)
            }
        },

        /**
         * Prepend the page of messages preceding the oldest one we hold.
         *
         * Returns the id of the previously-oldest message so the caller can restore
         * the scroll position against it after the list grows upward.
         */
        async loadOlderMessages(conversationId) {
            if (!conversationId) return null;
            if (this.loadingOlderMessages[conversationId]) return null;
            if (this.messagesHasMore[conversationId] === false) return null;

            const existing = this.conversationMessages[conversationId] || [];
            const oldest = existing[0];
            if (!oldest) return null;

            this.$set(this.loadingOlderMessages, conversationId, true);

            try {
                const response = await this.$axios.get(
                    `api/messages/get_messages/${conversationId}`,
                    { params: { before_id: oldest.id } }
                );

                if (response.data && response.data.messages) {
                    this.$set(this.conversationMessages, conversationId, [
                        ...response.data.messages,
                        ...existing,
                    ]);
                    this.$set(this.messagesHasMore, conversationId, !!response.data.has_more);
                    this.mergeLinkedTasks(response.data.messages);
                }

                return oldest.id;
            } catch (error) {
                console.error('[DefaultLayout] Failed to load older messages:', error);
                return null;
            } finally {
                this.$set(this.loadingOlderMessages, conversationId, false);
            }
        },

        /**
         * Load a window of messages centred on one message, replacing whatever is
         * loaded. Used to jump to a pinned message that is not on the current page.
         *
         * The list becomes "detached" from the live bottom unless the window
         * already reaches the newest message.
         */
        async loadMessagesAround(conversationId, messageId) {
            if (!conversationId || !messageId) return;

            const response = await this.$axios.get(
                `api/messages/get_messages/${conversationId}`,
                { params: { around_id: messageId } }
            );

            if (!response.data || !response.data.messages) {
                throw new Error(response.data?.message || 'Could not load messages');
            }

            this.$set(this.conversationMessages, conversationId, response.data.messages);
            this.$set(this.messagesHasMore, conversationId, !!response.data.has_older);
            this.$set(this.messagesHasNewer, conversationId, !!response.data.has_newer);
            this.$set(this.conversationDetached, conversationId, !!response.data.has_newer);
            this.mergeLinkedTasks(response.data.messages);
        },

        /**
         * Append the next page of newer messages. How a detached view scrolls back
         * down toward the present; when nothing newer remains, it reconnects.
         */
        async loadNewerMessages(conversationId) {
            if (!conversationId) return;
            if (this.messagesHasNewer[conversationId] === false) return;

            const existing = this.conversationMessages[conversationId] || [];
            const newest = existing[existing.length - 1];
            if (!newest) return;

            const response = await this.$axios.get(
                `api/messages/get_messages/${conversationId}`,
                { params: { after_id: newest.id } }
            );

            if (response.data && response.data.messages) {
                const merged = [...existing, ...response.data.messages];
                this.$set(this.conversationMessages, conversationId, merged);
                this.$set(this.messagesHasNewer, conversationId, !!response.data.has_newer);
                // Reaching the newest message reconnects the view to the present.
                if (!response.data.has_newer) {
                    this.$set(this.conversationDetached, conversationId, false);
                }
                this.mergeLinkedTasks(response.data.messages);
            }
        },

        /**
         * Reload the newest page and reconnect to the live conversation.
         */
        async jumpToLatest(conversationId) {
            if (!conversationId) return;

            await this.getConversationMessages(conversationId);
            this.$set(this.messagesHasNewer, conversationId, false);
            this.$set(this.conversationDetached, conversationId, false);
        },

        hasNewerMessages(conversationId) {
            return this.messagesHasNewer[conversationId] === true;
        },

        isConversationDetached(conversationId) {
            return this.conversationDetached[conversationId] === true;
        },

        /**
         * Get messages by conversation ID (for provide/inject)
         */
        getMessagesByConversation(conversationId) {
            return this.conversationMessages[conversationId] || []
        },

        /**
         * Set the currently active conversation
         */
        setActiveConversation(conversationId) {
            this.activeConversationId = conversationId;
        },

        /**
         * Optimistically send a message
         */
        async optimisticSendMessage(messageData, apiCall) {
            if (!messageData || !messageData.conversation_id) {
                throw new Error('Invalid message data')
            }

            const conversationId = messageData.conversation_id
            const tempId = `temp_${Date.now()}_${Math.random()}`

            // Create temporary message with sender info
            const currentUser = this.get_user;
            const tempMessage = {
                id: tempId,
                conversation_id: conversationId,
                sender_id: messageData.sender_id,
                message: messageData.message,
                created_at: new Date().toISOString(),
                is_read: 0,
                sender: {
                    id: currentUser.id,
                    name: currentUser.name,
                    profile_picture: currentUser.profile_picture
                }
            }

            // Initialize conversation messages array if needed
            if (!this.conversationMessages[conversationId]) {
                this.$set(this.conversationMessages, conversationId, [])
            }

            // Optimistic update: Add message to UI immediately with reactivity
            const messages = this.conversationMessages[conversationId];
            messages.push(tempMessage);
            this.$set(this.conversationMessages, conversationId, [...messages]); // Force reactivity

            // Update conversation's last message optimistically
            const conversationIndex = this.conversations.findIndex(c => c.id === conversationId);
            if (conversationIndex !== -1) {
                const conversation = this.conversations[conversationIndex];
                this.$set(conversation, 'last_message', messageData.message);
                this.$set(conversation, 'last_message_at', tempMessage.created_at);

                // Re-sort conversations optimistically
                const sortedConversations = [...this.conversations].sort((a, b) => {
                    const dateA = new Date(a.last_message_at || 0);
                    const dateB = new Date(b.last_message_at || 0);
                    return dateB - dateA;
                });
                this.conversations.splice(0, this.conversations.length, ...sortedConversations);
            }

            try {
                // Make API call
                const result = await apiCall(messageData)

                if (result && result.data) {
                    // Replace temp message with real one from server
                    const messagesArray = this.conversationMessages[conversationId];
                    const tempIndex = messagesArray.findIndex(m => m.id === tempId);

                    if (tempIndex !== -1) {
                        // Replace temp message with real message from server
                        this.$set(messagesArray, tempIndex, result.data);
                        this.$set(this.conversationMessages, conversationId, [...messagesArray]);
                    }

                    // Update conversation with real data if provided
                    if (conversationIndex !== -1 && result.conversation) {
                        const conversation = this.conversations[conversationIndex];
                        Object.keys(result.conversation).forEach(key => {
                            this.$set(conversation, key, result.conversation[key]);
                        });
                    }
                }

                return result;
            } catch (error) {
                console.error('[DefaultLayout] ❌ Send message failed, rolling back:', error);

                // Rollback: Remove temp message
                const messagesArray = this.conversationMessages[conversationId];
                const tempIndex = messagesArray.findIndex(m => m.id === tempId);

                if (tempIndex !== -1) {
                    messagesArray.splice(tempIndex, 1);
                    this.$set(this.conversationMessages, conversationId, [...messagesArray]);
                }

                throw error;
            }
        },

        /**
         * Send a message carrying file attachments.
         *
         * Attachments are shown optimistically via object URLs so the image appears
         * immediately; those URLs are revoked once the server response replaces them.
         */
        /**
         * Who the AI assistant is, so it can be offered in the mention menu.
         *
         * Best-effort and cached for the session: a 404 just means the feature
         * is off, and the chat works exactly as before without it.
         */
        async loadAssistant() {
            try {
                const response = await this.$axios.get('api/ai/assistant');

                if (response.data && response.data.response) {
                    this.assistantUser = response.data.data;
                }
            } catch (error) {
                this.assistantUser = null;
            }
        },

        /**
         * Make the assistant answer a message that mentioned it.
         *
         * Called after the message is safely sent, never as part of sending it -
         * a message must not fail, or wait on the AI, to be delivered.
         */
        async assistantReply(conversationId, messageId) {
            const response = await this.$axios.post(`api/conversations/${conversationId}/assistant-reply`, {
                message_id: messageId,
            });

            if (!response.data || !response.data.response) {
                throw new Error(response.data?.message || 'The assistant could not reply');
            }

            // Also arrives over the wire; appendThreadReply dedupes by id.
            this.appendThreadReply(conversationId, response.data.data);

            return response.data.data;
        },

        /**
         * Summarise one thread.
         */
        async summarizeThread(rootId) {
            const response = await this.$axios.post(`api/messages/thread/${rootId}/summarize`);

            if (!response.data || !response.data.response) {
                throw new Error(response.data?.message || 'Could not summarise the thread');
            }

            return response.data.data;
        },

        /**
         * This user's own AI notes about a conversation. Private to them.
         */
        async loadConversationContext(conversationId) {
            const response = await this.$axios.get(`api/conversations/${conversationId}/ai-context`);

            if (!response.data || !response.data.response) {
                throw new Error(response.data?.message || 'Could not load your AI context');
            }

            return response.data.data;
        },

        async saveConversationContext(conversationId, fields) {
            const response = await this.$axios.post(`api/conversations/${conversationId}/ai-context`, fields);

            if (!response.data || !response.data.response) {
                throw new Error(response.data?.message || 'Could not save your AI context');
            }

            return response.data.data;
        },

        /**
         * Answer a question from the user's own chat history.
         */
        async askChat(query, conversationId = null) {
            const payload = conversationId ? { query, conversation_id: conversationId } : { query };
            const response = await this.$axios.post('api/ai/search', payload);

            if (!response.data || !response.data.response) {
                throw new Error(response.data?.message || 'Could not search your chats');
            }

            return response.data.data;
        },

        /**
         * Short replies the user might send next. Nothing is sent.
         */
        async suggestReplies(conversationId, threadRootId = null) {
            const payload = threadRootId ? { thread_root_id: threadRootId } : {};
            const response = await this.$axios.post(`api/conversations/${conversationId}/suggest-replies`, payload);

            if (!response.data || !response.data.response) {
                throw new Error(response.data?.message || 'Could not suggest replies');
            }

            return response.data.data;
        },

        /**
         * Remove one of your own messages.
         *
         * Optimistic: it disappears at once and comes back if the server refuses,
         * which it will for a message that is too old, not yours, or has replies.
         */
        async deleteMessage(conversationId, message) {
            const removed = this.removeMessageLocally(conversationId, message);

            try {
                const response = await this.$axios.delete(`api/messages/delete_message/${message.id}`);

                if (!response.data || !response.data.response) {
                    throw new Error(response.data?.message || 'Could not delete the message');
                }

                return response.data;
            } catch (error) {
                // Put it back exactly where it was.
                if (removed) this.restoreMessageLocally(conversationId, message, removed.index);

                // Unlike post(), the axios plugin rejects delete() with the raw
                // response, so the server's reason - too old, not yours, has
                // replies - is under .data.message rather than .message.
                const reason = error?.data?.message
                    || error?.response?.data?.message
                    || error?.message;

                throw new Error(reason || 'Could not delete the message');
            }
        },

        /**
         * Drop a message from whichever list holds it, and correct the thread
         * summary if it was a reply.
         *
         * @return {?{index: number}} where it was, so a failed delete can undo.
         */
        removeMessageLocally(conversationId, message) {
            const parentId = message.parent_id || null;
            const store = parentId ? this.threadMessages : this.conversationMessages;
            const key = parentId || conversationId;
            const list = store[key];

            if (!list) return null;

            const index = list.findIndex(m => m.id === message.id);
            if (index === -1) return null;

            list.splice(index, 1);
            this.$set(store, key, [...list]);

            if (parentId) this.unbumpThreadSummary(conversationId, parentId);

            return { index };
        },

        restoreMessageLocally(conversationId, message, index) {
            const parentId = message.parent_id || null;
            const store = parentId ? this.threadMessages : this.conversationMessages;
            const key = parentId || conversationId;
            const list = store[key];

            if (!list) return;

            list.splice(index, 0, message);
            this.$set(store, key, [...list]);

            if (parentId) {
                this.bumpThreadSummary(conversationId, parentId, message.sender, message.created_at);
            }
        },

        /**
         * Fetch a thread. Passing a reply's id opens the thread it belongs to.
         */
        async loadThread(messageId) {
            const response = await this.$axios.get(`api/messages/thread/${messageId}`);

            if (!response.data || !response.data.response) {
                throw new Error(response.data?.message || 'Could not load the thread');
            }

            const { root, replies } = response.data.data;

            this.$set(this.threadMessages, root.id, replies);
            this.mergeLinkedTasks([root, ...replies]);

            return root;
        },

        closeThread(rootId) {
            this.$delete(this.threadMessages, rootId);
        },

        /**
         * Inject a view-only PMT Assistant message into a thread, so the PMT
         * preview replies in a thread exactly like the DG Assistant. Nothing is
         * persisted — this is static demo data. Returns the message so the caller
         * can flip its `pmt_reply` from a "thinking" state to the answer.
         */
        pmtInjectThreadReply(conversationId, rootId, { pmtReply = null, pmtCard = null, text = '', sender = null } = {}) {
            if (!this.threadMessages[rootId]) {
                this.$set(this.threadMessages, rootId, []);
            }

            // A plain `text`/`sender` posts a message from that person (used to echo
            // a suggested question); otherwise it is a PMT Assistant reply/card.
            const author = sender || { id: -777, name: 'PMT Assistant', profile_picture: null };

            const message = {
                id: `pmt-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
                parent_id: rootId,
                conversation_id: conversationId,
                sender_id: author.id,
                sender: author,
                message: text,
                created_at: new Date().toISOString(),
                attachments: [], mentions: [], reactions: [], task_links: [],
                pinned: false, sending: false, template_response: null, thread: null, voice_note: null,
                pmt_reply: pmtReply,
                pmt_card: pmtCard,
            };

            this.appendThreadReply(conversationId, message);
            return message;
        },

        /**
         * Add a reply that arrived over the wire to its thread, if we hold it,
         * and update the root's summary either way.
         */
        appendThreadReply(conversationId, reply) {
            const rootId = reply.parent_id;
            const existing = this.threadMessages[rootId];

            if (existing) {
                const idx = existing.findIndex(m => m.id === reply.id);
                if (idx !== -1) {
                    // Already present (the broadcast usually beats the HTTP reply).
                    // The broadcast copy omits export tables — they ride only on the
                    // ask() response — so upgrade the stored message when this copy
                    // carries tables the other lacks. Then stop: it was already counted.
                    const current = existing[idx];
                    const incomingTables = reply.pmt_payload && reply.pmt_payload.tables;
                    const hasTables = current.pmt_payload && current.pmt_payload.tables;
                    if (incomingTables && incomingTables.length && !hasTables) {
                        this.$set(existing, idx, {
                            ...current,
                            pmt_payload: { ...current.pmt_payload, ...reply.pmt_payload },
                        });
                        this.$set(this.threadMessages, rootId, [...existing]);
                    }
                    return;
                }

                existing.push(reply);
                this.$set(this.threadMessages, rootId, [...existing]);
            }

            this.bumpThreadSummary(conversationId, rootId, reply.sender, reply.created_at);
        },

        /**
         * Keep the "N replies" strip under a root message in step with its thread.
         *
         * The server sends an authoritative summary with every page load; this is
         * only to keep the strip honest between those loads.
         */
        bumpThreadSummary(conversationId, rootId, sender, at) {
            const messages = this.conversationMessages[conversationId];
            if (!messages) return;

            const root = messages.find(m => m.id === rootId);
            if (!root) return;

            const current = root.thread || { reply_count: 0, participants: [] };
            const participants = [...current.participants];

            if (sender && !participants.some(p => p.id === sender.id)) {
                participants.push({
                    id: sender.id,
                    name: sender.name,
                    profile_picture: sender.profile_picture || null,
                });
            }

            this.$set(root, 'thread', {
                reply_count: current.reply_count + 1,
                last_reply_at: at,
                participants: participants.slice(0, 3),
            });
        },

        /**
         * Undo an optimistic bump when the reply never made it.
         */
        unbumpThreadSummary(conversationId, rootId) {
            const messages = this.conversationMessages[conversationId];
            if (!messages) return;

            const root = messages.find(m => m.id === rootId);
            if (!root || !root.thread) return;

            const count = root.thread.reply_count - 1;

            // The last reply failing leaves the message with no thread at all.
            this.$set(root, 'thread', count > 0 ? { ...root.thread, reply_count: count } : null);
        },

        async sendMessageWithAttachments({ conversation_id: conversationId, parent_id: parentId, message, files, tasks, mentions, template, voiceNote }, onProgress) {
            if (!conversationId) throw new Error('Invalid message data');
            const taskIds = (tasks || []).map(t => t.id);
            if (!message && (!files || files.length === 0) && taskIds.length === 0 && !template && !voiceNote) {
                throw new Error('A message must have text, an attachment, a linked task, or a template');
            }

            // You cannot post into a gap. Returning to the live conversation is the
            // intent of sending, so reconnect first. A thread reply does not touch
            // the main-list detachment, so it is excluded.
            if (!parentId && this.isConversationDetached(conversationId)) {
                await this.jumpToLatest(conversationId);
            }

            const tempId = `temp_${Date.now()}_${Math.random()}`;
            const currentUser = this.get_user;
            const previewUrls = [];

            const tempMessage = {
                id: tempId,
                conversation_id: conversationId,
                parent_id: parentId || null,
                sender_id: currentUser.id,
                message: message || '',
                created_at: new Date().toISOString(),
                is_read: 0,
                sending: true,
                sender: {
                    id: currentUser.id,
                    name: currentUser.name,
                    profile_picture: currentUser.profile_picture,
                },
                reactions: [],
                // Echoed back so the bubble highlights the names straight away.
                // The server still decides which mentions were real.
                mentions: (mentions || []).map(m => ({ id: m.id, name: m.name })),
                // The picker already holds the full summary, so the card renders
                // immediately rather than waiting for the server round-trip.
                task_links: (tasks || []).map(task => ({ task_id: task.id, task })),
                // Rendered optimistically from the picker's own copy of the
                // fields, so the card appears before the server round-trip.
                template_response: template ? {
                    id: `temp_tpl_${tempId}`,
                    chat_template_id: template.id,
                    template_name: template.name,
                    fields_snapshot: template.fields,
                    answers: template.answers,
                } : null,
                attachments: (files || []).map((file, i) => {
                    const isImage = file.type.startsWith('image/');
                    const url = isImage ? URL.createObjectURL(file) : null;
                    if (url) previewUrls.push(url);
                    return {
                        id: `temp_att_${i}`,
                        file_name: file.name,
                        mime_type: file.type,
                        file_size: file.size,
                        url,
                        is_image: isImage,
                    };
                }),
                voice_note: voiceNote ? {
                    id: `temp_vn_${tempId}`,
                    url: (() => { const u = URL.createObjectURL(voiceNote.file); previewUrls.push(u); return u; })(),
                    mime_type: voiceNote.file.type,
                    duration_ms: voiceNote.durationMs,
                    peaks: voiceNote.peaks,
                    transcript: null,
                    transcript_status: 'pending',
                } : null,
            };

            this.mergeLinkedTasks([tempMessage]);

            // A reply is rendered in its thread, never in the main flow.
            const store = parentId ? this.threadMessages : this.conversationMessages;
            const key = parentId || conversationId;

            if (!store[key]) {
                this.$set(store, key, []);
            }
            const messages = store[key];
            messages.push(tempMessage);
            this.$set(store, key, [...messages]);

            if (parentId) {
                this.bumpThreadSummary(conversationId, parentId, currentUser, tempMessage.created_at);
            }

            // Optimistically bump the sidebar entry.
            const conversationIndex = this.conversations.findIndex(c => c.id === conversationId);
            if (conversationIndex !== -1) {
                const conversation = this.conversations[conversationIndex];
                this.$set(conversation, 'last_message', this.messagePreview(tempMessage));
                this.$set(conversation, 'last_message_at', tempMessage.created_at);
                this.moveConversationToTop(conversationIndex);
            }

            try {
                const formData = new FormData();
                formData.append('conversation_id', conversationId);
                if (message) formData.append('message', message);
                (files || []).forEach(file => formData.append('attachments[]', file));
                taskIds.forEach(id => formData.append('task_ids[]', id));
                (mentions || []).forEach(m => formData.append('mentioned_user_ids[]', m.id));
                if (parentId) formData.append('parent_id', parentId);
                if (voiceNote) {
                    formData.append('voice_note', voiceNote.file);
                    formData.append('voice_duration_ms', voiceNote.durationMs);
                    voiceNote.peaks.forEach(p => formData.append('voice_peaks[]', p));
                }

                if (template) {
                    formData.append('template_id', template.id);
                    Object.entries(template.answers).forEach(([key, value]) => {
                        formData.append(`template_answers[${key}]`, value);
                    });
                }

                const response = await this.$axios.post('api/messages/send_message', formData, {
                    // Required: without it the plugin defaults to application/json,
                    // and axios then serialises the FormData to JSON - turning every
                    // File into an empty object.
                    headers: { 'Content-Type': 'multipart/form-data' },
                    onUploadProgress: (event) => {
                        if (onProgress && event.total) {
                            onProgress(Math.round((event.loaded * 100) / event.total));
                        }
                    },
                });

                if (!response.data || !response.data.response) {
                    throw new Error(response.data?.message || 'Failed to send message');
                }

                const messagesArray = store[key];
                const tempIndex = messagesArray.findIndex(m => m.id === tempId);
                if (tempIndex !== -1) {
                    this.$set(messagesArray, tempIndex, response.data.data);
                    this.$set(store, key, [...messagesArray]);
                }

                // The server decides which tasks were actually linkable.
                this.mergeLinkedTasks([response.data.data]);

                return response.data;
            } catch (error) {
                console.error('[DefaultLayout] ❌ Send message with attachments failed, rolling back:', error);

                const messagesArray = store[key];
                const tempIndex = messagesArray.findIndex(m => m.id === tempId);
                if (tempIndex !== -1) {
                    messagesArray.splice(tempIndex, 1);
                    this.$set(store, key, [...messagesArray]);
                }

                if (parentId) this.unbumpThreadSummary(conversationId, parentId);

                throw error;
            } finally {
                previewUrls.forEach(url => URL.revokeObjectURL(url));
            }
        },

        // Patch a voice note in place wherever the message currently lives —
        // the main flow or an open thread — since a transcript can land for
        // either. Reactivity via $set on the containing array.
        patchVoiceNote(messageId, changes) {
            const apply = (store) => {
                Object.keys(store).forEach((key) => {
                    const arr = store[key];
                    if (!Array.isArray(arr)) return;
                    const idx = arr.findIndex(m => m && m.id === messageId && m.voice_note);
                    if (idx !== -1) {
                        this.$set(arr[idx], 'voice_note', { ...arr[idx].voice_note, ...changes });
                        this.$set(store, key, [...arr]);
                    }
                });
            };
            apply(this.conversationMessages);
            apply(this.threadMessages);
        },
        async retryTranscription(messageId) {
            try {
                await this.$axios.post(`api/messages/voice-notes/${messageId}/retranscribe`);
                this.patchVoiceNote(messageId, { transcript_status: 'pending' });
            } catch (e) {
                this.$toast.error('Could not restart transcription');
            }
        },

        /**
         * Toggle an emoji reaction on a message, updating the UI before the API confirms.
         */
        async optimisticToggleReaction(conversationId, messageId, emoji) {
            const messages = this.conversationMessages[conversationId];
            if (!messages) return;

            const index = messages.findIndex(m => m.id === messageId);
            if (index === -1) return;

            const message = messages[index];
            const original = JSON.parse(JSON.stringify(message.reactions || []));
            const userId = this.get_user.id;

            // Apply the toggle locally.
            const reactions = JSON.parse(JSON.stringify(original));
            const group = reactions.find(r => r.emoji === emoji);

            if (group && group.user_ids.includes(userId)) {
                group.user_ids = group.user_ids.filter(id => id !== userId);
                group.count -= 1;
                if (group.count <= 0) {
                    reactions.splice(reactions.indexOf(group), 1);
                }
            } else if (group) {
                group.user_ids.push(userId);
                group.count += 1;
            } else {
                reactions.push({ emoji, count: 1, user_ids: [userId] });
            }

            this.$set(messages[index], 'reactions', reactions);

            try {
                const response = await this.$axios.post(`api/messages/react/${messageId}`, { emoji });

                if (!response.data || !response.data.response) {
                    throw new Error(response.data?.message || 'Failed to react');
                }

                // Trust the server's grouping over our local guess.
                this.applyReaction(conversationId, messageId, response.data.data.reactions);
            } catch (error) {
                console.error('[DefaultLayout] ❌ Reaction failed, rolling back:', error);
                this.$set(messages[index], 'reactions', original);
                this.$toast.error('Could not save reaction');
            }
        },

        /** The loaded copy of a message, or null when its page is not held. */
        findMessageInConversation(conversationId, messageId) {
            const messages = this.conversationMessages[conversationId];

            return messages ? messages.find(m => m.id === messageId) || null : null;
        },

        /**
         * Pin a message for everybody in the conversation.
         *
         * Optimistic: the indicator appears at once and reverts if the API refuses,
         * which is what happens at the 50-pin cap.
         */
        async pinMessage(conversationId, messageId) {
            const message = this.findMessageInConversation(conversationId, messageId);
            const original = message ? message.pinned : null;

            if (message) {
                this.$set(message, 'pinned', {
                    pinned_at: new Date().toISOString(),
                    pinned_by: { id: this.get_user.id, name: this.get_user.name },
                });
            }

            try {
                const response = await this.$axios.post(`api/messages/pin/${messageId}`);

                if (!response.data || !response.data.response) {
                    throw new Error(response.data?.message || 'Could not pin the message');
                }

                if (message) {
                    this.$set(message, 'pinned', {
                        pinned_at: response.data.data.pinned_at,
                        pinned_by: response.data.data.pinned_by,
                    });
                }

                return response.data.data;
            } catch (error) {
                if (message) this.$set(message, 'pinned', original);
                throw error;
            }
        },

        async unpinMessage(conversationId, messageId) {
            const message = this.findMessageInConversation(conversationId, messageId);
            const original = message ? message.pinned : null;

            if (message) this.$set(message, 'pinned', null);

            try {
                const response = await this.$axios.delete(`api/messages/pin/${messageId}`);

                if (!response.data || !response.data.response) {
                    throw new Error(response.data?.message || 'Could not unpin the message');
                }
            } catch (error) {
                if (message) this.$set(message, 'pinned', original);
                throw error;
            }
        },

        async loadBoard(conversationId) {
            const response = await this.$axios.get(`api/conversations/${conversationId}/board`);

            if (!response.data || !response.data.response) {
                throw new Error(response.data?.message || 'Could not load the board');
            }

            return response.data.data;
        },

        async loadSharedItems(conversationId) {
            const response = await this.$axios.get(`api/conversations/${conversationId}/shared`);

            if (!response.data || !response.data.response) {
                throw new Error(response.data?.message || 'Could not load shared items');
            }

            return response.data.data;
        },

        /**
         * Subscribe to a conversation's presence channel to send and receive typing events.
         *
         * Typing is carried by Pusher client events, which require "Client Events" to be
         * enabled on the Pusher app. When it is off, subscribing still succeeds and typing
         * simply never fires - no errors, no broken chat.
         */
        watchConversationPresence(conversationId) {
            if (!this.$echo || !conversationId) return;

            this.unwatchConversationPresence();

            try {
                const channel = this.$echo.join(`conversation.${conversationId}`);

                channel.listenForWhisper('typing', (payload) => {
                    if (!payload || payload.user_id === this.get_user.id) return;
                    this.markTypingUser(conversationId, payload.user_id, payload.name);
                });

                channel.error((error) => {
                    console.error('[DefaultLayout] ❌ Error joining conversation presence:', error);
                });

                this.activePresenceChannel = { id: conversationId, channel };
            } catch (error) {
                console.error('[DefaultLayout] Failed to join conversation presence:', error);
            }
        },

        unwatchConversationPresence() {
            if (!this.activePresenceChannel) return;

            const { id } = this.activePresenceChannel;
            try {
                this.$echo.leave(`conversation.${id}`);
            } catch (error) {
                console.error('[DefaultLayout] Failed to leave conversation presence:', error);
            }

            this.$set(this.typingUsers, id, []);
            this.activePresenceChannel = null;
        },

        /**
         * Whisper a typing event, throttled so a burst of keystrokes sends at most
         * one event every 2 seconds.
         */
        emitTyping(conversationId) {
            if (!this.activePresenceChannel || this.activePresenceChannel.id !== conversationId) return;

            const now = Date.now();
            if (now - this.lastTypingEmittedAt < 2000) return;
            this.lastTypingEmittedAt = now;

            try {
                this.activePresenceChannel.channel.whisper('typing', {
                    user_id: this.get_user.id,
                    name: this.get_user.name,
                });
            } catch (error) {
                // Client events disabled on the Pusher app: degrade silently.
            }
        },

        /**
         * Record that a user is typing, and schedule its expiry. Each new typing event
         * from that user resets the timer, so the indicator disappears ~3s after they stop.
         */
        markTypingUser(conversationId, userId, name) {
            const current = this.typingUsers[conversationId] || [];
            if (!current.some(u => u.user_id === userId)) {
                this.$set(this.typingUsers, conversationId, [...current, { user_id: userId, name }]);
            }

            const key = `${conversationId}:${userId}`;
            if (this.typingTimers[key]) {
                clearTimeout(this.typingTimers[key]);
            }
            this.typingTimers[key] = setTimeout(() => {
                this.clearTypingUser(conversationId, userId);
            }, 3000);
        },

        clearTypingUser(conversationId, userId) {
            const current = this.typingUsers[conversationId];
            if (!current || !current.length) return;

            this.$set(this.typingUsers, conversationId, current.filter(u => u.user_id !== userId));

            const key = `${conversationId}:${userId}`;
            if (this.typingTimers[key]) {
                clearTimeout(this.typingTimers[key]);
                delete this.typingTimers[key];
            }
        },

        /**
         * Mark a conversation as read
         */
        async markConversationAsRead(conversationId) {
            if (!conversationId) return

            const conversationIndex = this.conversations.findIndex(c => c.id === conversationId);
            if (conversationIndex === -1) {
                console.warn('[DefaultLayout] Conversation not found:', conversationId);
                return;
            }

            const conversation = this.conversations[conversationIndex];
            if (conversation.unread_count === 0) {
                return;
            }

            // Store original for rollback
            const originalUnreadCount = conversation.unread_count;
            const originalTotalUnread = this.unreadMessagesCount;

            // Optimistic update with Vue reactivity
            this.$set(conversation, 'unread_count', 0);
            this.unreadMessagesCount = Math.max(0, this.unreadMessagesCount - originalUnreadCount);

            // Mark all messages in this conversation as read optimistically
            const messages = this.conversationMessages[conversationId];
            if (messages) {
                const updatedMessages = messages.map(msg => {
                    if (msg.sender_id !== this.get_user.id && msg.is_read === 0) {
                        return { ...msg, is_read: 1 };
                    }
                    return msg;
                });
                this.$set(this.conversationMessages, conversationId, updatedMessages);
            }

            try {
                await this.$axios.post(`api/conversations/mark_as_read/${conversationId}`);
            } catch (error) {
                console.error('[DefaultLayout] ❌ Failed to mark conversation as read, rolling back:', error);

                // Rollback with Vue reactivity
                this.$set(conversation, 'unread_count', originalUnreadCount);
                this.unreadMessagesCount = originalTotalUnread;

                // Rollback messages
                if (messages) {
                    const rolledBackMessages = messages.map(msg => {
                        if (msg.sender_id !== this.get_user.id) {
                            return { ...msg, is_read: 0 };
                        }
                        return msg;
                    });
                    this.$set(this.conversationMessages, conversationId, rolledBackMessages);
                }
            }
        },

        /**
         * Handle incoming message from Pusher (called from setupChatListener or inbox component)
         */
        handleIncomingMessage(messageData) {
            // Refresh conversations to update unread counts and last message
            this.getConversations()

            // If we have messages loaded for this conversation, add the new message
            if (messageData.conversation_id && this.conversationMessages[messageData.conversation_id]) {
                const messages = this.conversationMessages[messageData.conversation_id]

                // Check if message already exists (avoid duplicates)
                const exists = messages.find(m => m.id === messageData.id)
                if (!exists) {
                    messages.push(messageData)
                }
            }
        },

        async refreshConversationsWithPolling() {
            try {
                await this.getConversations();
            } catch (error) {
                console.error('[DefaultLayout] Failed to refresh conversations:', error);
            }
        },

        // Dashboard service methods
        async addFeedback_dashboardservice(item) {
            try {
                return await this.$axios.post("api/feedback", item);
            } catch (error) {
                console.error("error on adding a feedback", error);
            }
        },

        // ==================== AI CHAT METHODS ====================
        
        /**
         * Get all AI chats from the backend
         */
        async getAiChats() {
            try {
                const response = await this.$axios.get('api/ai-chat/');
                if (response.data.response) {
                    this.aiChats = response.data.data || [];
                }
            } catch (error) {
                console.error('Error fetching AI chats:', error);
            }
        },

        /**
         * Optimistically create a new AI chat
         */
        async optimisticCreateAiChat(title = 'New Chat') {
            const tempId = `temp_${Date.now()}`;
            const tempChat = {
                id: tempId,
                title: title,
                is_archived: false,
                created_by: this.get_user?.id,
                creator: {
                    id: this.get_user?.id,
                    name: this.get_user?.name,
                    profile_picture: this.get_user?.profile_picture,
                },
                last_message: null,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                isTemp: true,
            };

            // Optimistically add to the list
            this.aiChats.unshift(tempChat);

            try {
                const response = await this.$axios.post('api/ai-chat/', { title });
                if (response.data.response) {
                    // Replace temp chat with real one
                    const index = this.aiChats.findIndex(c => c.id === tempId);
                    if (index !== -1) {
                        this.aiChats.splice(index, 1, response.data.data);
                    }
                    return response.data.data;
                }
                throw new Error(response.data.message || 'Failed to create chat');
            } catch (error) {
                // Rollback on failure
                this.aiChats = this.aiChats.filter(c => c.id !== tempId);
                throw error;
            }
        },

        /**
         * Optimistically update an AI chat
         */
        async optimisticUpdateAiChat(chatId, updates) {
            const chatIndex = this.aiChats.findIndex(c => c.id === chatId);
            if (chatIndex === -1) return;

            const originalChat = { ...this.aiChats[chatIndex] };
            
            // Optimistically update
            this.aiChats.splice(chatIndex, 1, { ...originalChat, ...updates });

            try {
                const response = await this.$axios.post(`api/ai-chat/${chatId}`, updates);
                if (response.data.response) {
                    // Update with server response
                    const updatedIndex = this.aiChats.findIndex(c => c.id === chatId);
                    if (updatedIndex !== -1) {
                        this.aiChats.splice(updatedIndex, 1, response.data.data);
                    }
                    return response.data.data;
                }
                throw new Error(response.data.message || 'Failed to update chat');
            } catch (error) {
                // Rollback on failure
                const rollbackIndex = this.aiChats.findIndex(c => c.id === chatId);
                if (rollbackIndex !== -1) {
                    this.aiChats.splice(rollbackIndex, 1, originalChat);
                }
                throw error;
            }
        },

        /**
         * Optimistically delete an AI chat
         */
        async optimisticDeleteAiChat(chatId) {
            const chatIndex = this.aiChats.findIndex(c => c.id === chatId);
            if (chatIndex === -1) return;

            const originalChat = this.aiChats[chatIndex];
            
            // Optimistically remove
            this.aiChats.splice(chatIndex, 1);

            try {
                const response = await this.$axios.delete(`api/ai-chat/${chatId}`);
                if (!response.data.response) {
                    throw new Error(response.data.message || 'Failed to delete chat');
                }
            } catch (error) {
                // Rollback on failure
                this.aiChats.splice(chatIndex, 0, originalChat);
                throw error;
            }
        },

        /**
         * Optimistically archive an AI chat
         */
        async optimisticArchiveAiChat(chatId) {
            const chatIndex = this.aiChats.findIndex(c => c.id === chatId);
            if (chatIndex === -1) return;

            const originalChat = { ...this.aiChats[chatIndex] };
            
            // Optimistically update
            this.aiChats.splice(chatIndex, 1, { ...originalChat, is_archived: true });

            try {
                const response = await this.$axios.post(`api/ai-chat/${chatId}/archive`);
                if (response.data.response) {
                    const updatedIndex = this.aiChats.findIndex(c => c.id === chatId);
                    if (updatedIndex !== -1) {
                        this.aiChats.splice(updatedIndex, 1, response.data.data);
                    }
                    return response.data.data;
                }
                throw new Error(response.data.message || 'Failed to archive chat');
            } catch (error) {
                // Rollback on failure
                const rollbackIndex = this.aiChats.findIndex(c => c.id === chatId);
                if (rollbackIndex !== -1) {
                    this.aiChats.splice(rollbackIndex, 1, originalChat);
                }
                throw error;
            }
        },

        /**
         * Optimistically unarchive an AI chat
         */
        async optimisticUnarchiveAiChat(chatId) {
            const chatIndex = this.aiChats.findIndex(c => c.id === chatId);
            if (chatIndex === -1) return;

            const originalChat = { ...this.aiChats[chatIndex] };
            
            // Optimistically update
            this.aiChats.splice(chatIndex, 1, { ...originalChat, is_archived: false });

            try {
                const response = await this.$axios.post(`api/ai-chat/${chatId}/unarchive`);
                if (response.data.response) {
                    const updatedIndex = this.aiChats.findIndex(c => c.id === chatId);
                    if (updatedIndex !== -1) {
                        this.aiChats.splice(updatedIndex, 1, response.data.data);
                    }
                    return response.data.data;
                }
                throw new Error(response.data.message || 'Failed to unarchive chat');
            } catch (error) {
                // Rollback on failure
                const rollbackIndex = this.aiChats.findIndex(c => c.id === chatId);
                if (rollbackIndex !== -1) {
                    this.aiChats.splice(rollbackIndex, 1, originalChat);
                }
                throw error;
            }
        }
    }
};
</script>

<style scoped>
/* Top Navigation Styles */
.top-navigation {
    background-color: #ffffff;
    border-bottom: 1px solid #e0e0e0;
    padding: 12px 0;
    position: sticky;
    top: 0;
    /* Only needs to clear normal in-flow page content scrolling underneath it -
       keep this well below Vuetify's dialog/overlay stacking (~200+) so dialogs,
       menus and app drawers always render above the header. */
    z-index: 5;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.main-sidebar {
    z-index: 6;
}

.sidebar-brand {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 16px;
    height: 56px;
    background: #fff;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.sidebar-brand-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 6px;
    background: #343642;
    flex-shrink: 0;
}

.sidebar-brand-text {
    font-weight: 700;
    font-size: 1.05rem;
    color: #1a1a1a;
    white-space: nowrap;
}

/* Impersonation banner, relocated into the (narrow) sidebar — stacks
   vertically rather than the single-row layout it used as a full-width
   top banner. */
.impersonation-banner {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    margin: 0 12px 12px;
    padding: 10px 12px;
    border-radius: 8px;
    background: linear-gradient(90deg, #d97706 0%, #b45309 100%);
    color: #fff;
    font-size: 0.8rem;
    font-weight: 500;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
}

/* Keep the settings dropdown scrollable so it never auto-flips above the header,
   and give it a visible border + shadow so it reads as a floating panel rather
   than blending into the white page behind it. */
.settings-menu {
    overflow-y: auto;
    border: 1px solid rgba(0, 0, 0, 0.12) !important;
    border-radius: 8px !important;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18) !important;
}

.impersonation-text {
    display: inline-flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;
}

.impersonation-exit-btn {
    border-color: rgba(255, 255, 255, 0.6) !important;
    letter-spacing: 0 !important;
}

.nav-container {
    max-width: 1600px;
    margin: 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 0 24px;
}

.nav-profile {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
}

.nav-profile-identity {
    display: flex;
    flex-direction: column;
    line-height: 1.2;
    min-width: 0;
}

.nav-profile-name {
    font-size: 0.85rem;
    font-weight: 600;
    color: #1a1a1a;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.nav-profile-role {
    font-size: 0.7rem;
    color: #888;
    white-space: nowrap;
}

.nav-profile-actions {
    display: flex;
    align-items: center;
    gap: 6px;
}

.nav-left {
    display: flex;
    align-items: center;
    min-width: 0;
}

/* TimeBanner is normally a full-width sticky top strip (see its own
   scoped styles); override for its new inline placement in the top nav. */
::v-deep .nav-left .time-banner {
    position: static;
    height: auto;
    padding: 4px 10px;
    border-radius: 8px;
    border-bottom: none;
}

.nav-hamburger {
    display: none;
    align-items: center;
    justify-content: center;
    background: none;
    border: none;
    padding: 6px;
    margin-right: 4px;
    color: #555;
    cursor: pointer;
    border-radius: 8px;
}

.nav-hamburger:hover {
    background-color: #f1f3f4;
}

/* --- Mobile: the sidebar becomes a temporary drawer; this slim strip only
   needs to show the hamburger (to open it) alongside the notification bell
   and tour trigger. --- */
@media (max-width: 959px) {
    .top-navigation {
        padding: 6px 0;
    }

    .nav-container {
        justify-content: space-between;
        padding: 0 12px;
    }

    .nav-hamburger {
        display: inline-flex;
    }
}

.nav-settings {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
}

.nav-sod-eod {
    align-items: center;
    gap: 8px;
}

.settings-btn {
    color: #666;
}

/* Notification Menu Styles */
.notification-menu {
    margin-top: 8px;
    min-width: 420px !important;
}

.notification-card {
    border-radius: 12px;
    overflow: hidden;
    min-width: 420px;
}

.notification-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
}

.notification-title {
    display: flex;
    align-items: center;
    font-size: 1.1rem;

    .v-icon {
        color: white;
    }

    .v-chip {
        font-weight: 600;
    }
}

.notification-header-actions {
    display: flex;
    align-items: center;
    gap: 8px;
}

.refresh-btn {
    background: rgba(255, 255, 255, 0.2);
    color: white !important;

    &:hover {
        background: rgba(255, 255, 255, 0.3);
    }

    .v-icon {
        color: white !important;
    }
}

.mark-all-btn {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 6px;
    padding: 4px 12px;
    text-transform: none;
    font-weight: 500;
    color: white !important;

    &:hover {
        background: rgba(255, 255, 255, 0.3);
    }
}

.notification-list {
    max-height: 500px;
    overflow-y: auto;
}

.notification-item {
    display: flex;
    align-items: flex-start;
    padding: 16px 20px;
    cursor: pointer;
    transition: all 0.2s ease;
    border-bottom: 1px solid #f0f0f0;
    background-color: white;

    &:hover {
        background-color: #f8f9fa;
    }

    &:last-child {
        border-bottom: none;
    }
}

.notification-unread {
    background-color: #e3f2fd !important;

    &:hover {
        background-color: #bbdefb !important;
    }
}

.notification-icon {
    margin-right: 12px;
    flex-shrink: 0;
}

.notification-content {
    flex: 1;
    min-width: 0;
}

.notification-text {
    font-size: 0.9rem;
    line-height: 1.5;
    color: #333;
    margin-bottom: 6px;
    word-wrap: break-word;
}

.notification-time {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 0.75rem;
    color: #666;

    .v-icon {
        color: #999;
    }
}

.notification-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: 12px;
    flex-shrink: 0;
}

.mark-read-btn {
    opacity: 0;
    transition: opacity 0.2s;

    .notification-item:hover & {
        opacity: 1;
    }
}

.unread-dot {
    animation: pulse 2s infinite;
}

@keyframes pulse {

    0%,
    100% {
        opacity: 1;
    }

    50% {
        opacity: 0.5;
    }
}

.empty-notifications {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    text-align: center;
}

.empty-text {
    font-size: 1.1rem;
    font-weight: 600;
    color: #666;
    margin-top: 16px;
}

.empty-subtext {
    font-size: 0.9rem;
    color: #999;
    margin-top: 8px;
}

.notification-footer {
    padding: 12px 20px;
    background-color: #f8f9fa;
    border-top: 1px solid #e0e0e0;
    display: flex;
    justify-content: center;
}

.view-all-btn {
    text-transform: none;
    font-weight: 600;
    font-size: 0.9rem;
}

.notification-btn {
    position: relative;

    .v-badge__badge {
        font-size: 0.7rem;
        height: 18px;
        min-width: 18px;
        padding: 0 4px;
    }
}

.notification-badge {
    position: absolute;
    top: 4px;
    right: 4px;
    background-color: #EF5350;
    color: white;
    border-radius: 50%;
    font-size: 0.65rem;
    font-weight: bold;
    min-width: 18px;
    height: 18px;
    display: flex !important;
    align-items: center;
    justify-content: center;
    padding: 2px 4px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    z-index: 10;
    pointer-events: none;
    line-height: 1;
}

.main-content {
    padding-top: 0;
    min-height: calc(100vh - 80px);
}

/* Manual content offset for the fixed-position sidebar (see the
   v-navigation-drawer `fixed` note above main-content in the template). */
.sidebar-offset {
    margin-left: 240px;
    transition: margin-left 0.2s ease;
}

.sidebar-offset--collapsed {
    margin-left: 72px;
}

@media (max-width: 959px) {
    .sidebar-offset,
    .sidebar-offset--collapsed {
        margin-left: 0;
    }
}

/* This is for documentation purposes and will not be needed in your application */
#create .v-speed-dial {
    position: absolute;
}

#create .v-btn--floating {
    position: relative;
}

.title {
    font-weight: 700;
    font-size: 0.9rem;
}

.company-menu {
    border: 1px solid #ccc;
    border-radius: 10px;
    cursor: pointer;
}

.company-menu-title {
    font-family: "Manrope";
}

.company-menu-subtitle {
    font-size: 12px;
    font-family: "Manrope";
}

/* Global background color for the entire app */
body,
html,
.v-application {
    background-color: #F5F7F9 !important;
}

.v-app-bar-title__content {
    width: 50% !important;
}

.main-app-bar {
    background-color: #fff !important;
}

.main-app-bar-title {
    font-family: "Manrope";
    font-size: 24px;
    font-weight: bolder !important;
}

.main-app-bar-subtitle {
    font-size: 10px;
}

.v-menu__content {
    box-shadow: none;
}

.v-data-table__wrapper {
    tr {
        cursor: pointer;
    }
}

::-webkit-scrollbar {
    width: 7px;
    background: white;
}

::-webkit-scrollbar-thumb {
    border-radius: 2px;
    background-color: #d9d9d9;
    color: #d9d9d9;
    height: 50px;
}

:deep(.project-active::before) {
    opacity: 0.12 !important;
}

.notification-bubble {
    position: absolute;
    top: 35%;
    right: 10px;
    background-color: red;
    color: white;
    border-radius: 50%;
    padding: 5px 5px;
    font-size: 0.75rem;
    font-weight: bold;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
}

:deep(.v-btn span) {
    display: contents !important;
    white-space: initial;
}
</style>
